import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Modal, Image, Platform, TouchableWithoutFeedback, Dimensions, ScrollView } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { useNavigate, useParams } from 'react-router-dom';
import { Picker } from '@react-native-picker/picker';
import { ArrowLeft, MessageSquare, Calendar, Home as HomeIcon, Clock, CheckCircle2, X, Plus, Trash2, Wrench, User, UserCheck, Edit3, Paperclip, DollarSign, Download, Eye, File, Image as ImageIcon, Video, FileText, Link2, Loader2, Send } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/constants';
import { downloadFile } from '../../lib/fileDownload';
import { ServiceRequest, ServiceProvider, ServiceRequestQuote, ServiceRequestExpense } from './types';
import { ServiceRequestFileUpload } from '../../components/ServiceRequestFileUpload';
import { QuoteFileUpload } from '../../components/QuoteFileUpload';
import { AIAgentTimeline } from '../../components/AIAgentTimeline';
import { shadows } from '../../lib/styles';
import { formatDateForDisplay, formatDateOnly, parseLocalDate } from '../../lib/dateUtils';
import { sendExpensePaymentRequestEmail } from '../../lib/expensePaymentRequests';
import {
  DEFAULT_VISIT_DURATION_BY_TYPE,
  VisitDurationDefaults,
  VISIT_DURATION_OPTIONS_MINUTES,
  formatVisitDurationLabel,
} from '../../lib/visitDuration';
import { loadVisitDurationDefaults } from '../../lib/visitDurationDefaults';
import AdminLayout from './AdminLayout';
import DateInput from '../../components/ui/DateInput';
import ExpenseEditorModal from '../../components/admin/ExpenseEditorModal';
import ExpenseListSection from '../../components/admin/ExpenseListSection';
import { getExpenseContextLabel } from '../../lib/adminExpenses';

interface ServiceRequestNote {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
  creator?: {
    full_name: string;
  } | null;
}

interface AdminTaskLink {
  id: string;
  title: string;
  description: string | null;
  status: 'pending' | 'in_progress' | 'completed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  assigned_to: string | null;
  assignee?: {
    full_name: string;
  } | null;
}

interface UpcomingVisitOption {
  id: string;
  visit_type: 'maintenance' | 'service' | 'walkthrough' | string;
  scheduled_date: string | null;
  status: string;
  estimated_duration_minutes: number | null;
  assigned_to: string | null;
  service_provider_id: string | null;
  visit_notes: string | null;
  service_provider?: { company_name: string } | null;
  visit_tasks_count?: number;
}

interface ExtendedServiceRequest extends Omit<ServiceRequest, 'home'> {
  home?: {
    id: string;
    address: string;
    city: string;
    state: string;
    owner: {
      id: string;
      full_name: string;
    } | null;
  } | null;
  assigned_owner?: {
    id: string;
    full_name: string;
  } | null;
  notes?: ServiceRequestNote[];
}

interface ExpensePaymentRequestHistoryItem {
  id: string;
  expense_id: string;
  requested_at: string;
  requested_by: string | null;
  recipient_email: string;
  status: 'pending' | 'sent' | 'failed' | 'skipped';
  requester_name?: string | null;
}

const getDefaultExpenseFormData = () => ({
  parent_expense_id: '',
  quote_id: '',
  amount: '',
  internal_cost: '',
  homeowner_visible: true,
  description: '',
  expense_date: '',
  billable_to_homeowner: true,
  needs_contractor_payment: true,
});

type WizardLineItem = {
  tempId: string;
  description: string;
  category: string;
  vendor: string;
  amount: string;
  internal_cost: string;
  homeowner_visible: boolean;
};

const getDefaultWizardLineItemDraft = (): Omit<WizardLineItem, 'tempId'> => ({
  description: '',
  category: '',
  vendor: '',
  amount: '',
  internal_cost: '',
  homeowner_visible: true,
});

const ServiceRequestDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [windowWidth, setWindowWidth] = useState(Dimensions.get('window').width);
  const isMobile = windowWidth < 768;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [request, setRequest] = useState<ExtendedServiceRequest | null>(null);
  const [notes, setNotes] = useState<ServiceRequestNote[]>([]);
  const [adminTasks, setAdminTasks] = useState<AdminTaskLink[]>([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [systemManagers, setSystemManagers] = useState<Array<{ id: string; full_name: string }>>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [quotes, setQuotes] = useState<ServiceRequestQuote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [quoteAttachments, setQuoteAttachments] = useState<Record<string, any[]>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<ServiceRequestExpense[]>([]);
  const [expensePaymentHistoryByExpenseId, setExpensePaymentHistoryByExpenseId] = useState<Record<string, ExpensePaymentRequestHistoryItem[]>>({});
  const [requestingPaymentExpenseId, setRequestingPaymentExpenseId] = useState<string | null>(null);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<string>>(new Set());
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  
  // Edit states for title and description
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingCreatedAt, setEditingCreatedAt] = useState(false);
  const [editingCompletedAt, setEditingCompletedAt] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedCreatedAt, setEditedCreatedAt] = useState('');
  const [editedCompletedAt, setEditedCompletedAt] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);
  const [savingCreatedAt, setSavingCreatedAt] = useState(false);
  const [savingCompletedAt, setSavingCompletedAt] = useState(false);
  
  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showScheduleVisitModal, setShowScheduleVisitModal] = useState(false);
  const [showChooseVisitModal, setShowChooseVisitModal] = useState(false);
  const [upcomingVisits, setUpcomingVisits] = useState<UpcomingVisitOption[]>([]);
  const [loadingUpcomingVisits, setLoadingUpcomingVisits] = useState(false);
  const [addingToVisitId, setAddingToVisitId] = useState<string | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingRequest, setDeletingRequest] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showAssociateModal, setShowAssociateModal] = useState(false);
  const [showAssociateTaskModal, setShowAssociateTaskModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState<ServiceRequestQuote | null>(null);
  const [standaloneQuotes, setStandaloneQuotes] = useState<ServiceRequestQuote[]>([]);
  const [selectedStandaloneQuoteId, setSelectedStandaloneQuoteId] = useState<string>('');
  const [associatingQuote, setAssociatingQuote] = useState(false);
  const [standaloneAdminTasks, setStandaloneAdminTasks] = useState<AdminTaskLink[]>([]);
  const [selectedStandaloneTaskId, setSelectedStandaloneTaskId] = useState<string>('');
  const [loadingAdminTasks, setLoadingAdminTasks] = useState(false);
  const [loadingStandaloneTasks, setLoadingStandaloneTasks] = useState(false);
  const [associatingTask, setAssociatingTask] = useState(false);
  const [creatingAdminTask, setCreatingAdminTask] = useState(false);
  const [savingAdminTaskEdit, setSavingAdminTaskEdit] = useState(false);
  const [completingAdminTaskId, setCompletingAdminTaskId] = useState<string | null>(null);
  const [editingAdminTask, setEditingAdminTask] = useState<AdminTaskLink | null>(null);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showExpensePreviewModal, setShowExpensePreviewModal] = useState(false);
  const [expenseToPreview, setExpenseToPreview] = useState<ServiceRequestExpense | null>(null);
  const [expensePreviewTab, setExpensePreviewTab] = useState<'email' | 'invoice'>('email');
  const [expenseWizardStep, setExpenseWizardStep] = useState(1);
  const [wizardLineItems, setWizardLineItems] = useState<WizardLineItem[]>([]);
  const [wizardLineItemDraft, setWizardLineItemDraft] = useState<Omit<WizardLineItem, 'tempId'>>(getDefaultWizardLineItemDraft());
  const [showWizardAddLineItemForm, setShowWizardAddLineItemForm] = useState(false);
  const [wizardAmountOverride, setWizardAmountOverride] = useState(false);
  const [showHomeownerPaymentModal, setShowHomeownerPaymentModal] = useState(false);
  const [showContractorPaymentModal, setShowContractorPaymentModal] = useState(false);
  const [showRequestHomeownerPaymentConfirmModal, setShowRequestHomeownerPaymentConfirmModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ServiceRequestExpense | null>(null);
  const [lineItemParentExpense, setLineItemParentExpense] = useState<ServiceRequestExpense | null>(null);
  const [expenseToMarkHomeownerPaid, setExpenseToMarkHomeownerPaid] = useState<ServiceRequestExpense | null>(null);
  const [expenseToMarkContractorPaid, setExpenseToMarkContractorPaid] = useState<ServiceRequestExpense | null>(null);
  const [expenseToRequestHomeownerPayment, setExpenseToRequestHomeownerPayment] = useState<ServiceRequestExpense | null>(null);
  const [savingExpense, setSavingExpense] = useState(false);
  const [deletingExpenseId, setDeletingExpenseId] = useState<string | null>(null);
  const [markingHomeownerPaid, setMarkingHomeownerPaid] = useState(false);
  const [markingContractorPaid, setMarkingContractorPaid] = useState(false);
  
  const [visitFormData, setVisitFormData] = useState({
    scheduled_date: '',
    estimated_duration_minutes: DEFAULT_VISIT_DURATION_BY_TYPE.service,
    assigned_to: '',
    service_provider_id: '',
    visit_notes: '',
    visit_type: 'service' as 'maintenance' | 'service' | 'walkthrough'
  });
  const [visitDurationDefaults, setVisitDurationDefaults] = useState<VisitDurationDefaults>({
    ...DEFAULT_VISIT_DURATION_BY_TYPE,
  });

  const [quoteFormData, setQuoteFormData] = useState({
    service_provider_id: '',
    service_type: '',
    work_description: '',
    amount: '',
    quote_date: formatDateOnly(new Date()),
    expires_at: formatDateOnly(new Date()),
    notes: '',
    status: 'pending' as 'pending' | 'accepted' | 'rejected'
  });
  const [savingQuote, setSavingQuote] = useState(false);
  const [deletingQuoteId, setDeletingQuoteId] = useState<string | null>(null);
  const [serviceProviderSearch, setServiceProviderSearch] = useState('');
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const [generatingBidLink, setGeneratingBidLink] = useState(false);
  const [bidShareMessage, setBidShareMessage] = useState<string | null>(null);
  const [bidShareMessageType, setBidShareMessageType] = useState<'success' | 'error'>('success');
  
  const [expenseFormData, setExpenseFormData] = useState(getDefaultExpenseFormData());
  
  const [homeownerPaymentFormData, setHomeownerPaymentFormData] = useState({
    payment_date: '',
    payment_method: '',
    payment_notes: ''
  });
  const [contractorPaymentFormData, setContractorPaymentFormData] = useState({
    payment_date: '',
    payment_method: '',
    payment_notes: ''
  });
  const [adminTaskFormData, setAdminTaskFormData] = useState({
    title: '',
    description: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    due_date: formatDateOnly(new Date()),
    assigned_to: '',
  });
  const [adminTaskEditFormData, setAdminTaskEditFormData] = useState({
    title: '',
    description: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    due_date: '',
    assigned_to: '',
    status: 'pending' as 'pending' | 'in_progress' | 'completed',
  });

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setWindowWidth(window.width);
    });
    
    return () => {
      subscription?.remove();
    };
  }, []);

  useEffect(() => {
    if (!id) {
      navigate('/admin');
      return;
    }
    fetchRequest();
    fetchSystemManagers();
    fetchServiceProviders();
    fetchVisitDurationDefaults();
  }, [id]);

  useEffect(() => {
    setSelectedExpenseIds((previous) => {
      const eligibleParentIds = new Set(
        expenses
          .filter((expense) => !expense.parent_expense_id && expense.billable_to_homeowner && !expense.is_paid && !expense.invoice_id)
          .map((expense) => expense.id)
      );

      const nextSelection = new Set(Array.from(previous).filter((expenseId) => eligibleParentIds.has(expenseId)));
      if (nextSelection.size === previous.size) {
        return previous;
      }
      return nextSelection;
    });
  }, [expenses]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          id,
          title,
          description,
          priority,
          status,
          created_at,
          completed_at,
          home_id,
          visit_id,
          visit_scheduled_at,
          visit_completed_at,
          owner_id,
          home:homes (
            id,
            address,
            city,
            state,
            owner:profiles!homes_owner_id_fkey (
              id,
              full_name
            )
          ),
          assigned_owner:profiles!owner_id (
            id,
            full_name
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Transform the data to match our interface
      const homeData = Array.isArray(data.home) ? data.home[0] : data.home;
      const ownerData = homeData?.owner ? (Array.isArray(homeData.owner) ? homeData.owner[0] : homeData.owner) : null;
      const assignedOwnerData = Array.isArray(data.assigned_owner) ? data.assigned_owner[0] : data.assigned_owner;
      
      const transformedData: ExtendedServiceRequest = {
        ...data,
        home: homeData ? {
          ...homeData,
          owner: ownerData
        } : null,
        assigned_owner: assignedOwnerData || undefined
      };

      setRequest(transformedData);
      if (id) {
        await fetchAdminTasks(id);
        await fetchNotes(id);
        await fetchQuotes(id);
        await fetchExpenses(id);
      }
    } catch (error: any) {
      console.error('Error fetching service request:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async (requestId: string) => {
    try {
      // Fetch notes
      const { data: notesData, error: notesError } = await supabase
        .from('service_request_notes')
        .select('*')
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (notesError) throw notesError;

      if (!notesData || notesData.length === 0) {
        setNotes([]);
        return;
      }

      // Get unique user IDs from notes
      const userIds = [...new Set(notesData.map(note => note.created_by).filter(Boolean))];

      // Fetch user information for all creators
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (usersError) {
        console.error('Error fetching user info:', usersError);
        setNotes(notesData);
        return;
      }

      // Create a map of user IDs to user info
      const usersMap = new Map(usersData?.map(user => [user.id, user]) || []);

      // Enrich notes with user information
      const enrichedNotes = notesData.map(note => ({
        ...note,
        creator: usersMap.get(note.created_by) || null
      }));

      setNotes(enrichedNotes);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchAdminTasks = async (requestId: string) => {
    try {
      setLoadingAdminTasks(true);
      const { data, error } = await supabase
        .from('admin_tasks')
        .select('id, title, description, status, priority, due_date, completed_at, created_at, assigned_to')
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        setAdminTasks([]);
        return;
      }

      const assigneeIds = [...new Set(data.map(task => task.assigned_to).filter(Boolean))];
      let assigneeMap = new Map<string, { full_name: string }>();

      if (assigneeIds.length > 0) {
        const { data: assignees, error: assigneesError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', assigneeIds as string[]);

        if (assigneesError) {
          console.error('Error fetching assignees for admin tasks:', assigneesError);
        } else {
          assigneeMap = new Map((assignees || []).map(profile => [profile.id, { full_name: profile.full_name }]));
        }
      }

      const enrichedTasks: AdminTaskLink[] = data.map((task: any) => ({
        ...task,
        assignee: task.assigned_to ? assigneeMap.get(task.assigned_to) || null : null,
      }));

      setAdminTasks(enrichedTasks);
    } catch (error) {
      console.error('Error fetching admin tasks:', error);
      setAdminTasks([]);
    } finally {
      setLoadingAdminTasks(false);
    }
  };

  const fetchStandaloneAdminTasks = async () => {
    try {
      setLoadingStandaloneTasks(true);
      const { data, error } = await supabase
        .from('admin_tasks')
        .select('id, title, description, status, priority, due_date, created_at, assigned_to')
        .is('service_request_id', null)
        .neq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      if (!data || data.length === 0) {
        setStandaloneAdminTasks([]);
        return;
      }

      const assigneeIds = [...new Set(data.map(task => task.assigned_to).filter(Boolean))];
      let assigneeMap = new Map<string, { full_name: string }>();

      if (assigneeIds.length > 0) {
        const { data: assignees, error: assigneesError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', assigneeIds as string[]);

        if (assigneesError) {
          console.error('Error fetching standalone admin task assignees:', assigneesError);
        } else {
          assigneeMap = new Map((assignees || []).map(profile => [profile.id, { full_name: profile.full_name }]));
        }
      }

      const enrichedTasks: AdminTaskLink[] = data.map((task: any) => ({
        ...task,
        assignee: task.assigned_to ? assigneeMap.get(task.assigned_to) || null : null,
      }));

      setStandaloneAdminTasks(enrichedTasks);
    } catch (error) {
      console.error('Error fetching standalone admin tasks:', error);
      setStandaloneAdminTasks([]);
    } finally {
      setLoadingStandaloneTasks(false);
    }
  };

  const handleAssociateTask = async () => {
    if (!id || !selectedStandaloneTaskId) return;

    setAssociatingTask(true);
    try {
      const { error } = await supabase
        .from('admin_tasks')
        .update({ service_request_id: id })
        .eq('id', selectedStandaloneTaskId);

      if (error) throw error;

      await fetchAdminTasks(id);
      setShowAssociateTaskModal(false);
      setSelectedStandaloneTaskId('');
    } catch (error: any) {
      console.error('Error associating admin task:', error);
      setError(error.message);
    } finally {
      setAssociatingTask(false);
    }
  };

  const handleOpenCreateTaskModal = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const defaultAssignee = request?.assigned_owner?.id || request?.owner_id || user?.id || '';

      setAdminTaskFormData({
        title: '',
        description: '',
        priority: 'normal',
        due_date: formatDateOnly(new Date()),
        assigned_to: defaultAssignee,
      });
      setShowCreateTaskModal(true);
    } catch (error) {
      console.error('Error preparing create task modal:', error);
      setShowCreateTaskModal(true);
    }
  };

  const handleCreateAdminTask = async () => {
    if (!id || !request || !adminTaskFormData.title.trim()) return;

    setCreatingAdminTask(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const dueDateIso = adminTaskFormData.due_date
        ? (parseLocalDate(adminTaskFormData.due_date)?.toISOString() || null)
        : null;

      const assignedTo = adminTaskFormData.assigned_to || request.assigned_owner?.id || request.owner_id || user.id;

      const { error } = await supabase
        .from('admin_tasks')
        .insert([{
          title: adminTaskFormData.title.trim(),
          description: adminTaskFormData.description.trim() || null,
          status: 'pending',
          priority: adminTaskFormData.priority,
          due_date: dueDateIso,
          home_id: request.home_id || null,
          homeowner_id: null,
          service_request_id: id,
          created_by: user.id,
          assigned_to: assignedTo,
        }]);

      if (error) throw error;

      await fetchAdminTasks(id);
      setShowCreateTaskModal(false);
      setAdminTaskFormData({
        title: '',
        description: '',
        priority: 'normal',
        due_date: formatDateOnly(new Date()),
        assigned_to: '',
      });
    } catch (error: any) {
      console.error('Error creating admin task:', error);
      setError(error.message);
    } finally {
      setCreatingAdminTask(false);
    }
  };

  const handleOpenEditTaskModal = (task: AdminTaskLink) => {
    setEditingAdminTask(task);
    setAdminTaskEditFormData({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority,
      due_date: task.due_date ? formatDateOnly(task.due_date) : '',
      assigned_to: task.assigned_to || '',
      status: task.status,
    });
    setShowEditTaskModal(true);
  };

  const handleCompleteAdminTask = async (taskId: string) => {
    if (!id) return;

    setCompletingAdminTaskId(taskId);
    try {
      const completedAt = new Date().toISOString();
      const { error } = await supabase
        .from('admin_tasks')
        .update({ status: 'completed', completed_at: completedAt })
        .eq('id', taskId);

      if (error) throw error;

      setAdminTasks(prev =>
        prev.map(task =>
          task.id === taskId
            ? { ...task, status: 'completed', completed_at: completedAt }
            : task
        )
      );
    } catch (error: any) {
      console.error('Error completing admin task:', error);
      setError(error.message);
    } finally {
      setCompletingAdminTaskId(null);
    }
  };

  const handleSaveAdminTaskEdit = async () => {
    if (!id || !editingAdminTask || !adminTaskEditFormData.title.trim()) return;

    setSavingAdminTaskEdit(true);
    try {
      const dueDateIso = adminTaskEditFormData.due_date
        ? (parseLocalDate(adminTaskEditFormData.due_date)?.toISOString() || null)
        : null;

      const updates = {
        title: adminTaskEditFormData.title.trim(),
        description: adminTaskEditFormData.description.trim() || null,
        priority: adminTaskEditFormData.priority,
        due_date: dueDateIso,
        assigned_to: adminTaskEditFormData.assigned_to || null,
        status: adminTaskEditFormData.status,
        completed_at: adminTaskEditFormData.status === 'completed' ? new Date().toISOString() : null,
      };

      const { error } = await supabase
        .from('admin_tasks')
        .update(updates)
        .eq('id', editingAdminTask.id);

      if (error) throw error;

      await fetchAdminTasks(id);
      setShowEditTaskModal(false);
      setEditingAdminTask(null);
    } catch (error: any) {
      console.error('Error updating admin task:', error);
      setError(error.message);
    } finally {
      setSavingAdminTaskEdit(false);
    }
  };

  const fetchSystemManagers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('is_system_manager', true)
        .order('full_name');

      if (error) throw error;
      setSystemManagers(data || []);
    } catch (error) {
      console.error('Error fetching system managers:', error);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      const { data, error } = await supabase
        .from('service_providers')
        .select('*')
        .order('company_name');

      if (error) throw error;
      setServiceProviders(data || []);
    } catch (error) {
      console.error('Error fetching service providers:', error);
    }
  };

  const fetchVisitDurationDefaults = async () => {
    try {
      const defaults = await loadVisitDurationDefaults();
      setVisitDurationDefaults(defaults);
    } catch (error) {
      console.error('Error fetching visit duration defaults:', error);
    }
  };

  const fetchStandaloneQuotes = async () => {
    try {
      const { data, error } = await supabase
        .from('service_request_quotes')
        .select(`
          *,
          service_provider:service_providers (
            id,
            company_name,
            email,
            phone,
            address
          )
        `)
        .is('service_request_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedQuotes = (data || []).map((quote: any) => ({
        ...quote,
        service_provider: Array.isArray(quote.service_provider) 
          ? quote.service_provider[0] 
          : quote.service_provider
      }));

      setStandaloneQuotes(transformedQuotes);
    } catch (error) {
      console.error('Error fetching standalone quotes:', error);
    }
  };

  const handleAssociateQuote = async () => {
    if (!id || !selectedStandaloneQuoteId) return;

    setAssociatingQuote(true);
    try {
      const { error } = await supabase
        .from('service_request_quotes')
        .update({ service_request_id: id })
        .eq('id', selectedStandaloneQuoteId);

      if (error) throw error;

      await fetchQuotes(id);
      setShowAssociateModal(false);
      setSelectedStandaloneQuoteId('');
    } catch (error: any) {
      console.error('Error associating quote:', error);
      setError(error.message);
    } finally {
      setAssociatingQuote(false);
    }
  };

  const fetchQuotes = async (requestId: string) => {
    try {
      setLoadingQuotes(true);
      const { data, error } = await supabase
        .from('service_request_quotes')
        .select(`
          *,
          service_provider:service_providers (
            id,
            company_name,
            email,
            phone,
            address
          )
        `)
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match our interface and fetch attachments
      const attachmentsMap: Record<string, any[]> = {};
      const transformedQuotes = await Promise.all(
        (data || []).map(async (quote: any) => {
          // Fetch full attachment data for each quote
          const { data: attachmentsData, error: attachmentsError } = await supabase
            .from('service_request_quote_attachments')
            .select('*')
            .eq('quote_id', quote.id)
            .order('created_at', { ascending: false });

          const attachments = attachmentsError ? [] : (attachmentsData || []);
          attachmentsMap[quote.id] = attachments;

          return {
            ...quote,
            service_provider: Array.isArray(quote.service_provider) 
              ? quote.service_provider[0] 
              : quote.service_provider,
            attachment_count: attachments.length
          };
        })
      );

      setQuotes(transformedQuotes);
      setQuoteAttachments(attachmentsMap);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const handleStatusUpdate = async (newStatus: ServiceRequest['status']) => {
    try {
      setUpdating(true);
      const { error } = await supabase
        .from('service_requests')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      
      setRequest(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (error: any) {
      console.error('Error updating request status:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleOwnerAssignment = async (ownerId: string | null) => {
    try {
      setUpdating(true);
      const { error } = await supabase
        .from('service_requests')
        .update({ owner_id: ownerId })
        .eq('id', id);

      if (error) throw error;
      
      // Refresh the request data
      await fetchRequest();
    } catch (error: any) {
      console.error('Error updating request owner:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handlePriorityUpdate = async (newPriority: ServiceRequest['priority']) => {
    try {
      setUpdating(true);
      const { error } = await supabase
        .from('service_requests')
        .update({ priority: newPriority })
        .eq('id', id);

      if (error) throw error;
      
      setRequest(prev => prev ? { ...prev, priority: newPriority } : null);
    } catch (error: any) {
      console.error('Error updating request priority:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleTitleEdit = () => {
    if (request) {
      setEditedTitle(request.title);
      setEditingTitle(true);
    }
  };

  const handleTitleSave = async () => {
    if (!id || !editedTitle.trim()) return;

    setSavingTitle(true);
    try {
      const { error } = await supabase
        .from('service_requests')
        .update({ title: editedTitle.trim() })
        .eq('id', id);

      if (error) throw error;
      
      setRequest(prev => prev ? { ...prev, title: editedTitle.trim() } : null);
      setEditingTitle(false);
    } catch (error: any) {
      console.error('Error updating request title:', error);
      setError(error.message);
    } finally {
      setSavingTitle(false);
    }
  };

  const handleTitleCancel = () => {
    setEditingTitle(false);
    setEditedTitle('');
  };

  const handleDescriptionEdit = () => {
    if (request) {
      setEditedDescription(request.description || '');
      setEditingDescription(true);
    }
  };

  const handleDescriptionSave = async () => {
    if (!id) return;

    setSavingDescription(true);
    try {
      const newDescription: string = editedDescription.trim() || '';
      const { error } = await supabase
        .from('service_requests')
        .update({ description: newDescription })
        .eq('id', id);

      if (error) throw error;
      
      setRequest(prev => prev ? { ...prev, description: newDescription as string } : null);
      setEditingDescription(false);
    } catch (error: any) {
      console.error('Error updating request description:', error);
      setError(error.message);
    } finally {
      setSavingDescription(false);
    }
  };

  const handleDescriptionCancel = () => {
    setEditingDescription(false);
    setEditedDescription('');
  };

  const handleCreatedAtEdit = () => {
    if (request) {
      // Format the date as YYYY-MM-DD for the input
      const dateStr = formatDateOnly(request.created_at);
      setEditedCreatedAt(dateStr);
      setEditingCreatedAt(true);
    }
  };

  const handleCreatedAtSave = async () => {
    if (!id || !editedCreatedAt.trim()) return;

    setSavingCreatedAt(true);
    try {
      // Parse the date-only string as a local date to get the correct year/month/day
      const localDate = parseLocalDate(editedCreatedAt);
      if (!localDate || isNaN(localDate.getTime())) {
        throw new Error('Invalid date format');
      }
      
      // Preserve the original time if it exists, otherwise use noon UTC
      const originalDate = request ? (parseLocalDate(request.created_at) || new Date(request.created_at)) : new Date();
      // Create a new date with the selected date's year/month/day and original time
      const newDate = new Date(Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth(),
        localDate.getDate(),
        originalDate.getUTCHours(),
        originalDate.getUTCMinutes(),
        originalDate.getUTCSeconds()
      ));
      
      const { error } = await supabase
        .from('service_requests')
        .update({ created_at: newDate.toISOString() })
        .eq('id', id);

      if (error) throw error;
      
      setRequest(prev => prev ? { ...prev, created_at: newDate.toISOString() } : null);
      setEditingCreatedAt(false);
    } catch (error: any) {
      console.error('Error updating request created date:', error);
      setError(error.message);
    } finally {
      setSavingCreatedAt(false);
    }
  };

  const handleCreatedAtCancel = () => {
    setEditingCreatedAt(false);
    setEditedCreatedAt('');
  };

  const handleCompletedAtEdit = () => {
    if (request) {
      // Format the date as YYYY-MM-DD for the input
      const dateStr = request.completed_at 
        ? formatDateOnly(request.completed_at)
        : '';
      setEditedCompletedAt(dateStr);
      setEditingCompletedAt(true);
    }
  };

  const handleCompletedAtSave = async () => {
    if (!id) return;

    setSavingCompletedAt(true);
    try {
      let updateData: { completed_at: string | null } = { completed_at: null };
      
      if (editedCompletedAt.trim()) {
        // Parse the date-only string as a local date to get the correct year/month/day
        const localDate = parseLocalDate(editedCompletedAt);
        if (!localDate || isNaN(localDate.getTime())) {
          throw new Error('Invalid date format');
        }
        
        // Preserve the original time if it exists, otherwise use noon UTC
        // Using noon UTC ensures the date displays correctly across all timezones
        if (request?.completed_at) {
          const originalDate = parseLocalDate(request.completed_at) || new Date(request.completed_at);
          // Create a new date with the selected date's year/month/day and original time
          const newDate = new Date(Date.UTC(
            localDate.getFullYear(),
            localDate.getMonth(),
            localDate.getDate(),
            originalDate.getUTCHours(),
            originalDate.getUTCMinutes(),
            originalDate.getUTCSeconds()
          ));
          updateData.completed_at = newDate.toISOString();
        } else {
          // Create date at noon UTC using the year/month/day from the local date
          // This prevents the date from shifting back a day when displayed
          const newDate = new Date(Date.UTC(
            localDate.getFullYear(),
            localDate.getMonth(),
            localDate.getDate(),
            12, 0, 0, 0
          ));
          updateData.completed_at = newDate.toISOString();
        }
      }
      
      const { error } = await supabase
        .from('service_requests')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      
      setRequest(prev => prev ? { ...prev, completed_at: updateData.completed_at } : null);
      setEditingCompletedAt(false);
    } catch (error: any) {
      console.error('Error updating request completed date:', error);
      setError(error.message);
    } finally {
      setSavingCompletedAt(false);
    }
  };

  const handleCompletedAtCancel = () => {
    setEditingCompletedAt(false);
    setEditedCompletedAt('');
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return;

    setAddingNote(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('service_request_notes')
        .insert([{
          service_request_id: id,
          content: newNote.trim(),
          created_by: user?.id || 'admin'
        }]);

      if (error) throw error;

      // Refetch notes to get the complete data with user information
      await fetchNotes(id);
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    setDeletingNoteId(noteId);
    try {
      const { error } = await supabase
        .from('service_request_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleDeleteRequest = async () => {
    setDeletingRequest(true);
    try {
      const { error } = await supabase
        .from('service_requests')
        .delete()
        .eq('id', id);

      if (error) throw error;

      navigate('/admin');
    } catch (error: any) {
      console.error('Error deleting service request:', error);
      setError(error.message);
    } finally {
      setDeletingRequest(false);
    }
  };

  const openScheduleVisitFlow = async () => {
    if (!request?.home?.id) {
      setShowScheduleVisitModal(true);
      return;
    }

    setLoadingUpcomingVisits(true);
    try {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('visits')
        .select(`
          id,
          visit_type,
          scheduled_date,
          status,
          estimated_duration_minutes,
          assigned_to,
          service_provider_id,
          visit_notes,
          service_provider:service_providers (
            company_name
          ),
          visit_tasks (id)
        `)
        .eq('home_id', request.home.id)
        .in('status', ['requested', 'proposed', 'scheduled'])
        .or(`scheduled_date.is.null,scheduled_date.gte.${nowIso}`)
        .order('scheduled_date', { ascending: true, nullsFirst: false });

      if (error) throw error;

      const visits: UpcomingVisitOption[] = (data || []).map((visit: any) => ({
        id: visit.id,
        visit_type: visit.visit_type,
        scheduled_date: visit.scheduled_date,
        status: visit.status,
        estimated_duration_minutes: visit.estimated_duration_minutes,
        assigned_to: visit.assigned_to,
        service_provider_id: visit.service_provider_id,
        visit_notes: visit.visit_notes,
        service_provider: Array.isArray(visit.service_provider)
          ? visit.service_provider[0]
          : visit.service_provider,
        visit_tasks_count: Array.isArray(visit.visit_tasks) ? visit.visit_tasks.length : 0,
      }));

      if (visits.length > 0) {
        setUpcomingVisits(visits);
        setShowChooseVisitModal(true);
      } else {
        setShowScheduleVisitModal(true);
      }
    } catch (err: any) {
      console.error('Error fetching upcoming visits:', err);
      // On failure, fall through to the standard create-visit modal so admins are not blocked
      setShowScheduleVisitModal(true);
    } finally {
      setLoadingUpcomingVisits(false);
    }
  };

  const handleAddToExistingVisit = async (visit: UpcomingVisitOption) => {
    if (!request) return;

    setAddingToVisitId(visit.id);
    setError(null);
    try {
      const { error: taskError } = await supabase
        .from('visit_tasks')
        .insert([{
          visit_id: visit.id,
          task_type: 'service_request',
          task_id: id,
          task_title: request.title,
          task_description: request.description,
          estimated_duration_minutes:
            visit.estimated_duration_minutes ?? visitDurationDefaults.service,
        }]);

      if (taskError) throw taskError;

      const { error: updateError } = await supabase
        .from('service_requests')
        .update({
          visit_id: visit.id,
          visit_scheduled_at: visit.scheduled_date,
          status: 'in_progress',
        })
        .eq('id', id);

      if (updateError) throw updateError;

      setShowChooseVisitModal(false);
      setUpcomingVisits([]);
      await fetchRequest();
    } catch (err: any) {
      console.error('Error adding service request to existing visit:', err);
      setError(err.message);
    } finally {
      setAddingToVisitId(null);
    }
  };

  const handleScheduleVisit = async () => {
    if (!request) return;

    setUpdating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create the visit
      const { data: visitData, error: visitError } = await supabase
        .from('visits')
        .insert([{
          home_id: request.home?.id,
          visit_type: visitFormData.visit_type,
          scheduled_date: visitFormData.scheduled_date
            ? new Date(visitFormData.scheduled_date).toISOString()
            : null,
          estimated_duration_minutes: visitFormData.estimated_duration_minutes,
          assigned_to: visitFormData.assigned_to || null,
          service_provider_id: visitFormData.service_provider_id || null,
          visit_notes: visitFormData.visit_notes || null,
          created_by: user.id,
          updated_by: user.id
        }])
        .select()
        .single();

      if (visitError) throw visitError;

      // Create a visit task for this service request. Setting `task_id` is
      // what links the visit_task back to the service request, which the DB
      // trigger uses to keep `service_requests.visit_id` /
      // `visit_scheduled_at` in sync if the task is later removed.
      const { error: taskError } = await supabase
        .from('visit_tasks')
        .insert([{
          visit_id: visitData.id,
          task_type: 'service_request',
          task_id: id,
          task_title: request.title,
          task_description: request.description,
          estimated_duration_minutes: visitFormData.estimated_duration_minutes
        }]);

      if (taskError) throw taskError;

      // Update the service request with visit information
      const { error: updateError } = await supabase
        .from('service_requests')
        .update({
          visit_id: visitData.id,
          visit_scheduled_at: visitData.scheduled_date,
          status: 'in_progress'
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // Reset form and close modal
      setVisitFormData({
        scheduled_date: '',
        estimated_duration_minutes: visitDurationDefaults.service,
        assigned_to: '',
        service_provider_id: '',
        visit_notes: '',
        visit_type: 'service'
      });
      setShowScheduleVisitModal(false);

      // Refresh the request data
      await fetchRequest();
    } catch (error: any) {
      console.error('Error scheduling visit:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const fetchExpenses = async (requestId: string) => {
    try {
      setLoadingExpenses(true);
      const { data, error } = await supabase
        .from('service_request_expenses')
        .select(`
          *,
          quote:service_request_quotes (
            id,
            amount,
            service_provider:service_providers (
              company_name
            )
          )
        `)
        .eq('service_request_id', requestId)
        .order('expense_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data
      const transformedExpenses = (data || []).map((expense: any) => ({
        ...expense,
        quote: Array.isArray(expense.quote) ? expense.quote[0] : expense.quote
      }));

      setExpenses(transformedExpenses);
      await fetchExpensePaymentRequestHistory(transformedExpenses.map((expense: ServiceRequestExpense) => expense.id));
    } catch (error: any) {
      console.error('Error fetching expenses:', error);
      setError(error.message);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const fetchExpensePaymentRequestHistory = async (expenseIds: string[]) => {
    if (expenseIds.length === 0) {
      setExpensePaymentHistoryByExpenseId({});
      return;
    }

    try {
      const { data: requestRows, error: requestError } = await supabase
        .from('expense_payment_requests')
        .select('id, expense_id, requested_at, requested_by, recipient_email, status')
        .in('expense_id', expenseIds)
        .order('requested_at', { ascending: false });

      if (requestError) throw requestError;

      const requesterIds = Array.from(
        new Set(
          (requestRows || [])
            .map((row) => row.requested_by)
            .filter((value): value is string => Boolean(value))
        )
      );

      let requesterNameMap = new Map<string, string>();
      if (requesterIds.length > 0) {
        const { data: requesterProfiles, error: requesterError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', requesterIds);

        if (requesterError) throw requesterError;

        requesterNameMap = new Map(
          (requesterProfiles || []).map((profile) => [profile.id, profile.full_name || 'Unknown'])
        );
      }

      const historyMap: Record<string, ExpensePaymentRequestHistoryItem[]> = {};
      (requestRows || []).forEach((row) => {
        const item: ExpensePaymentRequestHistoryItem = {
          ...row,
          status: (row.status || 'pending') as ExpensePaymentRequestHistoryItem['status'],
          requester_name: row.requested_by ? requesterNameMap.get(row.requested_by) || null : null,
        };

        if (!historyMap[row.expense_id]) {
          historyMap[row.expense_id] = [];
        }

        historyMap[row.expense_id].push(item);
      });

      setExpensePaymentHistoryByExpenseId(historyMap);
    } catch (historyError) {
      console.error('Error fetching expense payment request history:', historyError);
    }
  };

  const calculateTotalExpenses = () => {
    return expenses
      .filter((expense) => !expense.parent_expense_id)
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  const calculatePaidExpenses = () => {
    return expenses
      .filter(expense => !expense.parent_expense_id && (expense.is_paid || expense.contractor_paid))
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  const calculateHomeownerOutstandingExpenses = () => {
    return expenses
      .filter(expense => !expense.parent_expense_id && expense.billable_to_homeowner && !expense.is_paid)
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  const calculateContractorOutstandingExpenses = () => {
    return expenses
      .filter(expense => !expense.parent_expense_id && expense.needs_contractor_payment && !expense.contractor_paid)
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  const getParentExpenses = () => expenses.filter((expense) => !expense.parent_expense_id);

  const getVisibleLineItemsForParent = (parentId: string) =>
    expenses.filter((expense) => expense.parent_expense_id === parentId && expense.homeowner_visible);

  const getChildLineItemsForParent = (parentId: string) =>
    expenses.filter((expense) => expense.parent_expense_id === parentId);

  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return 'N/A';
    const labels: Record<string, string> = {
      'credit_card': 'Credit Card',
      'check': 'Check',
      'cash': 'Cash',
      'bank_transfer': 'Bank Transfer',
      'other': 'Other',
      'stripe': 'Stripe',
    };
    return labels[method] || method;
  };

  const getExpenseMoneyFlow = (expense: ServiceRequestExpense) => {
    const payeeLabel = expense.vendor || 'Contractor/Vendor';

    if (expense.billable_to_homeowner && expense.needs_contractor_payment) {
      return {
        nodes: ['Homeowner', 'HoneyDone', payeeLabel],
        steps: [
          { label: 'Homeowner pays HoneyDone', isComplete: expense.is_paid },
          { label: `HoneyDone pays ${payeeLabel}`, isComplete: expense.contractor_paid },
        ],
      };
    }

    if (expense.billable_to_homeowner && !expense.needs_contractor_payment) {
      return {
        nodes: ['Homeowner', 'HoneyDone'],
        steps: [{ label: 'Homeowner pays HoneyDone', isComplete: expense.is_paid }],
      };
    }

    if (!expense.billable_to_homeowner && expense.needs_contractor_payment) {
      return {
        nodes: ['HoneyDone', payeeLabel],
        steps: [{ label: `HoneyDone pays ${payeeLabel}`, isComplete: expense.contractor_paid }],
      };
    }

    return {
      nodes: ['Homeowner', payeeLabel],
      steps: [{ label: `Homeowner pays ${payeeLabel} directly`, isComplete: true }],
    };
  };

  const handleOpenExpenseModal = (expense?: ServiceRequestExpense, parentExpense?: ServiceRequestExpense) => {
    // Reset build-up state every time the modal opens
    setWizardLineItems([]);
    setWizardLineItemDraft(getDefaultWizardLineItemDraft());
    setShowWizardAddLineItemForm(false);
    setWizardAmountOverride(false);

    if (expense) {
      setEditingExpense(expense);
      setLineItemParentExpense(null);
      setExpenseFormData({
        parent_expense_id: expense.parent_expense_id || '',
        quote_id: expense.quote_id || '',
        amount: expense.amount.toString(),
        internal_cost: expense.internal_cost !== null && expense.internal_cost !== undefined ? expense.internal_cost.toString() : '',
        homeowner_visible: expense.homeowner_visible,
        description: expense.description || '',
        expense_date: expense.expense_date || '',
        billable_to_homeowner: expense.billable_to_homeowner,
        needs_contractor_payment: expense.needs_contractor_payment,
      });
      // Skip intro step when editing
      setExpenseWizardStep(2);
    } else {
      setEditingExpense(null);
      setLineItemParentExpense(parentExpense || null);
      setExpenseFormData({
        ...getDefaultExpenseFormData(),
        parent_expense_id: parentExpense?.id || '',
        expense_date: formatDateOnly(new Date()),
      });
      setExpenseWizardStep(1);
    }
    setShowExpenseModal(true);
  };

  const handleSaveExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSavingExpense(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const isNewParentWithLineItems = !editingExpense && !lineItemParentExpense && wizardLineItems.length > 0;

      if (isNewParentWithLineItems) {
        // Build-up mode: compute parent total from line items (or use override)
        const computedTotal = wizardLineItems.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0);
        const parentAmount = wizardAmountOverride && expenseFormData.amount.trim()
          ? parseFloat(expenseFormData.amount)
          : computedTotal;

        const parentData: any = {
          service_request_id: id,
          parent_expense_id: null,
          quote_id: expenseFormData.quote_id || null,
          amount: parentAmount,
          internal_cost: null,
          homeowner_visible: true,
          description: expenseFormData.description || null,
          expense_date: expenseFormData.expense_date || null,
          category: null,
          vendor: null,
          billable_to_homeowner: expenseFormData.billable_to_homeowner,
          needs_contractor_payment: expenseFormData.needs_contractor_payment,
          created_by: user.id,
        };

        const { data: parentRow, error: parentError } = await supabase
          .from('service_request_expenses')
          .insert([parentData])
          .select('id')
          .single();

        if (parentError) throw parentError;

        const childRows = wizardLineItems.map((li) => ({
          service_request_id: id,
          parent_expense_id: parentRow.id,
          quote_id: null,
          amount: parseFloat(li.amount) || 0,
          internal_cost: li.internal_cost.trim() ? parseFloat(li.internal_cost) : null,
          homeowner_visible: li.homeowner_visible,
          description: li.description || null,
          expense_date: expenseFormData.expense_date || null,
          category: li.category || null,
          vendor: li.vendor || null,
          billable_to_homeowner: expenseFormData.billable_to_homeowner,
          needs_contractor_payment: expenseFormData.needs_contractor_payment,
          created_by: user.id,
        }));

        const { error: childError } = await supabase
          .from('service_request_expenses')
          .insert(childRows);

        if (childError) throw childError;
      } else {
        // Single expense or line item mode (edit OR add-line-item-to-existing-parent)
        const expenseData: any = {
          service_request_id: id,
          parent_expense_id: expenseFormData.parent_expense_id || null,
          quote_id: expenseFormData.quote_id || null,
          amount: parseFloat(expenseFormData.amount),
          internal_cost: expenseFormData.internal_cost.trim() ? parseFloat(expenseFormData.internal_cost) : null,
          homeowner_visible: expenseFormData.homeowner_visible,
          description: expenseFormData.description || null,
          expense_date: expenseFormData.expense_date || null,
          category: expenseFormData.category || null,
          vendor: expenseFormData.vendor || null,
          billable_to_homeowner: expenseFormData.billable_to_homeowner,
          needs_contractor_payment: expenseFormData.needs_contractor_payment,
          created_by: user.id,
        };

        if (editingExpense) {
          const { error } = await supabase
            .from('service_request_expenses')
            .update(expenseData)
            .eq('id', editingExpense.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('service_request_expenses')
            .insert([expenseData]);
          if (error) throw error;
        }
      }

      await fetchExpenses(id);
      setShowExpenseModal(false);
      setEditingExpense(null);
      setLineItemParentExpense(null);
      setExpenseFormData({
        ...getDefaultExpenseFormData(),
        expense_date: formatDateOnly(new Date()),
      });
      setWizardLineItems([]);
      setWizardLineItemDraft(getDefaultWizardLineItemDraft());
      setShowWizardAddLineItemForm(false);
      setWizardAmountOverride(false);
      setExpenseWizardStep(1);
      setError(null);
    } catch (error: any) {
      console.error('Error saving expense:', error);
      setError(error.message);
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    setDeletingExpenseId(expenseId);
    try {
      const { error } = await supabase
        .from('service_request_expenses')
        .delete()
        .eq('id', expenseId);

      if (error) throw error;

      if (id) {
        await fetchExpenses(id);
      }
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      setError(error.message);
    } finally {
      setDeletingExpenseId(null);
    }
  };

  const handleToggleLineItemVisibility = async (expense: ServiceRequestExpense) => {
    if (!id) return;

    try {
      setUpdating(true);
      const { error: visibilityError } = await supabase
        .from('service_request_expenses')
        .update({
          homeowner_visible: !expense.homeowner_visible,
        })
        .eq('id', expense.id);

      if (visibilityError) throw visibilityError;
      await fetchExpenses(id);
    } catch (toggleError: any) {
      console.error('Error toggling line item visibility:', toggleError);
      setError(toggleError.message || 'Failed to update line item visibility');
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenHomeownerPaymentModal = (expense: ServiceRequestExpense) => {
    setExpenseToMarkHomeownerPaid(expense);
    setHomeownerPaymentFormData({
      payment_date: expense.payment_date || formatDateOnly(new Date()),
      payment_method: expense.payment_method || '',
      payment_notes: expense.payment_notes || ''
    });
    setShowHomeownerPaymentModal(true);
  };

  const handleOpenContractorPaymentModal = (expense: ServiceRequestExpense) => {
    setExpenseToMarkContractorPaid(expense);
    setContractorPaymentFormData({
      payment_date: expense.contractor_payment_date || formatDateOnly(new Date()),
      payment_method: expense.contractor_payment_method || '',
      payment_notes: expense.contractor_payment_notes || ''
    });
    setShowContractorPaymentModal(true);
  };

  const handleMarkHomeownerPaid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!expenseToMarkHomeownerPaid || !id) return;

    setMarkingHomeownerPaid(true);
    try {
      const updateData: any = {
        is_paid: true,
        payment_date: homeownerPaymentFormData.payment_date || null,
        payment_method: homeownerPaymentFormData.payment_method || null,
        payment_notes: homeownerPaymentFormData.payment_notes || null
      };

      const { error } = await supabase
        .from('service_request_expenses')
        .update(updateData)
        .eq('id', expenseToMarkHomeownerPaid.id);

      if (error) throw error;

      // If this expense is linked to an invoice, mark the invoice as paid too
      if (expenseToMarkHomeownerPaid.invoice_id) {
        const paidAt = homeownerPaymentFormData.payment_date
          ? new Date(homeownerPaymentFormData.payment_date).toISOString()
          : new Date().toISOString();

        const { error: invoiceError } = await supabase
          .from('invoices')
          .update({ status: 'paid', paid_at: paidAt })
          .eq('id', expenseToMarkHomeownerPaid.invoice_id);

        if (invoiceError) {
          console.error('Failed to update invoice status:', invoiceError);
        }
      }

      await fetchExpenses(id);
      setShowHomeownerPaymentModal(false);
      setExpenseToMarkHomeownerPaid(null);
      setHomeownerPaymentFormData({
        payment_date: '',
        payment_method: '',
        payment_notes: ''
      });
      setError(null);
    } catch (error: any) {
      console.error('Error marking homeowner payment as paid:', error);
      setError(error.message);
    } finally {
      setMarkingHomeownerPaid(false);
    }
  };

  const handleMarkContractorPaid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!expenseToMarkContractorPaid || !id) return;

    setMarkingContractorPaid(true);
    try {
      const updateData: any = {
        contractor_paid: true,
        contractor_payment_date: contractorPaymentFormData.payment_date || null,
        contractor_payment_method: contractorPaymentFormData.payment_method || null,
        contractor_payment_notes: contractorPaymentFormData.payment_notes || null
      };

      const { error } = await supabase
        .from('service_request_expenses')
        .update(updateData)
        .eq('id', expenseToMarkContractorPaid.id);

      if (error) throw error;

      await fetchExpenses(id);
      setShowContractorPaymentModal(false);
      setExpenseToMarkContractorPaid(null);
      setContractorPaymentFormData({
        payment_date: '',
        payment_method: '',
        payment_notes: ''
      });
      setError(null);
    } catch (error: any) {
      console.error('Error marking contractor payment as paid:', error);
      setError(error.message);
    } finally {
      setMarkingContractorPaid(false);
    }
  };

  const handleOpenRequestHomeownerPaymentConfirmModal = (expense: ServiceRequestExpense) => {
    if (!id || !request?.home?.owner?.id || !expense.billable_to_homeowner || expense.is_paid) return;
    setExpenseToRequestHomeownerPayment(expense);
    setShowRequestHomeownerPaymentConfirmModal(true);
  };

  const handleCloseRequestHomeownerPaymentConfirmModal = () => {
    setShowRequestHomeownerPaymentConfirmModal(false);
    setExpenseToRequestHomeownerPayment(null);
  };

  const handleConfirmRequestHomeownerPayment = async () => {
    if (!id || !request?.home?.owner?.id || !expenseToRequestHomeownerPayment) return;

    try {
      setRequestingPaymentExpenseId(expenseToRequestHomeownerPayment.id);
      setError(null);
      const { data: authData } = await supabase.auth.getUser();
      const visibleLineItems = getVisibleLineItemsForParent(expenseToRequestHomeownerPayment.id);

      await sendExpensePaymentRequestEmail({
        expenseId: expenseToRequestHomeownerPayment.id,
        serviceRequestId: id,
        homeownerId: request.home.owner.id,
        homeownerName: request.home.owner.full_name || null,
        serviceRequestTitle: request.title || null,
        expenseContext: getExpenseContextLabel({
          ...expenseToRequestHomeownerPayment,
          service_request: { title: request.title || null },
        }),
        expenseAmount: expenseToRequestHomeownerPayment.amount,
        expenseDescription: expenseToRequestHomeownerPayment.description,
        lineItems: visibleLineItems.map((lineItem) => ({
          amount: lineItem.amount,
          description: lineItem.description,
        })),
        requestedBy: authData.user?.id ?? null,
      });

      handleCloseRequestHomeownerPaymentConfirmModal();
      await fetchExpenses(id);
    } catch (requestError: any) {
      console.error('Error requesting homeowner payment:', requestError);
      setError(requestError?.message || 'Failed to request homeowner payment');
    } finally {
      setRequestingPaymentExpenseId(null);
    }
  };

  const handleOpenInvoiceModal = () => {
    // Check if homeowner has Stripe customer
    if (!request?.home?.owner) {
      alert('Homeowner information not found');
      return;
    }
    
    // Set default due date (30 days from now)
    const defaultDueDate = new Date();
    defaultDueDate.setDate(defaultDueDate.getDate() + 30);
    setInvoiceDueDate(defaultDueDate.toISOString().split('T')[0]);
    setInvoiceNotes('');
    setInvoiceError(null);
    setShowInvoiceModal(true);
  };

  const handleCreateInvoice = async () => {
    if (!id || selectedExpenseIds.size === 0 || !request?.home?.owner) return;
    
    setCreatingInvoice(true);
    setInvoiceError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the selected expenses with their details
      const selectedExpenses = getParentExpenses().filter(exp => selectedExpenseIds.has(exp.id));
      const nonBillableExpenses = selectedExpenses.filter(exp => !exp.billable_to_homeowner);
      if (nonBillableExpenses.length > 0) {
        throw new Error('Selected expenses include non-billable items. Only homeowner-billable expenses can be invoiced.');
      }
      
      // Get homeowner ID from the home
      const { data: homeData, error: homeError } = await supabase
        .from('homes')
        .select('owner_id')
        .eq('id', request.home_id!)
        .single();

      if (homeError || !homeData) {
        throw new Error('Failed to fetch homeowner information');
      }

      const homeownerId = homeData.owner_id;

      // Check if homeowner has stripe_customer_id
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('stripe_customer_id')
        .eq('id', homeownerId)
        .single();

      if (profileError) {
        throw new Error('Failed to fetch homeowner profile');
      }

      if (!profileData?.stripe_customer_id) {
        throw new Error('Homeowner does not have a Stripe customer account. Please create one first in the profile page.');
      }

      // Call edge function to create Stripe invoice
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const functionUrl = `${SUPABASE_URL}/functions/v1/create-stripe-invoice`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          homeownerId,
          expenseIds: Array.from(selectedExpenseIds),
          dueDate: invoiceDueDate || undefined,
          notes: invoiceNotes || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to create invoice');
      }

      // Refresh expenses and clear selection
      await fetchExpenses(id);
      setSelectedExpenseIds(new Set());
      setShowInvoiceModal(false);
      
      // Show success message
      const totalAmount = selectedExpenses.reduce((sum, exp) => sum + exp.amount, 0);
      alert(`Invoice created successfully for ${selectedExpenses.length} expense(s) totaling ${formatCurrency(totalAmount)}. Homeowner can now view and pay the invoice.`);
      
    } catch (error: any) {
      console.error('Error creating invoice:', error);
      setInvoiceError(error.message || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleOpenQuoteModal = (quote?: ServiceRequestQuote) => {
    if (quote) {
      setEditingQuote(quote);
      const selectedProvider = serviceProviders.find(p => p.id === quote.service_provider_id);
      setServiceProviderSearch(selectedProvider?.company_name || '');
      setQuoteFormData({
        service_provider_id: quote.service_provider_id || '',
        service_type: quote.service_type || '',
        work_description: quote.work_description || '',
        amount: quote.amount?.toString() || '',
        quote_date: quote.quote_date || '',
        expires_at: quote.expires_at || '',
        notes: quote.notes || '',
        status: quote.status
      });
    } else {
      setEditingQuote(null);
      setServiceProviderSearch('');
      setQuoteFormData({
        service_provider_id: '',
        service_type: '',
        work_description: '',
        amount: '',
        quote_date: formatDateOnly(new Date()),
        expires_at: formatDateOnly(new Date()),
        notes: '',
        status: 'pending'
      });
    }
    setShowProviderDropdown(false);
    setShowQuoteModal(true);
  };

  const filteredServiceProviders = serviceProviders.filter(provider =>
    provider.company_name.toLowerCase().includes(serviceProviderSearch.toLowerCase()) ||
    provider.email?.toLowerCase().includes(serviceProviderSearch.toLowerCase()) ||
    provider.phone?.includes(serviceProviderSearch)
  );

  const handleSelectServiceProvider = (providerId: string) => {
    const provider = serviceProviders.find(p => p.id === providerId);
    setQuoteFormData({ ...quoteFormData, service_provider_id: providerId });
    setServiceProviderSearch(provider?.company_name || '');
    setShowProviderDropdown(false);
  };

  const createBidLink = async () => {
    if (!id) {
      throw new Error('Service request ID is required.');
    }

    const { data, error: invokeError } = await supabase.functions.invoke('create-service-request-bid-link', {
      body: {
        serviceRequestId: id,
      },
    });

    if (invokeError) {
      throw new Error(invokeError.message || 'Failed to generate bid link.');
    }

    if (!data?.url || !data?.expiresAt) {
      throw new Error('Bid link response was incomplete.');
    }

    return {
      url: data.url as string,
      expiresAt: data.expiresAt as string,
      reusedExisting: Boolean(data.reusedExisting),
    };
  };

  const handleCopyBidLink = async () => {
    setGeneratingBidLink(true);
    setBidShareMessage(null);
    try {
      const bidLink = await createBidLink();
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(bidLink.url);
      } else if (typeof document !== 'undefined') {
        const textArea = document.createElement('textarea');
        textArea.value = bidLink.url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      } else {
        throw new Error('Clipboard API unavailable');
      }

      setBidShareMessageType('success');
      if (bidLink.reusedExisting) {
        setBidShareMessage(`Existing bid link copied. Expires ${new Date(bidLink.expiresAt).toLocaleString()}.`);
      } else {
        setBidShareMessage(`New bid link copied. Expires ${new Date(bidLink.expiresAt).toLocaleString()}.`);
      }
    } catch (copyError: any) {
      console.error('Error generating bid link:', copyError);
      setBidShareMessageType('error');
      setBidShareMessage(copyError?.message || 'Could not generate bid link.');
    } finally {
      setGeneratingBidLink(false);
    }
  };

  const handleSaveQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSavingQuote(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const quoteData: any = {
        service_request_id: id,
        service_provider_id: quoteFormData.service_provider_id || null,
        service_type: quoteFormData.service_type || null,
        work_description: quoteFormData.work_description || null,
        amount: quoteFormData.amount ? parseFloat(quoteFormData.amount) : null,
        quote_date: quoteFormData.quote_date || null,
        expires_at: quoteFormData.expires_at || null,
        notes: quoteFormData.notes || null,
        status: quoteFormData.status,
        created_by: user.id
      };

      // Can't accept quotes without service_request_id
      if (!quoteData.service_request_id && quoteData.status === 'accepted') {
        quoteData.status = 'pending';
      }

      let savedQuoteId: string;
      
      if (editingQuote) {
        // Update existing quote
        const { error } = await supabase
          .from('service_request_quotes')
          .update(quoteData)
          .eq('id', editingQuote.id);

        if (error) throw error;
        savedQuoteId = editingQuote.id;
      } else {
        // Create new quote
        const { data: newQuote, error } = await supabase
          .from('service_request_quotes')
          .insert([quoteData])
          .select()
          .single();

        if (error) throw error;
        savedQuoteId = newQuote.id;
        
        // Set the newly created quote as editingQuote so attachments can be added
        setEditingQuote(newQuote);
      }

      // If accepting a quote, reject all other pending quotes
      if (quoteFormData.status === 'accepted') {
        const { error: rejectError } = await supabase
          .from('service_request_quotes')
          .update({ status: 'rejected' })
          .eq('service_request_id', id)
          .eq('status', 'pending')
          .neq('id', savedQuoteId);

        if (rejectError) {
          console.error('Error rejecting other quotes:', rejectError);
        }
      }

      // Refresh quotes
      await fetchQuotes(id);
      
      // If this was a new quote, keep modal open so user can add attachments
      // If editing, close the modal
      const wasEditing = !!editingQuote;
      
      if (wasEditing) {
        // Reset form and close modal
        setShowQuoteModal(false);
        setEditingQuote(null);
        setServiceProviderSearch('');
        setShowProviderDropdown(false);
        setQuoteFormData({
          service_provider_id: '',
          service_type: '',
          work_description: '',
          amount: '',
          quote_date: formatDateOnly(new Date()),
          expires_at: formatDateOnly(new Date()),
          notes: '',
          status: 'pending'
        });
      } else {
        // Keep modal open for new quote, just reset the form status
        setQuoteFormData({
          ...quoteFormData,
          status: 'pending' // Reset status so they can change it if needed
        });
      }
    } catch (error: any) {
      console.error('Error saving quote:', error);
      setError(error.message);
    } finally {
      setSavingQuote(false);
    }
  };

  const handleDeleteQuote = async (quoteId: string) => {
    if (!id) return;
    
    setDeletingQuoteId(quoteId);
    try {
      const { error } = await supabase
        .from('service_request_quotes')
        .delete()
        .eq('id', quoteId);

      if (error) throw error;

      // Refresh quotes
      await fetchQuotes(id);
    } catch (error) {
      console.error('Error deleting quote:', error);
    } finally {
      setDeletingQuoteId(null);
    }
  };

  const handleAcceptQuote = async (quoteId: string) => {
    if (!id) return;

    const quote = quotes.find(q => q.id === quoteId);
    if (!quote || !quote.service_request_id) {
      setError('Quote must be associated with a service request to be accepted');
      return;
    }

    setUpdating(true);
    try {
      // Accept the selected quote
      const { error: acceptError } = await supabase
        .from('service_request_quotes')
        .update({ status: 'accepted' })
        .eq('id', quoteId);

      if (acceptError) throw acceptError;

      // Reject all other pending quotes
      const { error: rejectError } = await supabase
        .from('service_request_quotes')
        .update({ status: 'rejected' })
        .eq('service_request_id', id)
        .eq('status', 'pending')
        .neq('id', quoteId);

      if (rejectError) {
        console.error('Error rejecting other quotes:', rejectError);
      }

      // Refresh quotes
      await fetchQuotes(id);
    } catch (error: any) {
      console.error('Error accepting quote:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleRejectQuote = async (quoteId: string) => {
    if (!id) return;

    setUpdating(true);
    try {
      const { error } = await supabase
        .from('service_request_quotes')
        .update({ status: 'rejected' })
        .eq('id', quoteId);

      if (error) throw error;

      // Refresh quotes
      await fetchQuotes(id);
    } catch (error: any) {
      console.error('Error rejecting quote:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const getPriorityColor = (priority: ServiceRequest['priority']) => {
    switch (priority) {
      case 'urgent': return { color: '#dc2626', backgroundColor: '#fee2e2' };
      case 'high': return { color: '#ea580c', backgroundColor: '#ffedd5' };
      case 'normal': return { color: '#2563eb', backgroundColor: '#dbeafe' };
      case 'low': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      default: return { color: '#4b5563', backgroundColor: '#f3f4f6' };
    }
  };

  const getStatusColor = (status: ServiceRequest['status']) => {
    switch (status) {
      case 'completed': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      case 'in_progress': return { color: '#2563eb', backgroundColor: '#dbeafe' };
      case 'cancelled': return { color: '#4b5563', backgroundColor: '#f3f4f6' };
      default: return { color: '#ea580c', backgroundColor: '#ffedd5' };
    }
  };

  const getQuoteStatusColor = (status: ServiceRequestQuote['status']) => {
    switch (status) {
      case 'accepted': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      case 'rejected': return { color: '#dc2626', backgroundColor: '#fee2e2' };
      default: return { color: '#ca8a04', backgroundColor: '#fef9c3' };
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const downloadAttachment = async (attachment: any) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-quote-attachments')
      .getPublicUrl(attachment.file_url);

    await downloadFile(publicUrl, attachment.file_name);
  };

  const viewImage = (attachment: any) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-quote-attachments')
      .getPublicUrl(attachment.file_url);
    setPreviewImage(publicUrl);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon size={12} color="#6b7280" />;
    if (fileType.startsWith('video/')) return <Video size={12} color="#6b7280" />;
    if (fileType === 'application/pdf') return <FileText size={12} color="#6b7280" />;
    return <File size={12} color="#6b7280" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImageFile = (fileType: string) => fileType.startsWith('image/');

  if (loading) {
    return (
      <AdminLayout activeTab="requests">
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#9ca3af" />
        </View>
      </AdminLayout>
    );
  }

  if (error || !request) {
    return (
      <AdminLayout activeTab="requests">
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error || 'Service request not found'}</Text>
        </View>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="requests">
    <WebScrollView style={styles.container}>
      <View style={styles.header}>
        <Pressable
          onPress={() => navigate('/admin')}
          style={styles.backButton}
        >
          <ArrowLeft size={20} color="#4b5563" />
          <Text style={styles.backButtonText}>Back to Admin</Text>
        </Pressable>
        <Text style={styles.pageTitle}>Service Request Detail</Text>
      </View>

      {error && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorBannerText}>{error}</Text>
        </View>
      )}

      <View style={[styles.contentRow, isMobile && styles.contentRowMobile]}>
        {/* Main Content */}
        <View style={[styles.mainContent, isMobile && styles.mainContentMobile]}>
          {/* Request Details */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <View style={styles.cardHeaderLeft}>
                {editingTitle ? (
                  <View style={styles.editRow}>
                    <TextInput
                      style={styles.titleInput}
                      value={editedTitle}
                      onChangeText={setEditedTitle}
                      placeholder="Service request title"
                      autoFocus
                    />
                    <View style={styles.editActions}>
                      <Pressable
                        onPress={handleTitleSave}
                        disabled={savingTitle || !editedTitle.trim()}
                        style={[styles.editButton, styles.saveButton]}
                      >
                        {savingTitle ? (
                          <Loader2 size={16} color="#ffffff" />
                        ) : (
                          <CheckCircle2 size={16} color="#ffffff" />
                        )}
                      </Pressable>
                      <Pressable
                        onPress={handleTitleCancel}
                        disabled={savingTitle}
                        style={[styles.editButton, styles.cancelButton]}
                      >
                        <X size={16} color="#6b7280" />
                      </Pressable>
                    </View>
                  </View>
                ) : (
                  <View style={styles.titleRow}>
                    <Text style={styles.cardTitle}>{request.title}</Text>
                    <Pressable
                      onPress={handleTitleEdit}
                      style={styles.editIconButton}
                    >
                      <Edit3 size={16} color="#6b7280" />
                    </Pressable>
                  </View>
                )}
                <Text style={styles.cardOwner}>
                  {request.home?.owner?.full_name || 'Unknown Owner'}
                </Text>
                <View style={styles.cardMeta}>
                  <View style={styles.metaItem}>
                    <HomeIcon size={16} color="#6b7280" />
                    <Text style={styles.metaText}>
                      {request.home?.address}, {request.home?.city}, {request.home?.state}
                    </Text>
                  </View>
                  <View style={styles.metaItem}>
                    <Clock size={16} color="#6b7280" />
                    {editingCreatedAt ? (
                      <View style={styles.dateEditRow}>
                        <TextInput
                          style={styles.dateInput}
                          value={editedCreatedAt}
                          onChangeText={setEditedCreatedAt}
                          placeholder="YYYY-MM-DD"
                          autoFocus
                        />
                        <Pressable
                          onPress={handleCreatedAtSave}
                          disabled={savingCreatedAt || !editedCreatedAt.trim()}
                          style={[styles.editButton, styles.saveButton, styles.smallButton]}
                        >
                          {savingCreatedAt ? (
                            <Loader2 size={14} color="#ffffff" />
                          ) : (
                            <CheckCircle2 size={14} color="#ffffff" />
                          )}
                        </Pressable>
                        <Pressable
                          onPress={handleCreatedAtCancel}
                          disabled={savingCreatedAt}
                          style={[styles.editButton, styles.cancelButton, styles.smallButton]}
                        >
                          <X size={14} color="#6b7280" />
                        </Pressable>
                      </View>
                    ) : (
                      <View style={styles.dateDisplayRow}>
                        <Text style={styles.metaText}>
                          Created: {formatDateForDisplay(request.created_at)}
                        </Text>
                        <Pressable
                          onPress={handleCreatedAtEdit}
                          style={styles.editIconButton}
                        >
                          <Edit3 size={14} color="#6b7280" />
                        </Pressable>
                      </View>
                    )}
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.cardBody}>
              <View style={styles.section}>
                <View style={styles.sectionTitleRow}>
                  <Text style={styles.sectionTitle}>Description</Text>
                  {!editingDescription && (
                    <Pressable
                      onPress={handleDescriptionEdit}
                      style={styles.editIconButton}
                    >
                      <Edit3 size={16} color="#6b7280" />
                    </Pressable>
                  )}
                </View>
                {editingDescription ? (
                  <View style={styles.descriptionBox}>
                    <TextInput
                      style={styles.descriptionInput}
                      value={editedDescription}
                      onChangeText={setEditedDescription}
                      placeholder="Service request description"
                      multiline
                      numberOfLines={6}
                      textAlignVertical="top"
                      autoFocus
                    />
                    <View style={styles.editActions}>
                      <Pressable
                        onPress={handleDescriptionSave}
                        disabled={savingDescription}
                        style={[styles.editButton, styles.saveButton]}
                      >
                        {savingDescription ? (
                          <Loader2 size={16} color="#ffffff" />
                        ) : (
                          <CheckCircle2 size={16} color="#ffffff" />
                        )}
                      </Pressable>
                      <Pressable
                        onPress={handleDescriptionCancel}
                        disabled={savingDescription}
                        style={[styles.editButton, styles.cancelButton]}
                      >
                        <X size={16} color="#6b7280" />
                      </Pressable>
                    </View>
                  </View>
                ) : (
                  <View style={styles.descriptionBox}>
                    <Text style={styles.descriptionText}>
                      {request.description || 'No description provided'}
                    </Text>
                  </View>
                )}
              </View>

              <View style={styles.detailRow}>
                <View style={styles.detailColumn}>
                  <Text style={styles.sectionTitle}>Priority</Text>
                  <View style={[styles.pickerContainer, { borderColor: getPriorityColor(request.priority).color }]}>
                    <Picker
                      selectedValue={request.priority}
                      onValueChange={(value) => handlePriorityUpdate(value as ServiceRequest['priority'])}
                      style={styles.picker}
                      enabled={!updating}
                    >
                      <Picker.Item label="Low" value="low" />
                      <Picker.Item label="Normal" value="normal" />
                      <Picker.Item label="High" value="high" />
                      <Picker.Item label="Urgent" value="urgent" />
                    </Picker>
                  </View>
                </View>
                <View style={styles.detailColumn}>
                  <Text style={styles.sectionTitle}>Status</Text>
                  <View style={[styles.pickerContainer, { borderColor: getStatusColor(request.status).color }]}>
                    <Picker
                      selectedValue={request.status}
                      onValueChange={(value) => handleStatusUpdate(value as ServiceRequest['status'])}
                      style={styles.picker}
                      enabled={!updating}
                    >
                      <Picker.Item label="Pending" value="pending" />
                      <Picker.Item label="In Progress" value="in_progress" />
                      <Picker.Item label="Completed" value="completed" />
                      <Picker.Item label="Cancelled" value="cancelled" />
                    </Picker>
                  </View>
                </View>
              </View>

              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Assigned Owner</Text>
                <View style={styles.ownerRow}>
                  {request.assigned_owner ? (
                    <View style={styles.ownerInfo}>
                      <UserCheck size={16} color="#16a34a" />
                      <Text style={styles.ownerName}>
                        {request.assigned_owner.full_name}
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.ownerInfo}>
                      <User size={16} color="#9ca3af" />
                      <Text style={styles.ownerUnassigned}>
                        Unassigned
                      </Text>
                    </View>
                  )}
                  <Pressable
                    onPress={() => setShowOwnerModal(true)}
                  >
                    <Text style={styles.assignOwnerButton}>
                      {request.assigned_owner ? 'Reassign' : 'Assign Owner'}
                    </Text>
                  </Pressable>
                </View>
              </View>

              <View style={styles.section}>
                <View style={styles.sectionTitleRow}>
                  <Text style={styles.sectionTitle}>Completion Date</Text>
                  {!editingCompletedAt && (
                    <Pressable
                      onPress={handleCompletedAtEdit}
                      style={styles.editIconButton}
                    >
                      <Edit3 size={14} color="#6b7280" />
                    </Pressable>
                  )}
                </View>
                {editingCompletedAt ? (
                  <View style={styles.dateEditRow}>
                    <TextInput
                      style={styles.dateInput}
                      value={editedCompletedAt}
                      onChangeText={setEditedCompletedAt}
                      placeholder="YYYY-MM-DD"
                    />
                    <View style={styles.editActions}>
                      <Pressable
                        onPress={handleCompletedAtSave}
                        disabled={savingCompletedAt}
                        style={[styles.editButton, styles.saveButton, styles.smallButton]}
                      >
                        {savingCompletedAt ? (
                          <Loader2 size={14} color="#ffffff" />
                        ) : (
                          <CheckCircle2 size={14} color="#ffffff" />
                        )}
                      </Pressable>
                      <Pressable
                        onPress={handleCompletedAtCancel}
                        disabled={savingCompletedAt}
                        style={[styles.editButton, styles.cancelButton, styles.smallButton]}
                      >
                        <X size={14} color="#6b7280" />
                      </Pressable>
                    </View>
                  </View>
                ) : (
                  <View style={styles.dateDisplayRow}>
                    <Text style={styles.metaText}>
                      {request.completed_at 
                        ? `Completed: ${formatDateForDisplay(request.completed_at)}`
                        : 'Not completed'}
                    </Text>
                  </View>
                )}
              </View>

              {request.visit_scheduled_at && (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>Visit Information</Text>
                  <View style={styles.visitInfoBox}>
                    <View style={styles.visitInfoRow}>
                      <Calendar size={16} color="#2563eb" />
                      <Text style={styles.visitInfoText}>
                        Visit Scheduled: {new Date(request.visit_scheduled_at).toLocaleDateString()}
                      </Text>
                    </View>
                    {request.visit_completed_at && (
                      <View style={styles.visitInfoRow}>
                        <Clock size={16} color="#16a34a" />
                        <Text style={styles.visitInfoTextCompleted}>
                          Completed: {formatDateForDisplay(request.visit_completed_at)}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              )}
            </View>
          </View>

          {/* Notes Section */}
          <View style={styles.card}>
            <View style={styles.sectionTitleRow}>
              <MessageSquare size={20} color="#6b7280" />
              <Text style={styles.sectionTitle}>Notes</Text>
            </View>
            
            {/* Add Note Form */}
            <View style={styles.addNoteForm}>
              <TextInput
                style={styles.noteInput}
                value={newNote}
                onChangeText={setNewNote}
                placeholder="Add a note..."
                multiline
                numberOfLines={2}
                textAlignVertical="top"
              />
              <Pressable
                style={[styles.addNoteButton, (!newNote.trim() || addingNote) && styles.buttonDisabled]}
                onPress={handleAddNote}
                disabled={!newNote.trim() || addingNote}
              >
                {addingNote ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={styles.addNoteButtonText}>Add</Text>
                )}
              </Pressable>
            </View>
            
            {/* Notes List */}
            <View style={styles.notesList}>
              {notes.length === 0 ? (
                <Text style={styles.noNotesText}>No notes yet</Text>
              ) : (
                notes.map((note) => (
                  <View key={note.id} style={styles.noteCard}>
                    <View style={styles.noteContent}>
                      <Text style={styles.noteText}>{note.content}</Text>
                      <Pressable
                        onPress={() => handleDeleteNote(note.id)}
                        disabled={deletingNoteId === note.id}
                      >
                        {deletingNoteId === note.id ? (
                          <ActivityIndicator size="small" color="#6b7280" />
                        ) : (
                          <Trash2 size={16} color="#9ca3af" />
                        )}
                      </Pressable>
                    </View>
                    <View style={styles.noteFooter}>
                      <Text style={styles.noteDate}>
                        {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString()}
                      </Text>
                      <Text style={styles.noteAuthor}>
                        {note.creator?.full_name || 'Unknown User'}
                      </Text>
                    </View>
                  </View>
                ))
              )}
            </View>
          </View>

          {/* Admin Tasks Section */}
          <View style={styles.card}>
            <View style={[styles.sectionHeader, isMobile && styles.sectionHeaderMobile]}>
              <View style={styles.sectionTitleRow}>
                <CheckCircle2 size={20} color="#6b7280" />
                <Text style={styles.sectionTitle}>Admin Tasks</Text>
              </View>
              <View style={[styles.sectionActions, isMobile && styles.sectionActionsMobile]}>
                <Pressable
                  style={styles.addButton}
                  onPress={handleOpenCreateTaskModal}
                >
                  <Plus size={16} color="#ffffff" />
                  <Text style={styles.addButtonText}>Create Task</Text>
                </Pressable>
                <Pressable
                  style={styles.associateButton}
                  onPress={() => {
                    fetchStandaloneAdminTasks();
                    setShowAssociateTaskModal(true);
                  }}
                >
                  <Link2 size={16} color="#ffffff" />
                  <Text style={styles.associateButtonText}>Associate Task</Text>
                </Pressable>
              </View>
            </View>

            {loadingAdminTasks ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color="#9ca3af" />
              </View>
            ) : adminTasks.length === 0 ? (
              <Text style={styles.noDataText}>No admin tasks linked yet</Text>
            ) : (
              <View style={styles.adminTasksList}>
                {adminTasks.map((task) => {
                  const taskPriorityColors = getPriorityColor(task.priority);
                  const taskStatusColors = getStatusColor(task.status as ServiceRequest['status']);
                  return (
                    <View key={task.id} style={styles.adminTaskCard}>
                      <View style={styles.adminTaskHeader}>
                        <Text style={styles.adminTaskTitle}>{task.title}</Text>
                        <View style={styles.adminTaskHeaderActions}>
                          <Pressable
                            onPress={() => handleOpenEditTaskModal(task)}
                            style={styles.adminTaskEditButton}
                          >
                            <Edit3 size={14} color="#6b7280" />
                          </Pressable>
                        </View>
                      </View>
                      <View style={styles.adminTaskBadges}>
                          <View style={[styles.badge, { backgroundColor: taskPriorityColors.backgroundColor }]}>
                            <Text style={[styles.badgeText, { color: taskPriorityColors.color }]}>
                              {task.priority}
                            </Text>
                          </View>
                          <View style={[styles.badge, { backgroundColor: taskStatusColors.backgroundColor }]}>
                            <Text style={[styles.badgeText, { color: taskStatusColors.color }]}>
                              {task.status.replace('_', ' ')}
                            </Text>
                          </View>
                      </View>
                      {task.description && (
                        <Text style={styles.adminTaskDescription}>{task.description}</Text>
                      )}
                      <View style={styles.adminTaskMeta}>
                        {task.due_date && (
                          <View style={styles.metaItem}>
                            <Clock size={12} color="#6b7280" />
                            <Text style={styles.quoteMetaText}>
                              Due: {formatDateForDisplay(task.due_date)}
                            </Text>
                          </View>
                        )}
                        {task.assignee?.full_name && (
                          <View style={styles.metaItem}>
                            <User size={12} color="#6b7280" />
                            <Text style={styles.quoteMetaText}>
                              Assigned: {task.assignee.full_name}
                            </Text>
                          </View>
                        )}
                        {task.completed_at && (
                          <View style={styles.metaItem}>
                            <CheckCircle2 size={12} color="#16a34a" />
                            <Text style={styles.quoteMetaText}>
                              Completed: {formatDateForDisplay(task.completed_at)}
                            </Text>
                          </View>
                        )}
                      </View>
                      {task.status !== 'completed' && (
                        <Pressable
                          style={[
                            styles.adminTaskCompleteButton,
                            completingAdminTaskId === task.id && styles.adminTaskCompleteButtonDisabled,
                          ]}
                          onPress={() => handleCompleteAdminTask(task.id)}
                          disabled={completingAdminTaskId === task.id}
                        >
                          {completingAdminTaskId === task.id ? (
                            <ActivityIndicator size="small" color="#ffffff" />
                          ) : (
                            <>
                              <CheckCircle2 size={14} color="#ffffff" />
                              <Text style={styles.adminTaskCompleteButtonText}>Mark Complete</Text>
                            </>
                          )}
                        </Pressable>
                      )}
                    </View>
                  );
                })}
              </View>
            )}
          </View>

          {/* Quotes Section */}
          <View style={styles.card}>
            <View style={[styles.sectionHeader, isMobile && styles.sectionHeaderMobile]}>
              <View style={styles.sectionTitleRow}>
                <DollarSign size={20} color="#6b7280" />
                <Text style={styles.sectionTitle}>Quotes/Estimates</Text>
              </View>
              <View style={[styles.sectionActions, isMobile && styles.sectionActionsMobile]}>
                <Pressable
                  style={[styles.bidLinkButton, generatingBidLink && styles.bidLinkButtonDisabled]}
                  onPress={handleCopyBidLink}
                  disabled={generatingBidLink}
                >
                  {generatingBidLink ? (
                    <Loader2 size={16} color="#ffffff" />
                  ) : (
                    <Send size={16} color="#ffffff" />
                  )}
                  <Text style={styles.bidLinkButtonText}>
                    {generatingBidLink ? 'Generating...' : 'Generate Bid Link'}
                  </Text>
                </Pressable>
                <Pressable
                  style={styles.associateButton}
                  onPress={() => {
                    fetchStandaloneQuotes();
                    setShowAssociateModal(true);
                  }}
                >
                  <Link2 size={16} color="#ffffff" />
                  <Text style={styles.associateButtonText}>Associate Quote</Text>
                </Pressable>
                <Pressable
                  style={styles.addButton}
                  onPress={() => handleOpenQuoteModal()}
                >
                  <Plus size={16} color="#ffffff" />
                  <Text style={styles.addButtonText}>Add Quote</Text>
                </Pressable>
              </View>
            </View>
            
            {loadingQuotes ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color="#9ca3af" />
              </View>
            ) : quotes.length === 0 ? (
              <View style={{ gap: 8 }}>
                {bidShareMessage && (
                  <Text
                    style={[
                      styles.shareMessageText,
                      bidShareMessageType === 'success' ? styles.shareMessageSuccess : styles.shareMessageError
                    ]}
                  >
                    {bidShareMessage}
                  </Text>
                )}
                <Text style={styles.noDataText}>No quotes yet</Text>
              </View>
            ) : (
              <View style={styles.quotesList}>
                {bidShareMessage && (
                  <Text
                    style={[
                      styles.shareMessageText,
                      bidShareMessageType === 'success' ? styles.shareMessageSuccess : styles.shareMessageError
                    ]}
                  >
                    {bidShareMessage}
                  </Text>
                )}
                {quotes.map((quote) => {
                  const quoteStatusColors = getQuoteStatusColor(quote.status);
                  return (
                    <View key={quote.id} style={styles.quoteCard}>
                      <View style={styles.quoteCardHeader}>
                        <View style={styles.quoteCardContent}>
                          <View style={styles.quoteTitleRow}>
                            <Text style={styles.quoteProviderName}>
                              {quote.service_provider?.company_name || 'Unknown Provider'}
                            </Text>
                            <View style={[styles.badge, { backgroundColor: quoteStatusColors.backgroundColor }]}>
                              <Text style={[styles.badgeText, { color: quoteStatusColors.color }]}>
                                {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                              </Text>
                            </View>
                          </View>
                          {quote.amount !== null && (
                            <Text style={styles.quoteAmount}>
                              {formatCurrency(quote.amount)}
                            </Text>
                          )}
                          {quote.notes && (
                            <Text style={styles.quoteNotes}>{quote.notes}</Text>
                          )}
                          <View style={styles.quoteMeta}>
                            {quote.quote_date && (
                              <View style={styles.metaItem}>
                                <Calendar size={12} color="#6b7280" />
                                <Text style={styles.quoteMetaText}>
                                  Quote Date: {formatDateForDisplay(quote.quote_date)}
                                </Text>
                              </View>
                            )}
                            {quote.expires_at && (
                              <View style={styles.metaItem}>
                                <Clock size={12} color="#6b7280" />
                                <Text style={styles.quoteMetaText}>
                                  Expires: {formatDateForDisplay(quote.expires_at)}
                                </Text>
                              </View>
                            )}
                            {(quote as any).attachment_count > 0 && (
                              <View style={styles.metaItem}>
                                <Paperclip size={12} color="#6b7280" />
                                <Text style={styles.quoteMetaText}>
                                  {(quote as any).attachment_count} file{((quote as any).attachment_count !== 1) ? 's' : ''}
                                </Text>
                              </View>
                            )}
                          </View>
                          {/* Attachments List */}
                          {quoteAttachments[quote.id] && quoteAttachments[quote.id].length > 0 && (
                            <View style={styles.attachmentsContainer}>
                              {quoteAttachments[quote.id].map((attachment: any) => (
                                <Pressable
                                  key={attachment.id}
                                  style={styles.attachmentItem}
                                  onPress={() => downloadAttachment(attachment)}
                                >
                                  <View style={styles.attachmentContent}>
                                    {getFileIcon(attachment.file_type)}
                                    <View style={styles.attachmentInfo}>
                                      <Text style={styles.attachmentName} numberOfLines={1}>
                                        {attachment.file_name}
                                      </Text>
                                      <Text style={styles.attachmentSize}>
                                        {formatFileSize(attachment.file_size)}
                                      </Text>
                                    </View>
                                  </View>
                                  <View style={styles.attachmentActions}>
                                    {isImageFile(attachment.file_type) && (
                                      <Pressable
                                        style={styles.attachmentAction}
                                        onPress={(e) => {
                                          e.stopPropagation();
                                          viewImage(attachment);
                                        }}
                                      >
                                        <Eye size={12} color="#6b7280" />
                                      </Pressable>
                                    )}
                                    <Pressable
                                      style={styles.attachmentAction}
                                      onPress={(e) => {
                                        e.stopPropagation();
                                        downloadAttachment(attachment);
                                      }}
                                    >
                                      <Download size={12} color="#6b7280" />
                                    </Pressable>
                                  </View>
                                </Pressable>
                              ))}
                            </View>
                          )}
                        </View>
                        <View style={styles.quoteActions}>
                          {quote.status !== 'accepted' && (
                            <Pressable
                              onPress={() => handleAcceptQuote(quote.id)}
                              disabled={updating || !quote.service_request_id}
                              style={styles.quoteActionButton}
                            >
                              <CheckCircle2 size={16} color="#16a34a" />
                            </Pressable>
                          )}
                          {quote.status !== 'rejected' && (
                            <Pressable
                              onPress={() => handleRejectQuote(quote.id)}
                              disabled={updating}
                              style={styles.quoteActionButton}
                            >
                              <X size={16} color="#dc2626" />
                            </Pressable>
                          )}
                          <Pressable
                            onPress={() => handleOpenQuoteModal(quote)}
                            style={styles.quoteActionButton}
                          >
                            <Edit3 size={16} color="#6b7280" />
                          </Pressable>
                          <Pressable
                            onPress={() => handleDeleteQuote(quote.id)}
                            disabled={deletingQuoteId === quote.id}
                            style={styles.quoteActionButton}
                          >
                            {deletingQuoteId === quote.id ? (
                              <ActivityIndicator size="small" color="#6b7280" />
                            ) : (
                              <Trash2 size={16} color="#9ca3af" />
                            )}
                          </Pressable>
                        </View>
                      </View>
                      <Text style={styles.quoteCreated}>
                        Created: {new Date(quote.created_at).toLocaleDateString()} at {new Date(quote.created_at).toLocaleTimeString()}
                      </Text>
                    </View>
                  );
                })}
              </View>
            )}
          </View>

          {/* Expenses Section */}
          <ExpenseListSection
            containerStyle={styles.card}
            titleRow={(
              <View style={styles.sectionTitleRow}>
                <DollarSign size={20} color="#6b7280" />
                <Text style={styles.sectionTitle}>Expenses</Text>
              </View>
            )}
          >

            {/* Action Buttons Row */}
            <View style={styles.expenseActionButtonsRow}>
              <Pressable
                style={styles.addButton}
                onPress={() => handleOpenExpenseModal()}
              >
                <Plus size={16} color="#ffffff" />
                <Text style={styles.addButtonText}>Add Expense</Text>
              </Pressable>
              {selectedExpenseIds.size > 0 && (
                <Pressable
                  style={[styles.addButton, { backgroundColor: '#5469d4', marginLeft: 8 }]}
                  onPress={handleOpenInvoiceModal}
                  disabled={creatingInvoice}
                >
                  {creatingInvoice ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <>
                      <DollarSign size={16} color="#ffffff" />
                      <Text style={styles.addButtonText}>
                        Create Invoice ({selectedExpenseIds.size})
                      </Text>
                    </>
                  )}
                </Pressable>
              )}
            </View>

            {/* Expense Summary */}
            {expenses.length > 0 && (
              <View style={styles.expenseSummaryContainer}>
                <View style={styles.expenseSummaryRow}>
                  <View style={styles.expenseSummaryItem}>
                    <Text style={styles.expenseSummaryLabel}>Total</Text>
                    <Text style={styles.expenseSummaryTotal}>{formatCurrency(calculateTotalExpenses())}</Text>
                  </View>
                  <View style={styles.expenseSummaryItem}>
                    <Text style={styles.expenseSummaryLabel}>Any Paid</Text>
                    <Text style={styles.expenseSummaryPaid}>{formatCurrency(calculatePaidExpenses())}</Text>
                  </View>
                  <View style={styles.expenseSummaryItem}>
                    <Text style={styles.expenseSummaryLabel}>Homeowner Outstanding</Text>
                    <Text style={styles.expenseSummaryUnpaid}>{formatCurrency(calculateHomeownerOutstandingExpenses())}</Text>
                  </View>
                  <View style={styles.expenseSummaryItem}>
                    <Text style={styles.expenseSummaryLabel}>Contractor Outstanding</Text>
                    <Text style={styles.expenseSummaryUnpaid}>{formatCurrency(calculateContractorOutstandingExpenses())}</Text>
                  </View>
                </View>
              </View>
            )}

            {loadingExpenses ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color="#9ca3af" />
              </View>
            ) : expenses.length === 0 ? (
              <Text style={styles.noDataText}>No expenses yet</Text>
            ) : (
              <View style={styles.expensesList}>
                {getParentExpenses().map((expense) => {
                  const childLineItems = getChildLineItemsForParent(expense.id);
                  return (
                  <View key={expense.id} style={styles.expenseCard}>
                    <View style={styles.expenseCardHeader}>
                      {expense.billable_to_homeowner && !expense.is_paid && !expense.invoice_id && (
                        <Pressable
                          style={styles.expenseCheckbox}
                          onPress={() => {
                            const newSelected = new Set(selectedExpenseIds);
                            if (newSelected.has(expense.id)) {
                              newSelected.delete(expense.id);
                            } else {
                              newSelected.add(expense.id);
                            }
                            setSelectedExpenseIds(newSelected);
                          }}
                        >
                          <View style={[
                            styles.checkbox,
                            selectedExpenseIds.has(expense.id) && styles.checkboxChecked
                          ]}>
                            {selectedExpenseIds.has(expense.id) && (
                              <CheckCircle2 size={16} color="#ffffff" />
                            )}
                          </View>
                        </Pressable>
                      )}
                      <View style={styles.expenseCardContent}>
                        <View style={styles.expenseHeaderRow}>
                          <Text style={styles.expenseAmount}>
                            {formatCurrency(expense.amount)}
                          </Text>
                          <View style={[
                            styles.badge,
                            { backgroundColor: expense.billable_to_homeowner ? (expense.is_paid ? '#dcfce7' : '#fee2e2') : '#f3f4f6' }
                          ]}>
                            <Text style={[
                              styles.badgeText,
                              { color: expense.billable_to_homeowner ? (expense.is_paid ? '#16a34a' : '#dc2626') : '#6b7280' }
                            ]}>
                              {expense.billable_to_homeowner
                                ? (expense.is_paid ? 'Homeowner Paid' : 'Homeowner Unpaid')
                                : 'Not Billable'}
                            </Text>
                          </View>
                          <View style={[
                            styles.badge,
                            { backgroundColor: expense.needs_contractor_payment ? (expense.contractor_paid ? '#dcfce7' : '#ffedd5') : '#f3f4f6' }
                          ]}>
                            <Text style={[
                              styles.badgeText,
                              { color: expense.needs_contractor_payment ? (expense.contractor_paid ? '#16a34a' : '#c2410c') : '#6b7280' }
                            ]}>
                              {expense.needs_contractor_payment
                                ? (expense.contractor_paid ? 'Contractor Paid' : 'Contractor Unpaid')
                                : 'No Contractor Payment'}
                            </Text>
                          </View>
                          {expense.invoice_id && (
                            <View style={[styles.badge, { backgroundColor: '#dbeafe' }]}>
                              <Text style={[styles.badgeText, { color: '#2563eb' }]}>
                                Invoiced
                              </Text>
                            </View>
                          )}
                          {expense.invoice_requested && !expense.invoice_id && (
                            <View style={[styles.badge, { backgroundColor: '#fef3c7' }]}>
                              <Text style={[styles.badgeText, { color: '#d97706' }]}>
                                Invoice Requested
                              </Text>
                            </View>
                          )}
                          {expense.quote && (
                            <View style={[styles.badge, { backgroundColor: '#dbeafe' }]}>
                              <Text style={[styles.badgeText, { color: '#2563eb' }]}>
                                Linked to Quote
                              </Text>
                            </View>
                          )}
                        </View>
                        {expense.description && (
                          <Text style={styles.expenseDescription}>{expense.description}</Text>
                        )}
                        <View style={styles.expenseMetaGrid}>
                          {expense.expense_date && (
                            <View style={styles.metaItem}>
                              <Calendar size={12} color="#6b7280" />
                              <Text style={styles.expenseMetaText}>
                                Date: {formatDateForDisplay(expense.expense_date)}
                              </Text>
                            </View>
                          )}
                          {expense.category && (
                            <Text style={styles.expenseMetaText}>
                              <Text style={styles.expenseMetaLabel}>Category:</Text> {expense.category}
                            </Text>
                          )}
                          {expense.vendor && (
                            <Text style={styles.expenseMetaText}>
                              <Text style={styles.expenseMetaLabel}>Vendor:</Text> {expense.vendor}
                            </Text>
                          )}
                          <Text style={styles.expenseMetaText}>
                            <Text style={styles.expenseMetaLabel}>Billable to Homeowner:</Text> {expense.billable_to_homeowner ? 'Yes' : 'No'}
                          </Text>
                          <Text style={styles.expenseMetaText}>
                            <Text style={styles.expenseMetaLabel}>Needs Contractor Payment:</Text> {expense.needs_contractor_payment ? 'Yes' : 'No'}
                          </Text>
                          {expense.quote && (
                            <Text style={styles.expenseMetaText}>
                              <Text style={styles.expenseMetaLabel}>Quote:</Text> {formatCurrency(expense.quote.amount || 0)}
                              {expense.quote.service_provider && ` - ${expense.quote.service_provider.company_name}`}
                            </Text>
                          )}
                          {expense.internal_cost !== null && expense.internal_cost !== undefined && (
                            <Text style={styles.expenseMetaText}>
                              <Text style={styles.expenseMetaLabel}>Internal Cost:</Text> {formatCurrency(expense.internal_cost)}
                            </Text>
                          )}
                          <Text style={styles.expenseMetaText}>
                            <Text style={styles.expenseMetaLabel}>Homeowner Visibility:</Text>{' '}
                            {expense.homeowner_visible ? 'Visible' : 'Hidden'}
                          </Text>
                        </View>
                        {childLineItems.length > 0 && (
                          <View style={styles.expenseLineItemsContainer}>
                            <Text style={styles.expenseLineItemsTitle}>Line Items</Text>
                            {childLineItems.map((lineItem) => (
                              <View key={lineItem.id} style={styles.expenseLineItemRow}>
                                <View style={{ flex: 1 }}>
                                  <Text style={styles.expenseLineItemAmount}>
                                    {formatCurrency(lineItem.amount)}
                                    {lineItem.homeowner_visible ? '' : ' (hidden from homeowner)'}
                                  </Text>
                                  <Text style={styles.expenseLineItemDescription}>
                                    {lineItem.description || `${lineItem.category || 'Service'}${lineItem.vendor ? ` - ${lineItem.vendor}` : ''}`}
                                  </Text>
                                  {lineItem.internal_cost !== null && lineItem.internal_cost !== undefined && (
                                    <Text style={styles.expenseLineItemMeta}>
                                      Internal cost: {formatCurrency(lineItem.internal_cost)}
                                      {' | '}
                                      Margin: {formatCurrency(lineItem.amount - lineItem.internal_cost)}
                                    </Text>
                                  )}
                                </View>
                                <View style={styles.expenseLineItemActions}>
                                  <Pressable
                                    onPress={() => handleToggleLineItemVisibility(lineItem)}
                                    disabled={updating}
                                    style={styles.expenseLineItemActionButton}
                                  >
                                    <Text style={styles.expenseLineItemActionText}>
                                      {lineItem.homeowner_visible ? 'Hide' : 'Show'}
                                    </Text>
                                  </Pressable>
                                  <Pressable
                                    onPress={() => handleOpenExpenseModal(lineItem)}
                                    style={styles.expenseLineItemActionButton}
                                  >
                                    <Text style={styles.expenseLineItemActionText}>Edit</Text>
                                  </Pressable>
                                  <Pressable
                                    onPress={() => handleDeleteExpense(lineItem.id)}
                                    disabled={deletingExpenseId === lineItem.id}
                                    style={styles.expenseLineItemActionButton}
                                  >
                                    <Text style={styles.expenseLineItemActionText}>
                                      {deletingExpenseId === lineItem.id ? 'Deleting...' : 'Delete'}
                                    </Text>
                                  </Pressable>
                                </View>
                              </View>
                            ))}
                          </View>
                        )}
                        <View style={styles.expensePaymentInfo}>
                          <Text style={styles.expenseMetaText}>
                            <Text style={styles.expenseMetaLabel}>Homeowner Payment:</Text>{' '}
                            {expense.billable_to_homeowner
                              ? (expense.is_paid
                                ? `${expense.payment_date ? formatDateForDisplay(expense.payment_date) : 'N/A'} via ${getPaymentMethodLabel(expense.payment_method)}`
                                : 'Pending')
                              : 'Not required'}
                          </Text>
                          <Text style={styles.expenseMetaText}>
                            <Text style={styles.expenseMetaLabel}>Contractor Payment:</Text>{' '}
                            {expense.needs_contractor_payment
                              ? (expense.contractor_paid
                                ? `${expense.contractor_payment_date ? formatDateForDisplay(expense.contractor_payment_date) : 'N/A'} via ${getPaymentMethodLabel(expense.contractor_payment_method)}`
                                : 'Pending')
                              : 'Not required'}
                          </Text>
                          {(expense.payment_notes || expense.contractor_payment_notes) && (
                            <Text style={styles.expenseMetaText}>
                              <Text style={styles.expenseMetaLabel}>Notes:</Text>{' '}
                              {[expense.payment_notes, expense.contractor_payment_notes].filter(Boolean).join(' | ')}
                            </Text>
                          )}
                          {expensePaymentHistoryByExpenseId[expense.id]?.[0] && (
                            <Text style={styles.expenseMetaText}>
                              <Text style={styles.expenseMetaLabel}>Last Payment Request:</Text>{' '}
                              {new Date(expensePaymentHistoryByExpenseId[expense.id][0].requested_at).toLocaleString()} by{' '}
                              {expensePaymentHistoryByExpenseId[expense.id][0].requester_name || 'Unknown'}
                            </Text>
                          )}
                          {expensePaymentHistoryByExpenseId[expense.id] && expensePaymentHistoryByExpenseId[expense.id].length > 0 && (
                            <View style={styles.expenseHistoryList}>
                              {expensePaymentHistoryByExpenseId[expense.id].slice(0, 3).map((historyItem) => (
                                <Text key={historyItem.id} style={styles.expenseHistoryItemText}>
                                  {new Date(historyItem.requested_at).toLocaleString()} - {historyItem.status} - {historyItem.requester_name || 'Unknown'}
                                </Text>
                              ))}
                            </View>
                          )}
                        </View>
                      </View>
                    </View>
                    <Text style={styles.expenseCreated}>
                      Created: {new Date(expense.created_at).toLocaleDateString()} at {new Date(expense.created_at).toLocaleTimeString()}
                    </Text>
                    {(() => {
                      const moneyFlow = getExpenseMoneyFlow(expense);
                      return (
                        <View style={styles.moneyFlowWidget}>
                          <Text style={styles.moneyFlowTitle}>Money Flow</Text>
                          <View style={styles.moneyFlowRow}>
                            {moneyFlow.nodes.map((node, index) => (
                              <View key={`${expense.id}-flow-${node}-${index}`} style={styles.moneyFlowSegment}>
                                {(() => {
                                  const nodeIsComplete =
                                    index === 0
                                      ? (moneyFlow.steps[0]?.isComplete ?? true)
                                      : Boolean(moneyFlow.steps[index - 1]?.isComplete);
                                  return (
                                    <View
                                      style={[
                                        styles.moneyFlowNode,
                                        nodeIsComplete
                                          ? styles.moneyFlowNodeComplete
                                          : styles.moneyFlowNodePending,
                                      ]}
                                    >
                                      <Text
                                        style={[
                                          styles.moneyFlowNodeText,
                                          nodeIsComplete
                                            ? styles.moneyFlowNodeTextComplete
                                            : styles.moneyFlowNodeTextPending,
                                        ]}
                                      >
                                        {node}
                                      </Text>
                                    </View>
                                  );
                                })()}
                                {index < moneyFlow.steps.length && (
                                  <View style={styles.moneyFlowArrowBlock}>
                                    <Text
                                      style={[
                                        styles.moneyFlowArrow,
                                        moneyFlow.steps[index].isComplete
                                          ? styles.moneyFlowArrowComplete
                                          : styles.moneyFlowArrowPending,
                                      ]}
                                    >
                                      →
                                    </Text>
                                    <Text
                                      style={[
                                        styles.moneyFlowStepLabel,
                                        moneyFlow.steps[index].isComplete
                                          ? styles.moneyFlowStepLabelComplete
                                          : styles.moneyFlowStepLabelPending,
                                      ]}
                                    >
                                      {moneyFlow.steps[index].label}
                                    </Text>
                                  </View>
                                )}
                              </View>
                            ))}
                          </View>
                        </View>
                      );
                    })()}
                    <View style={styles.expenseActionBar}>
                      <Pressable
                        onPress={() => handleOpenExpenseModal(undefined, expense)}
                        style={styles.expenseActionButton}
                      >
                        <Text style={styles.expenseActionButtonText}>Add Line Item</Text>
                      </Pressable>
                      {expense.billable_to_homeowner && !expense.is_paid && (
                        <Pressable
                          onPress={() => handleOpenRequestHomeownerPaymentConfirmModal(expense)}
                          style={[styles.expenseActionButton, styles.expenseActionButtonHomeowner]}
                          disabled={requestingPaymentExpenseId === expense.id}
                        >
                          {requestingPaymentExpenseId === expense.id ? (
                            <ActivityIndicator size="small" color="#166534" />
                          ) : (
                            <>
                              <Send size={14} color="#166534" />
                              <Text style={[styles.expenseActionButtonText, styles.expenseActionButtonTextHomeowner]}>
                                Request Homeowner Payment
                              </Text>
                            </>
                          )}
                        </Pressable>
                      )}
                      {expense.billable_to_homeowner && !expense.is_paid && (
                        <Pressable
                          onPress={() => handleOpenHomeownerPaymentModal(expense)}
                          style={[styles.expenseActionButton, styles.expenseActionButtonHomeowner]}
                        >
                          <Text style={[styles.expenseActionButtonText, styles.expenseActionButtonTextHomeowner]}>
                            Mark Homeowner Paid
                          </Text>
                        </Pressable>
                      )}
                      {expense.needs_contractor_payment && !expense.contractor_paid && (
                        <Pressable
                          onPress={() => handleOpenContractorPaymentModal(expense)}
                          style={[styles.expenseActionButton, styles.expenseActionButtonContractor]}
                        >
                          <Text style={[styles.expenseActionButtonText, styles.expenseActionButtonTextContractor]}>
                            Mark Contractor Paid
                          </Text>
                        </Pressable>
                      )}
                      <Pressable
                        onPress={() => {
                          setExpenseToPreview(expense);
                          setExpensePreviewTab('email');
                          setShowExpensePreviewModal(true);
                        }}
                        style={[styles.expenseActionButton, styles.expenseActionButtonPreview]}
                      >
                        <Eye size={14} color="#6b7280" />
                        <Text style={[styles.expenseActionButtonText, styles.expenseActionButtonTextPreview]}>Homeowner View</Text>
                      </Pressable>
                      <Pressable
                        onPress={() => handleOpenExpenseModal(expense)}
                        style={styles.expenseActionButton}
                      >
                        <Text style={styles.expenseActionButtonText}>Edit Expense</Text>
                      </Pressable>
                      <Pressable
                        onPress={() => handleDeleteExpense(expense.id)}
                        disabled={deletingExpenseId === expense.id}
                        style={[styles.expenseActionButton, styles.expenseActionButtonDelete]}
                      >
                        {deletingExpenseId === expense.id ? (
                          <ActivityIndicator size="small" color="#6b7280" />
                        ) : (
                          <Text style={[styles.expenseActionButtonText, styles.expenseActionButtonTextDelete]}>
                            Delete Expense
                          </Text>
                        )}
                      </Pressable>
                    </View>
                  </View>
                )})}
              </View>
            )}
          </ExpenseListSection>

          {/* Attachments Section */}
          <View style={styles.card}>
            <View style={styles.sectionTitleRow}>
              <Paperclip size={20} color="#6b7280" />
              <Text style={styles.sectionTitle}>Attachments</Text>
            </View>
            
            <ServiceRequestFileUpload
              serviceRequestId={id || null}
            />
          </View>

          {/* Quick Actions */}
          <View style={styles.card}>
            <Text style={[styles.sectionTitle, { marginBottom: 16 }]}>Quick Actions</Text>
            <View style={styles.quickActionsContainer}>
              {request.status !== 'completed' && request.status !== 'cancelled' && (
                <Pressable
                  onPress={openScheduleVisitFlow}
                  disabled={loadingUpcomingVisits}
                  style={[styles.quickActionButtonBlue, loadingUpcomingVisits && { opacity: 0.6 }]}
                >
                  {loadingUpcomingVisits ? (
                    <ActivityIndicator size="small" color="#2563eb" />
                  ) : (
                    <Wrench size={16} color="#2563eb" />
                  )}
                  <Text style={styles.quickActionButtonBlueText}>Schedule Visit</Text>
                </Pressable>
              )}
              
              <Pressable
                onPress={() => setShowDeleteConfirmModal(true)}
                style={styles.quickActionButtonRed}
              >
                <Trash2 size={16} color="#dc2626" />
                <Text style={styles.quickActionButtonRedText}>Delete Request</Text>
              </Pressable>
            </View>
          </View>
        </View>

        {/* AI Agent Sidebar */}
        <View style={[styles.sidebar, isMobile && styles.sidebarMobile]}>
          <AIAgentTimeline serviceRequestId={request.id} />
        </View>
      </View>

      {/* Status Update Modal */}
      <Modal
        visible={showStatusModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowStatusModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowStatusModal(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Update Request Status</Text>
                  <Pressable
                    onPress={() => setShowStatusModal(false)}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                <View style={styles.modalBody}>
                  <View style={styles.modalInfo}>
                    <Text style={styles.modalInfoTitle}>{request.title}</Text>
                    <Text style={styles.modalInfoText}>
                      Current Status: {request.status.split('_').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </Text>
                  </View>

                  <View style={styles.modalGridButtons}>
                    {(['pending', 'in_progress', 'completed', 'cancelled'] as const).map((status) => {
                      const statusColor = getStatusColor(status);
                      const isDisabled = status === request.status || updating;
                      return (
                        <Pressable
                          key={status}
                          onPress={() => handleStatusUpdate(status)}
                          disabled={isDisabled}
                          style={[
                            styles.modalGridButton,
                            { backgroundColor: isDisabled ? '#f3f4f6' : statusColor.backgroundColor },
                            isDisabled && styles.modalGridButtonDisabled
                          ]}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <CheckCircle2 size={16} color={isDisabled ? '#9ca3af' : statusColor.color} />
                            <Text style={[styles.modalGridButtonText, { color: isDisabled ? '#9ca3af' : statusColor.color }]}>
                              {status.split('_').map(word => 
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ')}
                            </Text>
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Priority Update Modal */}
      <Modal
        visible={showPriorityModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowPriorityModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowPriorityModal(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Update Request Priority</Text>
                  <Pressable
                    onPress={() => setShowPriorityModal(false)}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                <View style={styles.modalBody}>
                  <View style={styles.modalInfo}>
                    <Text style={styles.modalInfoTitle}>{request.title}</Text>
                    <Text style={styles.modalInfoText}>
                      Current Priority: {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                    </Text>
                  </View>

                  <View style={styles.modalGridButtons}>
                    {(['low', 'normal', 'high', 'urgent'] as const).map((priority) => {
                      const priorityColor = getPriorityColor(priority);
                      const isDisabled = priority === request.priority || updating;
                      return (
                        <Pressable
                          key={priority}
                          onPress={() => handlePriorityUpdate(priority)}
                          disabled={isDisabled}
                          style={[
                            styles.modalGridButton,
                            { backgroundColor: isDisabled ? '#f3f4f6' : priorityColor.backgroundColor },
                            isDisabled && styles.modalGridButtonDisabled
                          ]}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <CheckCircle2 size={16} color={isDisabled ? '#9ca3af' : priorityColor.color} />
                            <Text style={[styles.modalGridButtonText, { color: isDisabled ? '#9ca3af' : priorityColor.color }]}>
                              {priority.charAt(0).toUpperCase() + priority.slice(1)}
                            </Text>
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Assign Owner Modal */}
      <Modal
        visible={showOwnerModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowOwnerModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowOwnerModal(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Assign Owner</Text>
                  <Pressable
                    onPress={() => setShowOwnerModal(false)}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                <View style={styles.modalBody}>
                  <View style={styles.modalInfo}>
                    <Text style={styles.modalInfoTitle}>{request.title}</Text>
                    <Text style={styles.modalInfoText}>
                      Assign a system manager to take ownership of this request
                    </Text>
                  </View>

                  <View style={{ gap: 12 }}>
                    {systemManagers.map((manager) => (
                      <Pressable
                        key={manager.id}
                        onPress={() => {
                          handleOwnerAssignment(manager.id);
                          setShowOwnerModal(false);
                        }}
                        disabled={updating}
                        style={[styles.modalListButton, updating && styles.modalListButtonDisabled]}
                      >
                        <UserCheck size={20} color="#9ca3af" />
                        <Text style={styles.modalListButtonText}>{manager.full_name}</Text>
                      </Pressable>
                    ))}
                    
                    {/* Unassign option */}
                    <Pressable
                      onPress={() => {
                        handleOwnerAssignment(null);
                        setShowOwnerModal(false);
                      }}
                      disabled={updating}
                      style={[styles.modalListButton, updating && styles.modalListButtonDisabled]}
                    >
                      <User size={20} color="#9ca3af" />
                      <Text style={[styles.modalListButtonText, { color: '#6b7280' }]}>Unassigned</Text>
                    </Pressable>
                  </View>

                  {updating && (
                    <View style={styles.modalLoadingContainer}>
                      <ActivityIndicator size="small" color="#9ca3af" />
                      <Text style={styles.modalLoadingText}>Updating...</Text>
                    </View>
                  )}
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Choose Existing or New Visit Modal */}
      <Modal
        visible={showChooseVisitModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          if (addingToVisitId) return;
          setShowChooseVisitModal(false);
          setUpcomingVisits([]);
        }}
      >
        <TouchableWithoutFeedback
          onPress={() => {
            if (addingToVisitId) return;
            setShowChooseVisitModal(false);
            setUpcomingVisits([]);
          }}
        >
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={[styles.modalScrollContent, { maxHeight: '90%' }]}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Add to Existing Visit?</Text>
                  <Pressable
                    onPress={() => {
                      if (addingToVisitId) return;
                      setShowChooseVisitModal(false);
                      setUpcomingVisits([]);
                    }}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                <ScrollView style={{ maxHeight: 480 }} showsVerticalScrollIndicator={true}>
                  <View style={styles.modalBody}>
                    <View style={[styles.modalInfo, { backgroundColor: '#eff6ff', marginBottom: 4 }]}>
                      <Text style={[styles.modalInfoText, { color: '#1e40af' }]}>
                        This home already has {upcomingVisits.length} upcoming visit{upcomingVisits.length === 1 ? '' : 's'}. You can add this service request to one of them, or create a brand new visit.
                      </Text>
                    </View>

                    <View style={[styles.modalInfo, { backgroundColor: '#f9fafb' }]}>
                      <Text style={styles.modalInfoTitle}>{request.title}</Text>
                      <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                        {request.home?.address}, {request.home?.city}
                      </Text>
                    </View>

                    <View style={{ gap: 8 }}>
                      {upcomingVisits.map((visit) => {
                        const isAddingThis = addingToVisitId === visit.id;
                        const isAnyAdding = !!addingToVisitId;
                        const visitTypeLabel =
                          visit.visit_type === 'maintenance'
                            ? 'Maintenance Visit'
                            : visit.visit_type === 'walkthrough'
                            ? 'Walkthrough'
                            : 'Service Visit';
                        const dateLabel = visit.scheduled_date
                          ? new Date(visit.scheduled_date).toLocaleString(undefined, {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : 'Not yet scheduled';
                        const statusLabel = visit.status
                          .split('_')
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(' ');

                        return (
                          <Pressable
                            key={visit.id}
                            onPress={() => handleAddToExistingVisit(visit)}
                            disabled={isAnyAdding}
                            style={[
                              styles.modalListButton,
                              {
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: 6,
                                padding: 14,
                              },
                              isAnyAdding && !isAddingThis && styles.modalListButtonDisabled,
                            ]}
                          >
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Calendar size={16} color="#2563eb" />
                              <Text style={[styles.modalListButtonText, { color: '#111827' }]}>
                                {dateLabel}
                              </Text>
                              <View
                                style={{
                                  paddingHorizontal: 8,
                                  paddingVertical: 2,
                                  borderRadius: 999,
                                  backgroundColor: '#eef2ff',
                                }}
                              >
                                <Text style={{ fontSize: 11, color: '#4338ca', fontWeight: '500' }}>
                                  {visitTypeLabel}
                                </Text>
                              </View>
                              <View
                                style={{
                                  paddingHorizontal: 8,
                                  paddingVertical: 2,
                                  borderRadius: 999,
                                  backgroundColor: '#f3f4f6',
                                }}
                              >
                                <Text style={{ fontSize: 11, color: '#374151', fontWeight: '500' }}>
                                  {statusLabel}
                                </Text>
                              </View>
                            </View>

                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                              {visit.assigned_to ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                  <User size={12} color="#6b7280" />
                                  <Text style={styles.modalInfoText}>{visit.assigned_to}</Text>
                                </View>
                              ) : null}
                              {visit.service_provider?.company_name ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                  <Wrench size={12} color="#6b7280" />
                                  <Text style={styles.modalInfoText}>{visit.service_provider.company_name}</Text>
                                </View>
                              ) : null}
                              {visit.estimated_duration_minutes ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} color="#6b7280" />
                                  <Text style={styles.modalInfoText}>
                                    {formatVisitDurationLabel(visit.estimated_duration_minutes)}
                                  </Text>
                                </View>
                              ) : null}
                              {typeof visit.visit_tasks_count === 'number' ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                  <CheckCircle2 size={12} color="#6b7280" />
                                  <Text style={styles.modalInfoText}>
                                    {visit.visit_tasks_count} task{visit.visit_tasks_count === 1 ? '' : 's'}
                                  </Text>
                                </View>
                              ) : null}
                            </View>

                            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginTop: 4 }}>
                              {isAddingThis ? (
                                <ActivityIndicator size="small" color="#2563eb" />
                              ) : (
                                <Plus size={14} color="#2563eb" />
                              )}
                              <Text style={{ color: '#2563eb', fontSize: 13, fontWeight: '600' }}>
                                {isAddingThis ? 'Adding...' : 'Add to this visit'}
                              </Text>
                            </View>
                          </Pressable>
                        );
                      })}
                    </View>

                    {error && (
                      <View style={styles.modalErrorBox}>
                        <Text style={styles.modalErrorText}>{error}</Text>
                      </View>
                    )}
                  </View>
                </ScrollView>

                <View style={styles.modalActions}>
                  <Pressable
                    onPress={() => {
                      if (addingToVisitId) return;
                      setShowChooseVisitModal(false);
                      setUpcomingVisits([]);
                    }}
                    disabled={!!addingToVisitId}
                    style={[styles.modalButton, styles.modalButtonCancel, !!addingToVisitId && { opacity: 0.5 }]}
                  >
                    <Text style={styles.modalButtonCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={() => {
                      if (addingToVisitId) return;
                      setShowChooseVisitModal(false);
                      setUpcomingVisits([]);
                      setShowScheduleVisitModal(true);
                    }}
                    disabled={!!addingToVisitId}
                    style={[styles.modalButton, styles.modalButtonPrimary, !!addingToVisitId && styles.modalButtonPrimaryDisabled]}
                  >
                    <Text style={styles.modalButtonPrimaryText}>Create New Visit</Text>
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Schedule Visit Modal */}
      <Modal
        visible={showScheduleVisitModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowScheduleVisitModal(false);
          setVisitFormData({
            scheduled_date: '',
            estimated_duration_minutes: visitDurationDefaults.service,
            assigned_to: '',
            service_provider_id: '',
            visit_notes: '',
            visit_type: 'service'
          });
        }}
      >
        <TouchableWithoutFeedback onPress={() => {
          setShowScheduleVisitModal(false);
          setVisitFormData({
            scheduled_date: '',
            estimated_duration_minutes: visitDurationDefaults.service,
            assigned_to: '',
            service_provider_id: '',
            visit_notes: '',
            visit_type: 'service'
          });
        }}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={[styles.modalScrollContent, { maxHeight: '90%' }]}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Schedule Service Visit</Text>
                  <Pressable
                    onPress={() => {
                      setShowScheduleVisitModal(false);
                      setVisitFormData({
                        scheduled_date: '',
                        estimated_duration_minutes: visitDurationDefaults.service,
                        assigned_to: '',
                        service_provider_id: '',
                        visit_notes: '',
                        visit_type: 'service'
                      });
                    }}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator={true}>
                  <View style={styles.modalBody}>
                    <View style={[styles.modalInfo, { backgroundColor: '#f9fafb', marginBottom: 16 }]}>
                      <Text style={styles.modalInfoTitle}>{request.title}</Text>
                      <Text style={[styles.modalInfoText, { color: '#2563eb', fontWeight: '500', marginTop: 4 }]}>
                        {request.home?.owner?.full_name || 'Unknown Owner'}
                      </Text>
                      <Text style={[styles.modalInfoText, { marginTop: 4 }]}>{request.description}</Text>
                      <Text style={[styles.modalInfoText, { fontSize: 11, marginTop: 8 }]}>
                        {request.home?.address}, {request.home?.city}
                      </Text>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Visit Type</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={visitFormData.visit_type}
                          onValueChange={(value) => {
                            const visitType = value as 'maintenance' | 'service' | 'walkthrough';
                            setVisitFormData({
                              ...visitFormData,
                              visit_type: visitType,
                              estimated_duration_minutes: visitDurationDefaults[visitType],
                            });
                          }}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="Service Visit" value="service" />
                          <Picker.Item label="Maintenance Visit" value="maintenance" />
                          <Picker.Item label="Walkthrough" value="walkthrough" />
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Scheduled Date & Time (Optional)</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="datetime-local"
                          value={visitFormData.scheduled_date}
                          onChange={(e) => setVisitFormData({ ...visitFormData, scheduled_date: (e as any).target.value })}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            fontSize: 14,
                            fontFamily: 'inherit',
                            outline: 'none',
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.modalFormInput}
                          value={visitFormData.scheduled_date}
                          onChangeText={(text) => setVisitFormData({ ...visitFormData, scheduled_date: text })}
                          placeholder="YYYY-MM-DDTHH:MM"
                        />
                      )}
                      <Text style={[styles.modalInfoText, { fontSize: 11, marginTop: 4 }]}>
                        Leave empty for visit requests pending homeowner confirmation
                      </Text>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Duration</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={visitFormData.estimated_duration_minutes}
                          onValueChange={(value) =>
                            setVisitFormData({
                              ...visitFormData,
                              estimated_duration_minutes: Number(value),
                            })
                          }
                          style={{ height: 40 }}
                        >
                          {VISIT_DURATION_OPTIONS_MINUTES.map((minutes) => (
                            <Picker.Item
                              key={minutes}
                              label={formatVisitDurationLabel(minutes)}
                              value={minutes}
                            />
                          ))}
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Assigned To</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={visitFormData.assigned_to}
                          onValueChange={(value) => setVisitFormData({ ...visitFormData, assigned_to: value })}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="Unassigned" value="" />
                          {systemManagers.map((manager) => (
                            <Picker.Item key={manager.id} label={manager.full_name} value={manager.full_name} />
                          ))}
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Service Provider (Optional)</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={visitFormData.service_provider_id}
                          onValueChange={(value) => setVisitFormData({ ...visitFormData, service_provider_id: value })}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="None - Internal/Unassigned" value="" />
                          {serviceProviders.map((provider) => (
                            <Picker.Item key={provider.id} label={provider.company_name} value={provider.id} />
                          ))}
                        </Picker>
                      </View>
                      {visitFormData.service_provider_id && (
                        <Text style={[styles.modalInfoText, { fontSize: 11, marginTop: 4 }]}>
                          {serviceProviders.find(p => p.id === visitFormData.service_provider_id)?.phone || 'No phone'} | {' '}
                          {serviceProviders.find(p => p.id === visitFormData.service_provider_id)?.email || 'No email'}
                        </Text>
                      )}
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Visit Notes (Optional)</Text>
                      <TextInput
                        style={styles.modalFormTextArea}
                        value={visitFormData.visit_notes}
                        onChangeText={(text) => setVisitFormData({ ...visitFormData, visit_notes: text })}
                        placeholder="Special instructions or notes..."
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    {error && (
                      <View style={styles.modalErrorBox}>
                        <Text style={styles.modalErrorText}>{error}</Text>
                      </View>
                    )}
                  </View>
                </ScrollView>

                <View style={styles.modalActions}>
                  <Pressable
                    onPress={() => {
                      setShowScheduleVisitModal(false);
                      setVisitFormData({
                        scheduled_date: '',
                        estimated_duration_minutes: visitDurationDefaults.service,
                        assigned_to: '',
                        service_provider_id: '',
                        visit_notes: '',
                        visit_type: 'service'
                      });
                    }}
                    style={[styles.modalButton, styles.modalButtonCancel]}
                  >
                    <Text style={styles.modalButtonCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleScheduleVisit}
                    disabled={updating}
                    style={[styles.modalButton, styles.modalButtonPrimary, updating && styles.modalButtonPrimaryDisabled]}
                  >
                    {updating ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <Text style={styles.modalButtonPrimaryText}>Schedule Visit</Text>
                    )}
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDeleteConfirmModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowDeleteConfirmModal(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitleRed}>Delete Service Request</Text>
                  <Pressable
                    onPress={() => setShowDeleteConfirmModal(false)}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                <View style={styles.modalBody}>
                  <View style={{ flexDirection: 'row', gap: 12 }}>
                    <View style={{ paddingTop: 2 }}>
                      <Trash2 size={24} color="#dc2626" />
                    </View>
                    <View style={{ flex: 1, gap: 8 }}>
                      <Text style={[styles.modalInfoText, { marginBottom: 8 }]}>
                        Are you sure you want to delete this service request? This action cannot be undone.
                      </Text>
                      <View style={[styles.modalInfo, { backgroundColor: '#f9fafb' }]}>
                        <Text style={styles.modalInfoTitle}>{request.title}</Text>
                        <Text style={[styles.modalInfoText, { fontSize: 11, marginTop: 4 }]}>
                          {request.home?.owner?.full_name || 'Unknown Owner'}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>

                <View style={styles.modalActions}>
                  <Pressable
                    onPress={() => setShowDeleteConfirmModal(false)}
                    style={[styles.modalButton, styles.modalButtonCancel]}
                  >
                    <Text style={styles.modalButtonCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleDeleteRequest}
                    disabled={deletingRequest}
                    style={[styles.modalButton, styles.modalButtonDelete, deletingRequest && styles.modalButtonPrimaryDisabled]}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      {deletingRequest ? (
                        <>
                          <ActivityIndicator size="small" color="#ffffff" />
                          <Text style={styles.modalButtonDeleteText}>Deleting...</Text>
                        </>
                      ) : (
                        <>
                          <Trash2 size={16} color="#ffffff" />
                          <Text style={styles.modalButtonDeleteText}>Delete Request</Text>
                        </>
                      )}
                    </View>
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Add/Edit Quote Modal */}
      <Modal
        visible={showQuoteModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowQuoteModal(false);
          setEditingQuote(null);
          setServiceProviderSearch('');
          setShowProviderDropdown(false);
          setQuoteFormData({
            service_provider_id: '',
            service_type: '',
            work_description: '',
            amount: '',
            quote_date: formatDateOnly(new Date()),
            expires_at: formatDateOnly(new Date()),
            notes: '',
            status: 'pending'
          });
        }}
      >
        <TouchableWithoutFeedback onPress={() => {
          setShowProviderDropdown(false);
        }}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
              <View style={[styles.modalScrollContent, { maxHeight: '90%' }]}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>
                    {editingQuote ? 'Edit Quote' : 'Add Quote'}
                  </Text>
                  <Pressable
                    onPress={() => {
                      setShowQuoteModal(false);
                      setEditingQuote(null);
                      setServiceProviderSearch('');
                      setShowProviderDropdown(false);
                      setQuoteFormData({
                        service_provider_id: '',
                        service_type: '',
                        work_description: '',
                        amount: '',
                        quote_date: formatDateOnly(new Date()),
                        expires_at: formatDateOnly(new Date()),
                        notes: '',
                        status: 'pending'
                      });
                    }}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                <ScrollView style={{ maxHeight: 450 }} showsVerticalScrollIndicator={true}>
                  <View style={styles.modalBody}>
                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Service Type (Optional)</Text>
                      <TextInput
                        style={styles.modalFormInput}
                        value={quoteFormData.service_type}
                        onChangeText={(text) => setQuoteFormData({ ...quoteFormData, service_type: text })}
                        placeholder="e.g., Plumbing, Electrical, HVAC"
                      />
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Work Description (Optional)</Text>
                      <TextInput
                        style={styles.modalFormTextArea}
                        value={quoteFormData.work_description}
                        onChangeText={(text) => setQuoteFormData({ ...quoteFormData, work_description: text })}
                        placeholder="Describe the work being quoted..."
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Amount (Optional)</Text>
                      <TextInput
                        style={styles.modalFormInput}
                        value={quoteFormData.amount}
                        onChangeText={(text) => setQuoteFormData({ ...quoteFormData, amount: text })}
                        placeholder="0.00"
                        keyboardType="decimal-pad"
                      />
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Quote Date (Optional)</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={quoteFormData.quote_date}
                          onChange={(e) => setQuoteFormData({ ...quoteFormData, quote_date: e.target.value })}
                          style={{
                            padding: '8px 12px',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: '#d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#111827',
                            backgroundColor: '#ffffff',
                            width: '100%',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box'
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.modalFormInput}
                          value={quoteFormData.quote_date}
                          onChangeText={(text) => setQuoteFormData({ ...quoteFormData, quote_date: text })}
                          placeholder="YYYY-MM-DD"
                        />
                      )}
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Expiration Date (Optional)</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={quoteFormData.expires_at}
                          onChange={(e) => setQuoteFormData({ ...quoteFormData, expires_at: e.target.value })}
                          style={{
                            padding: '8px 12px',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: '#d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#111827',
                            backgroundColor: '#ffffff',
                            width: '100%',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box'
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.modalFormInput}
                          value={quoteFormData.expires_at}
                          onChangeText={(text) => setQuoteFormData({ ...quoteFormData, expires_at: text })}
                          placeholder="YYYY-MM-DD"
                        />
                      )}
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Status</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={quoteFormData.status}
                          onValueChange={(value) => setQuoteFormData({ ...quoteFormData, status: value as 'pending' | 'accepted' | 'rejected' })}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="Pending" value="pending" />
                          <Picker.Item label="Accepted" value="accepted" />
                          <Picker.Item label="Rejected" value="rejected" />
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Notes (Optional)</Text>
                      <TextInput
                        style={[styles.modalFormTextArea, { minHeight: 100 }]}
                        value={quoteFormData.notes}
                        onChangeText={(text) => setQuoteFormData({ ...quoteFormData, notes: text })}
                        placeholder="Quote details, terms, conditions..."
                        multiline
                        numberOfLines={4}
                      />
                    </View>

                    <View
                      style={[
                        styles.modalFormField,
                        showProviderDropdown && styles.modalFormFieldDropdownOpen,
                      ]}
                    >
                      <Text style={styles.modalFormLabel}>Service Provider (Optional)</Text>
                      <View style={styles.typeaheadContainer}>
                        <TextInput
                          style={styles.modalFormInput}
                          value={serviceProviderSearch}
                          onChangeText={(text) => {
                            setServiceProviderSearch(text);
                            setShowProviderDropdown(true);
                            // Clear selection if search doesn't match selected provider
                            const selectedProvider = serviceProviders.find(p => p.id === quoteFormData.service_provider_id);
                            if (selectedProvider && !selectedProvider.company_name.toLowerCase().includes(text.toLowerCase())) {
                              setQuoteFormData({ ...quoteFormData, service_provider_id: '' });
                            }
                          }}
                          onFocus={() => setShowProviderDropdown(true)}
                          onBlur={() => {
                            // Delay closing to allow item clicks
                            setTimeout(() => setShowProviderDropdown(false), 200);
                          }}
                          placeholder="Search by company name, email, or phone..."
                          placeholderTextColor="#9ca3af"
                        />
                        {showProviderDropdown && (
                          <View style={styles.typeaheadDropdown}>
                            <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                              <Pressable
                                style={styles.typeaheadItem}
                                onPress={() => {
                                  setQuoteFormData({ ...quoteFormData, service_provider_id: '' });
                                  setServiceProviderSearch('');
                                  setShowProviderDropdown(false);
                                }}
                              >
                                <Text style={styles.typeaheadItemText}>None - Manual Entry</Text>
                              </Pressable>
                              {serviceProviderSearch.length > 0 ? (
                                filteredServiceProviders.length > 0 ? (
                                  filteredServiceProviders.map((provider) => (
                                    <Pressable
                                      key={provider.id}
                                      style={[
                                        styles.typeaheadItem,
                                        quoteFormData.service_provider_id === provider.id && styles.typeaheadItemSelected
                                      ]}
                                      onPress={() => handleSelectServiceProvider(provider.id)}
                                    >
                                      <Text style={styles.typeaheadItemText}>{provider.company_name}</Text>
                                      {(provider.email || provider.phone) && (
                                        <Text style={styles.typeaheadItemSubtext}>
                                          {provider.email || ''} {provider.phone ? `| ${provider.phone}` : ''}
                                        </Text>
                                      )}
                                    </Pressable>
                                  ))
                                ) : (
                                  <View style={styles.typeaheadItem}>
                                    <Text style={styles.typeaheadItemText}>No providers found</Text>
                                  </View>
                                )
                              ) : (
                                serviceProviders.slice(0, 10).map((provider) => (
                                  <Pressable
                                    key={provider.id}
                                    style={[
                                      styles.typeaheadItem,
                                      quoteFormData.service_provider_id === provider.id && styles.typeaheadItemSelected
                                    ]}
                                    onPress={() => handleSelectServiceProvider(provider.id)}
                                  >
                                    <Text style={styles.typeaheadItemText}>{provider.company_name}</Text>
                                    {(provider.email || provider.phone) && (
                                      <Text style={styles.typeaheadItemSubtext}>
                                        {provider.email || ''} {provider.phone ? `| ${provider.phone}` : ''}
                                      </Text>
                                    )}
                                  </Pressable>
                                ))
                              )}
                            </ScrollView>
                          </View>
                        )}
                      </View>
                    </View>

                    {/* File Attachments - Only show when editing existing quote */}
                    {editingQuote && editingQuote.id && (
                      <View style={styles.modalFormField}>
                        <Text style={styles.modalFormLabel}>Attachments</Text>
                        <QuoteFileUpload
                          quoteId={editingQuote.id}
                          disabled={savingQuote}
                        />
                      </View>
                    )}

                    {error && (
                      <View style={styles.modalErrorBox}>
                        <Text style={styles.modalErrorText}>{error}</Text>
                      </View>
                    )}
                  </View>
                </ScrollView>

                <View style={styles.modalActions}>
                  <Pressable
                    onPress={() => {
                      setShowQuoteModal(false);
                      setEditingQuote(null);
                      setServiceProviderSearch('');
                      setShowProviderDropdown(false);
                      setQuoteFormData({
                        service_provider_id: '',
                        service_type: '',
                        work_description: '',
                        amount: '',
                        quote_date: formatDateOnly(new Date()),
                        expires_at: formatDateOnly(new Date()),
                        notes: '',
                        status: 'pending'
                      });
                    }}
                    style={[styles.modalButton, styles.modalButtonCancel]}
                  >
                    <Text style={styles.modalButtonCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={(e: any) => handleSaveQuote(e)}
                    disabled={savingQuote}
                    style={[styles.modalButton, styles.modalButtonPrimary, savingQuote && styles.modalButtonPrimaryDisabled]}
                  >
                    {savingQuote ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <ActivityIndicator size="small" color="#ffffff" />
                        <Text style={styles.modalButtonPrimaryText}>Saving...</Text>
                      </View>
                    ) : (
                      <Text style={styles.modalButtonPrimaryText}>
                        {editingQuote ? 'Update Quote' : 'Add Quote'}
                      </Text>
                    )}
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Associate Quote Modal */}
      <Modal
        visible={showAssociateModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowAssociateModal(false);
          setSelectedStandaloneQuoteId('');
        }}
      >
        <TouchableWithoutFeedback onPress={() => {
          setShowAssociateModal(false);
          setSelectedStandaloneQuoteId('');
        }}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={[styles.modalScrollContent, { maxHeight: '90%' }]}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Associate Standalone Quote</Text>
                  <Pressable
                    onPress={() => {
                      setShowAssociateModal(false);
                      setSelectedStandaloneQuoteId('');
                    }}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                {standaloneQuotes.length === 0 ? (
                  <View style={{ alignItems: 'center', paddingVertical: 32 }}>
                    <Text style={[styles.modalInfoText, { marginBottom: 16 }]}>
                      No standalone quotes available to associate.
                    </Text>
                    <Pressable
                      onPress={() => {
                        setShowAssociateModal(false);
                        setSelectedStandaloneQuoteId('');
                      }}
                      style={[styles.modalButton, styles.modalButtonCancel]}
                    >
                      <Text style={styles.modalButtonCancelText}>Close</Text>
                    </Pressable>
                  </View>
                ) : (
                  <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator={true}>
                    <View style={styles.modalBody}>
                      <View style={styles.modalFormField}>
                        <Text style={styles.modalFormLabel}>Select Standalone Quote</Text>
                        <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                          <Picker
                            selectedValue={selectedStandaloneQuoteId}
                            onValueChange={(value) => setSelectedStandaloneQuoteId(value)}
                            style={{ height: 40 }}
                          >
                            <Picker.Item label="Select a quote..." value="" />
                            {standaloneQuotes.map((quote) => (
                              <Picker.Item 
                                key={quote.id} 
                                label={`${quote.service_provider?.company_name || 'Unknown Provider'} - ${quote.service_type || 'No Type'} - ${quote.amount ? formatCurrency(quote.amount) : 'No Amount'}`}
                                value={quote.id} 
                              />
                            ))}
                          </Picker>
                        </View>
                      </View>

                      {selectedStandaloneQuoteId && (() => {
                        const selectedQuote = standaloneQuotes.find(q => q.id === selectedStandaloneQuoteId);
                        if (!selectedQuote) return null;
                        return (
                          <View style={[styles.modalInfo, { backgroundColor: '#f9fafb', marginTop: 16 }]}>
                            <Text style={styles.modalInfoTitle}>
                              {selectedQuote.service_provider?.company_name || 'Unknown Provider'}
                            </Text>
                            {selectedQuote.service_type && (
                              <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                                Service Type: {selectedQuote.service_type}
                              </Text>
                            )}
                            {selectedQuote.work_description && (
                              <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                                {selectedQuote.work_description}
                              </Text>
                            )}
                            {selectedQuote.amount !== null && (
                              <Text style={[styles.modalInfoTitle, { marginTop: 8 }]}>
                                {formatCurrency(selectedQuote.amount)}
                              </Text>
                            )}
                          </View>
                        );
                      })()}
                    </View>
                  </ScrollView>
                )}

                {standaloneQuotes.length > 0 && (
                  <View style={styles.modalActions}>
                    <Pressable
                      onPress={() => {
                        setShowAssociateModal(false);
                        setSelectedStandaloneQuoteId('');
                      }}
                      style={[styles.modalButton, styles.modalButtonCancel]}
                    >
                      <Text style={styles.modalButtonCancelText}>Cancel</Text>
                    </Pressable>
                    <Pressable
                      onPress={handleAssociateQuote}
                      disabled={!selectedStandaloneQuoteId || associatingQuote}
                      style={[
                        styles.modalButton, 
                        styles.modalButtonPrimary, 
                        (!selectedStandaloneQuoteId || associatingQuote) && styles.modalButtonPrimaryDisabled
                      ]}
                    >
                      {associatingQuote ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <ActivityIndicator size="small" color="#ffffff" />
                          <Text style={styles.modalButtonPrimaryText}>Associating...</Text>
                        </View>
                      ) : (
                        <Text style={styles.modalButtonPrimaryText}>Associate Quote</Text>
                      )}
                    </Pressable>
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Associate Task Modal */}
      <Modal
        visible={showAssociateTaskModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowAssociateTaskModal(false);
          setSelectedStandaloneTaskId('');
        }}
      >
        <TouchableWithoutFeedback onPress={() => {
          setShowAssociateTaskModal(false);
          setSelectedStandaloneTaskId('');
        }}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={[styles.modalScrollContent, { maxHeight: '90%' }]}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Associate Existing Admin Task</Text>
                  <Pressable
                    onPress={() => {
                      setShowAssociateTaskModal(false);
                      setSelectedStandaloneTaskId('');
                    }}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                {loadingStandaloneTasks ? (
                  <View style={styles.modalLoadingContainer}>
                    <ActivityIndicator size="small" color="#9ca3af" />
                    <Text style={styles.modalLoadingText}>Loading tasks...</Text>
                  </View>
                ) : standaloneAdminTasks.length === 0 ? (
                  <View style={{ alignItems: 'center', paddingVertical: 32 }}>
                    <Text style={[styles.modalInfoText, { marginBottom: 16 }]}>
                      No unlinked admin tasks available to associate.
                    </Text>
                    <Pressable
                      onPress={() => {
                        setShowAssociateTaskModal(false);
                        setSelectedStandaloneTaskId('');
                      }}
                      style={[styles.modalButton, styles.modalButtonCancel]}
                    >
                      <Text style={styles.modalButtonCancelText}>Close</Text>
                    </Pressable>
                  </View>
                ) : (
                  <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator={true}>
                    <View style={styles.modalBody}>
                      <View style={styles.modalFormField}>
                        <Text style={styles.modalFormLabel}>Select Admin Task</Text>
                        <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                          <Picker
                            selectedValue={selectedStandaloneTaskId}
                            onValueChange={(value) => setSelectedStandaloneTaskId(value)}
                            style={{ height: 40 }}
                          >
                            <Picker.Item label="Select a task..." value="" />
                            {standaloneAdminTasks.map((task) => (
                              <Picker.Item
                                key={task.id}
                                label={`${task.title} (${task.priority.replace('_', ' ')})`}
                                value={task.id}
                              />
                            ))}
                          </Picker>
                        </View>
                      </View>

                      {selectedStandaloneTaskId && (() => {
                        const selectedTask = standaloneAdminTasks.find(task => task.id === selectedStandaloneTaskId);
                        if (!selectedTask) return null;
                        return (
                          <View style={[styles.modalInfo, { backgroundColor: '#f9fafb', marginTop: 16 }]}>
                            <Text style={styles.modalInfoTitle}>{selectedTask.title}</Text>
                            {selectedTask.description && (
                              <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                                {selectedTask.description}
                              </Text>
                            )}
                            <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                              Priority: {selectedTask.priority}
                            </Text>
                            <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                              Status: {selectedTask.status.replace('_', ' ')}
                            </Text>
                            {selectedTask.assignee?.full_name && (
                              <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                                Assigned to: {selectedTask.assignee.full_name}
                              </Text>
                            )}
                          </View>
                        );
                      })()}
                    </View>
                  </ScrollView>
                )}

                {!loadingStandaloneTasks && standaloneAdminTasks.length > 0 && (
                  <View style={styles.modalActions}>
                    <Pressable
                      onPress={() => {
                        setShowAssociateTaskModal(false);
                        setSelectedStandaloneTaskId('');
                      }}
                      style={[styles.modalButton, styles.modalButtonCancel]}
                    >
                      <Text style={styles.modalButtonCancelText}>Cancel</Text>
                    </Pressable>
                    <Pressable
                      onPress={handleAssociateTask}
                      disabled={!selectedStandaloneTaskId || associatingTask}
                      style={[
                        styles.modalButton,
                        styles.modalButtonPrimary,
                        (!selectedStandaloneTaskId || associatingTask) && styles.modalButtonPrimaryDisabled
                      ]}
                    >
                      {associatingTask ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <ActivityIndicator size="small" color="#ffffff" />
                          <Text style={styles.modalButtonPrimaryText}>Associating...</Text>
                        </View>
                      ) : (
                        <Text style={styles.modalButtonPrimaryText}>Associate Task</Text>
                      )}
                    </Pressable>
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Create Task Modal */}
      <Modal
        visible={showCreateTaskModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCreateTaskModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowCreateTaskModal(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={[styles.modalScrollContent, { maxHeight: '90%' }]}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Create Admin Task</Text>
                  <Pressable
                    onPress={() => setShowCreateTaskModal(false)}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator={true}>
                  <View style={styles.modalBody}>
                    <View style={styles.modalInfo}>
                      <Text style={styles.modalInfoTitle}>{request?.title}</Text>
                      <Text style={styles.modalInfoText}>
                        Home and service request association will be set automatically.
                      </Text>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Task Title *</Text>
                      <TextInput
                        style={styles.modalFormInput}
                        value={adminTaskFormData.title}
                        onChangeText={(text) => setAdminTaskFormData(prev => ({ ...prev, title: text }))}
                        placeholder="Follow up with provider"
                      />
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Description (Optional)</Text>
                      <TextInput
                        style={styles.modalFormTextArea}
                        value={adminTaskFormData.description}
                        onChangeText={(text) => setAdminTaskFormData(prev => ({ ...prev, description: text }))}
                        placeholder="Any context for this task..."
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Priority</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={adminTaskFormData.priority}
                          onValueChange={(value) => setAdminTaskFormData(prev => ({ ...prev, priority: value as 'low' | 'normal' | 'high' | 'urgent' }))}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="Low" value="low" />
                          <Picker.Item label="Normal" value="normal" />
                          <Picker.Item label="High" value="high" />
                          <Picker.Item label="Urgent" value="urgent" />
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Due Date (Optional)</Text>
                      <DateInput
                        value={adminTaskFormData.due_date}
                        onChange={(value) => setAdminTaskFormData(prev => ({ ...prev, due_date: value }))}
                      />
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Assign To</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={adminTaskFormData.assigned_to}
                          onValueChange={(value) => setAdminTaskFormData(prev => ({ ...prev, assigned_to: value }))}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="Default assignee" value="" />
                          {systemManagers.map((manager) => (
                            <Picker.Item key={manager.id} label={manager.full_name} value={manager.id} />
                          ))}
                        </Picker>
                      </View>
                    </View>
                  </View>
                </ScrollView>

                <View style={styles.modalActions}>
                  <Pressable
                    onPress={() => setShowCreateTaskModal(false)}
                    style={[styles.modalButton, styles.modalButtonCancel]}
                  >
                    <Text style={styles.modalButtonCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleCreateAdminTask}
                    disabled={creatingAdminTask || !adminTaskFormData.title.trim()}
                    style={[
                      styles.modalButton,
                      styles.modalButtonPrimary,
                      (creatingAdminTask || !adminTaskFormData.title.trim()) && styles.modalButtonPrimaryDisabled,
                    ]}
                  >
                    {creatingAdminTask ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <ActivityIndicator size="small" color="#ffffff" />
                        <Text style={styles.modalButtonPrimaryText}>Creating...</Text>
                      </View>
                    ) : (
                      <Text style={styles.modalButtonPrimaryText}>Create Task</Text>
                    )}
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Edit Task Modal */}
      <Modal
        visible={showEditTaskModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowEditTaskModal(false);
          setEditingAdminTask(null);
        }}
      >
        <TouchableWithoutFeedback onPress={() => {
          setShowEditTaskModal(false);
          setEditingAdminTask(null);
        }}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={[styles.modalScrollContent, { maxHeight: '90%' }]}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Edit Admin Task</Text>
                  <Pressable
                    onPress={() => {
                      setShowEditTaskModal(false);
                      setEditingAdminTask(null);
                    }}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator={true}>
                  <View style={styles.modalBody}>
                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Task Title *</Text>
                      <TextInput
                        style={styles.modalFormInput}
                        value={adminTaskEditFormData.title}
                        onChangeText={(text) => setAdminTaskEditFormData(prev => ({ ...prev, title: text }))}
                        placeholder="Task title"
                      />
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Description (Optional)</Text>
                      <TextInput
                        style={styles.modalFormTextArea}
                        value={adminTaskEditFormData.description}
                        onChangeText={(text) => setAdminTaskEditFormData(prev => ({ ...prev, description: text }))}
                        placeholder="Task description"
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Priority</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={adminTaskEditFormData.priority}
                          onValueChange={(value) => setAdminTaskEditFormData(prev => ({ ...prev, priority: value as 'low' | 'normal' | 'high' | 'urgent' }))}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="Low" value="low" />
                          <Picker.Item label="Normal" value="normal" />
                          <Picker.Item label="High" value="high" />
                          <Picker.Item label="Urgent" value="urgent" />
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Status</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={adminTaskEditFormData.status}
                          onValueChange={(value) => setAdminTaskEditFormData(prev => ({ ...prev, status: value as 'pending' | 'in_progress' | 'completed' }))}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="Pending" value="pending" />
                          <Picker.Item label="In Progress" value="in_progress" />
                          <Picker.Item label="Completed" value="completed" />
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Due Date (Optional)</Text>
                      <DateInput
                        value={adminTaskEditFormData.due_date}
                        onChange={(value) => setAdminTaskEditFormData(prev => ({ ...prev, due_date: value }))}
                      />
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Assign To</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={adminTaskEditFormData.assigned_to}
                          onValueChange={(value) => setAdminTaskEditFormData(prev => ({ ...prev, assigned_to: value }))}
                          style={{ height: 40 }}
                        >
                          {systemManagers.map((manager) => (
                            <Picker.Item key={manager.id} label={manager.full_name} value={manager.id} />
                          ))}
                        </Picker>
                      </View>
                    </View>
                  </View>
                </ScrollView>

                <View style={styles.modalActions}>
                  <Pressable
                    onPress={() => {
                      setShowEditTaskModal(false);
                      setEditingAdminTask(null);
                    }}
                    style={[styles.modalButton, styles.modalButtonCancel]}
                  >
                    <Text style={styles.modalButtonCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleSaveAdminTaskEdit}
                    disabled={savingAdminTaskEdit || !adminTaskEditFormData.title.trim()}
                    style={[
                      styles.modalButton,
                      styles.modalButtonPrimary,
                      (savingAdminTaskEdit || !adminTaskEditFormData.title.trim()) && styles.modalButtonPrimaryDisabled,
                    ]}
                  >
                    {savingAdminTaskEdit ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <ActivityIndicator size="small" color="#ffffff" />
                        <Text style={styles.modalButtonPrimaryText}>Saving...</Text>
                      </View>
                    ) : (
                      <Text style={styles.modalButtonPrimaryText}>Save Changes</Text>
                    )}
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Homeowner Preview Modal */}
      {showExpensePreviewModal && expenseToPreview && (() => {
        const visibleItems = getVisibleLineItemsForParent(expenseToPreview.id);
        const hiddenItems = getChildLineItemsForParent(expenseToPreview.id).filter((li) => !li.homeowner_visible);
        const homeownerName = request?.home?.owner?.full_name || 'Homeowner';
        const serviceRequestTitle = request?.title || 'Service Request';
        const amount = expenseToPreview.amount;
        const description = expenseToPreview.description;
        const safeDescription = (description || '').trim();

        return (
          <Modal
            visible={showExpensePreviewModal}
            transparent
            animationType="fade"
            onRequestClose={() => setShowExpensePreviewModal(false)}
          >
            <TouchableWithoutFeedback onPress={() => setShowExpensePreviewModal(false)}>
              <View style={styles.modalOverlay}>
                <TouchableWithoutFeedback>
                  <View style={[styles.modalScrollContent, { maxWidth: 540, maxHeight: '92%' }]}>

                    {/* Header */}
                    <View style={styles.modalHeader}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.modalTitle}>Homeowner View</Text>
                        <Text style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                          Preview what {homeownerName} will see
                        </Text>
                      </View>
                      <Pressable onPress={() => setShowExpensePreviewModal(false)} style={styles.modalCloseButton}>
                        <X size={20} color="#9ca3af" />
                      </Pressable>
                    </View>

                    {/* Tab switcher */}
                    <View style={styles.previewTabRow}>
                      <Pressable
                        onPress={() => setExpensePreviewTab('email')}
                        style={[styles.previewTab, expensePreviewTab === 'email' && styles.previewTabActive]}
                      >
                        <Text style={[styles.previewTabText, expensePreviewTab === 'email' && styles.previewTabTextActive]}>
                          Payment Email
                        </Text>
                      </Pressable>
                      <Pressable
                        onPress={() => setExpensePreviewTab('invoice')}
                        style={[styles.previewTab, expensePreviewTab === 'invoice' && styles.previewTabActive]}
                      >
                        <Text style={[styles.previewTabText, expensePreviewTab === 'invoice' && styles.previewTabTextActive]}>
                          Stripe Invoice
                        </Text>
                      </Pressable>
                    </View>

                    <ScrollView showsVerticalScrollIndicator={false} style={{ flexShrink: 1 }}>
                      <View style={{ gap: 16 }}>

                        {/* ── Email preview ── */}
                        {expensePreviewTab === 'email' && (
                          <View style={styles.previewEmailCard}>
                            {/* Email chrome */}
                            <View style={styles.previewEmailHeader}>
                              <Text style={styles.previewEmailFrom}>From: service@honeydone.com</Text>
                              <Text style={styles.previewEmailSubject}>
                                Subject: Payment requested for {serviceRequestTitle}
                              </Text>
                            </View>
                            <View style={styles.previewEmailBody}>
                              <Text style={styles.previewEmailText}>Hi {homeownerName},</Text>
                              <Text style={[styles.previewEmailText, { marginTop: 12 }]}>
                                We are requesting payment of{' '}
                                <Text style={{ fontWeight: '700', color: '#111827' }}>
                                  {formatCurrency(amount)}
                                </Text>{' '}
                                for your service request "{serviceRequestTitle}".
                              </Text>

                              {safeDescription ? (
                                <Text style={[styles.previewEmailText, { marginTop: 12 }]}>
                                  Expense details: {safeDescription}
                                </Text>
                              ) : null}

                              {visibleItems.length > 0 && (
                                <View style={{ marginTop: 12 }}>
                                  <Text style={[styles.previewEmailText, { fontWeight: '600', marginBottom: 6 }]}>
                                    Line items:
                                  </Text>
                                  {visibleItems.map((li) => (
                                    <Text key={li.id} style={styles.previewEmailLineItem}>
                                      — {(li.description || li.category || 'Line item').trim()}: {formatCurrency(li.amount)}
                                    </Text>
                                  ))}
                                </View>
                              )}

                              <Text style={[styles.previewEmailText, { marginTop: 12 }]}>
                                Please log in to{' '}
                                <Text style={{ color: '#2563eb', textDecorationLine: 'underline' }}>
                                  app.honeydone.com
                                </Text>{' '}
                                to review and pay.
                              </Text>
                              <Text style={[styles.previewEmailText, { marginTop: 12 }]}>
                                Thank you,{'\n'}HoneyDone Team
                              </Text>
                            </View>
                          </View>
                        )}

                        {/* ── Stripe invoice preview ── */}
                        {expensePreviewTab === 'invoice' && (
                          <View style={styles.previewInvoiceCard}>
                            <View style={styles.previewInvoiceHeader}>
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                <DollarSign size={18} color="#2563eb" />
                                <Text style={styles.previewInvoiceTitle}>Invoice</Text>
                              </View>
                              <View style={[styles.badge, { backgroundColor: '#fef3c7' }]}>
                                <Text style={[styles.badgeText, { color: '#92400e' }]}>Open</Text>
                              </View>
                            </View>

                            <View style={styles.previewInvoiceMeta}>
                              <Text style={styles.previewInvoiceMetaText}>To: {homeownerName}</Text>
                              <Text style={styles.previewInvoiceMetaText}>
                                Service Request: {serviceRequestTitle}
                              </Text>
                              {expenseToPreview.expense_date && (
                                <Text style={styles.previewInvoiceMetaText}>
                                  Date: {formatDateForDisplay(expenseToPreview.expense_date)}
                                </Text>
                              )}
                            </View>

                            {/* Line items — visible only */}
                            {visibleItems.length > 0 ? (
                              <View style={styles.previewInvoiceLineItems}>
                                <View style={styles.previewInvoiceLineItemHeader}>
                                  <Text style={[styles.previewInvoiceLineItemHeaderText, { flex: 1 }]}>Description</Text>
                                  <Text style={styles.previewInvoiceLineItemHeaderText}>Amount</Text>
                                </View>
                                {visibleItems.map((li) => (
                                  <View key={li.id} style={styles.previewInvoiceLineItemRow}>
                                    <View style={{ flex: 1 }}>
                                      <Text style={styles.previewInvoiceLineItemLabel}>
                                        {(li.description || li.category || 'Line item').trim()}
                                      </Text>
                                      {li.category && li.description && (
                                        <Text style={styles.previewInvoiceLineItemSub}>{li.category}</Text>
                                      )}
                                    </View>
                                    <Text style={styles.previewInvoiceLineItemAmount}>
                                      {formatCurrency(li.amount)}
                                    </Text>
                                  </View>
                                ))}
                              </View>
                            ) : (
                              /* No visible children — show parent as single line item */
                              <View style={styles.previewInvoiceLineItems}>
                                <View style={styles.previewInvoiceLineItemHeader}>
                                  <Text style={[styles.previewInvoiceLineItemHeaderText, { flex: 1 }]}>Description</Text>
                                  <Text style={styles.previewInvoiceLineItemHeaderText}>Amount</Text>
                                </View>
                                <View style={styles.previewInvoiceLineItemRow}>
                                  <Text style={[styles.previewInvoiceLineItemLabel, { flex: 1 }]}>
                                    {description || serviceRequestTitle}
                                  </Text>
                                  <Text style={styles.previewInvoiceLineItemAmount}>
                                    {formatCurrency(amount)}
                                  </Text>
                                </View>
                              </View>
                            )}

                            {/* Total row */}
                            <View style={styles.previewInvoiceTotalRow}>
                              <Text style={styles.previewInvoiceTotalLabel}>Total due</Text>
                              <Text style={styles.previewInvoiceTotalAmount}>{formatCurrency(amount)}</Text>
                            </View>
                          </View>
                        )}

                        {/* Admin-only: hidden components callout */}
                        {hiddenItems.length > 0 && (
                          <View style={styles.previewHiddenCallout}>
                            <Text style={styles.previewHiddenCalloutTitle}>
                              {hiddenItems.length} component{hiddenItems.length > 1 ? 's' : ''} hidden from homeowner
                            </Text>
                            {hiddenItems.map((li) => (
                              <View key={li.id} style={styles.previewHiddenRow}>
                                <Text style={styles.previewHiddenLabel}>
                                  {(li.description || li.category || 'Line item').trim()}
                                </Text>
                                <Text style={styles.previewHiddenAmount}>{formatCurrency(li.amount)}</Text>
                              </View>
                            ))}
                            <Text style={styles.previewHiddenNote}>
                              These amounts are included in the parent total but not shown to the homeowner. Toggle visibility on the expense card to change this.
                            </Text>
                          </View>
                        )}

                        {visibleItems.length === 0 && getChildLineItemsForParent(expenseToPreview.id).length === 0 && (
                          <View style={styles.previewHiddenCallout}>
                            <Text style={styles.previewHiddenCalloutTitle}>No line item breakdown</Text>
                            <Text style={styles.previewHiddenNote}>
                              This expense has no line items. The homeowner will see only the total amount. Use "Add Line Item" on the expense card to break it down.
                            </Text>
                          </View>
                        )}

                      </View>
                    </ScrollView>

                    <View style={[styles.modalActions, { marginTop: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#f3f4f6' }]}>
                      <Pressable
                        onPress={() => setShowExpensePreviewModal(false)}
                        style={[styles.modalButton, styles.modalButtonCancel]}
                      >
                        <Text style={styles.modalButtonCancelText}>Close</Text>
                      </Pressable>
                    </View>
                  </View>
                </TouchableWithoutFeedback>
              </View>
            </TouchableWithoutFeedback>
          </Modal>
        );
      })()}

      {/* Add/Edit Expense Wizard Modal */}
      <ExpenseEditorModal
        visible={showExpenseModal}
        onClose={() => {
          setShowExpenseModal(false);
          setEditingExpense(null);
          setLineItemParentExpense(null);
          setExpenseFormData({ ...getDefaultExpenseFormData(), expense_date: formatDateOnly(new Date()) });
          setExpenseWizardStep(1);
          setError(null);
        }}
        overlayStyle={styles.modalOverlay}
      >
        <View style={[styles.modalScrollContent, { maxWidth: 520, maxHeight: '92%' }]}>

                {/* Wizard header */}
                <View style={styles.modalHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.modalTitle}>
                      {editingExpense
                        ? (editingExpense.parent_expense_id ? 'Edit Line Item' : 'Edit Expense')
                        : lineItemParentExpense ? 'Add Line Item' : 'Add Expense'}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {expenseWizardStep === 1 && 'Step 1 of 4 — Overview'}
                      {expenseWizardStep === 2 && 'Step 2 of 4 — Expense Details'}
                      {expenseWizardStep === 3 && 'Step 3 of 4 — Costs'}
                      {expenseWizardStep === 4 && 'Step 4 of 4 — Visibility'}
                    </Text>
                  </View>
                  <Pressable
                    onPress={() => {
                      setShowExpenseModal(false);
                      setEditingExpense(null);
                      setLineItemParentExpense(null);
                      setExpenseFormData({ ...getDefaultExpenseFormData(), expense_date: formatDateOnly(new Date()) });
                      setExpenseWizardStep(1);
                      setError(null);
                    }}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9ca3af" />
                  </Pressable>
                </View>

                {/* Step progress bar */}
                <View style={styles.wizardProgressBar}>
                  {[1, 2, 3, 4].map((step) => (
                    <View
                      key={step}
                      style={[
                        styles.wizardProgressSegment,
                        expenseWizardStep >= step
                          ? styles.wizardProgressSegmentActive
                          : styles.wizardProgressSegmentInactive,
                      ]}
                    />
                  ))}
                </View>

                {(() => {
                  // Derived helpers used across steps
                  const isNewParent = !editingExpense && !lineItemParentExpense;
                  const isLineItemFlow = !!lineItemParentExpense || (editingExpense && !!editingExpense.parent_expense_id);
                  const wizardTotal = wizardLineItems.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0);
                  const wizardInternalTotal = wizardLineItems.reduce((sum, li) => sum + (parseFloat(li.internal_cost) || 0), 0);

                  // Determine whether Continue is allowed
                  const step2Valid = isNewParent
                    ? wizardLineItems.length > 0
                    : !!(expenseFormData.description || expenseFormData.amount);
                  const step3Valid = isNewParent
                    ? true
                    : !!(expenseFormData.amount.trim());

                  const closeWizard = () => {
                    setShowExpenseModal(false);
                    setEditingExpense(null);
                    setLineItemParentExpense(null);
                    setExpenseFormData({ ...getDefaultExpenseFormData(), expense_date: formatDateOnly(new Date()) });
                    setWizardLineItems([]);
                    setWizardLineItemDraft(getDefaultWizardLineItemDraft());
                    setShowWizardAddLineItemForm(false);
                    setWizardAmountOverride(false);
                    setExpenseWizardStep(1);
                    setError(null);
                  };

                  const addDraftLineItem = () => {
                    if (!wizardLineItemDraft.amount.trim() || parseFloat(wizardLineItemDraft.amount) <= 0) return;
                    setWizardLineItems((prev) => [
                      ...prev,
                      { ...wizardLineItemDraft, tempId: `${Date.now()}-${Math.random()}` },
                    ]);
                    setWizardLineItemDraft(getDefaultWizardLineItemDraft());
                    setShowWizardAddLineItemForm(false);
                  };

                  return (
                    <>
                      <ScrollView showsVerticalScrollIndicator={false} style={{ flexShrink: 1 }}>
                        <View style={styles.modalBody}>

                          {/* ── Step 1: Overview ── */}
                          {expenseWizardStep === 1 && (
                            <View style={{ gap: 16 }}>
                              <View style={styles.wizardIntroHeader}>
                                <DollarSign size={28} color="#2563eb" />
                                <Text style={styles.wizardIntroTitle}>
                                  {isLineItemFlow ? 'Adding a line item' : 'Build up an expense'}
                                </Text>
                              </View>

                              {isLineItemFlow ? (
                                <View style={{ gap: 12 }}>
                                  <View style={styles.wizardInfoBox}>
                                    <Text style={styles.wizardInfoBoxTitle}>Parent expense</Text>
                                    <Text style={styles.wizardInfoBoxValue}>
                                      {formatCurrency(lineItemParentExpense?.amount ?? null)}
                                    </Text>
                                    {lineItemParentExpense?.description ? (
                                      <Text style={styles.wizardInfoBoxSub}>{lineItemParentExpense.description}</Text>
                                    ) : null}
                                  </View>
                                  <View style={styles.wizardStepCard}>
                                    <Text style={styles.wizardStepCardTitle}>Adding a single line item</Text>
                                    <Text style={styles.wizardStepCardBody}>
                                      Line items are the individual cost components under a parent expense. Each has its own category, vendor, external price, and optional internal cost. You control whether it appears on the homeowner's invoice.
                                    </Text>
                                  </View>
                                </View>
                              ) : (
                                <View style={{ gap: 12 }}>
                                  <View style={styles.wizardStepCard}>
                                    <Text style={styles.wizardStepCardTitle}>Start with individual costs</Text>
                                    <Text style={styles.wizardStepCardBody}>
                                      Instead of entering a lump sum, add each cost component one by one — labor, materials, fees, etc. The total billed to the homeowner is automatically computed from those line items.
                                    </Text>
                                  </View>
                                  <View style={styles.wizardStepCard}>
                                    <Text style={styles.wizardStepCardTitle}>Internal vs. external amounts</Text>
                                    <Text style={styles.wizardStepCardBody}>
                                      Each line item has an external amount (what the homeowner pays) and an optional internal cost (what HoneyDone actually pays out). The difference is your margin — never shown to homeowners.
                                    </Text>
                                  </View>
                                  <View style={styles.wizardStepCard}>
                                    <Text style={styles.wizardStepCardTitle}>Control price transparency</Text>
                                    <Text style={styles.wizardStepCardBody}>
                                      You decide which line items appear on the homeowner's invoice. You can show a full breakdown or only the total — set visibility per line item in the next step.
                                    </Text>
                                  </View>
                                </View>
                              )}
                            </View>
                          )}

                          {/* ── Step 2 ── */}
                          {expenseWizardStep === 2 && (
                            <View style={{ gap: 16 }}>

                              {/* ── Step 2 (new parent): Line item builder ── */}
                              {isNewParent && (
                                <>
                                  <View style={styles.wizardStepCard}>
                                    <Text style={styles.wizardStepCardTitle}>Add cost components</Text>
                                    <Text style={styles.wizardStepCardBody}>
                                      Add each individual cost below. The parent total will be computed automatically from the amounts you enter.
                                    </Text>
                                  </View>

                                  {/* Built-up line item list */}
                                  {wizardLineItems.length > 0 && (
                                    <View style={styles.wizardBuilderList}>
                                      {wizardLineItems.map((li, idx) => (
                                        <View key={li.tempId} style={styles.wizardBuilderRow}>
                                          <View style={{ flex: 1 }}>
                                            <Text style={styles.wizardBuilderRowLabel}>
                                              {li.description || '(no description)'}
                                            </Text>
                                            <Text style={styles.wizardBuilderRowMeta}>
                                              {[li.category, li.vendor].filter(Boolean).join(' · ')}
                                            </Text>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                              <Pressable
                                                onPress={() => setWizardLineItems((prev) => prev.map((item, i) => i === idx ? { ...item, homeowner_visible: !item.homeowner_visible } : item))}
                                                style={[styles.wizardVisibilityBadge, li.homeowner_visible ? styles.wizardVisibilityBadgeVisible : styles.wizardVisibilityBadgeHidden]}
                                              >
                                                <Text style={[styles.wizardVisibilityBadgeText, li.homeowner_visible ? styles.wizardVisibilityBadgeTextVisible : styles.wizardVisibilityBadgeTextHidden]}>
                                                  {li.homeowner_visible ? 'Visible' : 'Hidden'}
                                                </Text>
                                              </Pressable>
                                              {li.internal_cost ? (
                                                <Text style={{ fontSize: 11, color: '#6b7280' }}>
                                                  Cost: {formatCurrency(parseFloat(li.internal_cost))}
                                                </Text>
                                              ) : null}
                                            </View>
                                          </View>
                                          <View style={{ alignItems: 'flex-end', gap: 6 }}>
                                            <Text style={styles.wizardBuilderRowAmount}>
                                              {formatCurrency(parseFloat(li.amount) || 0)}
                                            </Text>
                                            <Pressable
                                              onPress={() => setWizardLineItems((prev) => prev.filter((_, i) => i !== idx))}
                                            >
                                              <X size={14} color="#9ca3af" />
                                            </Pressable>
                                          </View>
                                        </View>
                                      ))}

                                      {/* Running total bar */}
                                      <View style={styles.wizardBuilderTotal}>
                                        <Text style={styles.wizardBuilderTotalLabel}>Total billed to homeowner</Text>
                                        <Text style={styles.wizardBuilderTotalAmount}>{formatCurrency(wizardTotal)}</Text>
                                      </View>
                                      {wizardInternalTotal > 0 && (
                                        <View style={[styles.wizardBuilderTotal, { backgroundColor: '#f9fafb', borderTopWidth: 0, paddingTop: 8 }]}>
                                          <Text style={[styles.wizardBuilderTotalLabel, { color: '#6b7280' }]}>Internal cost total</Text>
                                          <Text style={[styles.wizardBuilderTotalAmount, { color: '#6b7280', fontSize: 14 }]}>{formatCurrency(wizardInternalTotal)}</Text>
                                        </View>
                                      )}
                                      {wizardInternalTotal > 0 && wizardTotal > 0 && (
                                        <View style={[styles.wizardBuilderTotal, { backgroundColor: '#f0fdf4', borderTopWidth: 0, paddingTop: 8 }]}>
                                          <Text style={[styles.wizardBuilderTotalLabel, { color: '#15803d' }]}>Margin</Text>
                                          <Text style={[styles.wizardBuilderTotalAmount, { color: '#15803d', fontSize: 14 }]}>{formatCurrency(wizardTotal - wizardInternalTotal)}</Text>
                                        </View>
                                      )}
                                    </View>
                                  )}

                                  {/* Add line item form */}
                                  {showWizardAddLineItemForm ? (
                                    <View style={styles.wizardAddLineItemForm}>
                                      <Text style={[styles.wizardStepCardTitle, { marginBottom: 12 }]}>New cost component</Text>

                                      <View style={[styles.modalFormField, { marginBottom: 10 }]}>
                                        <Text style={styles.modalFormLabel}>Description</Text>
                                        <TextInput
                                          style={styles.modalFormInput}
                                          value={wizardLineItemDraft.description}
                                          onChangeText={(t) => setWizardLineItemDraft({ ...wizardLineItemDraft, description: t })}
                                          placeholder="e.g. Contractor labor"
                                          autoFocus
                                        />
                                      </View>

                                      <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
                                        <View style={[styles.modalFormField, { flex: 1 }]}>
                                          <Text style={styles.modalFormLabel}>Category</Text>
                                          <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                                            <Picker
                                              selectedValue={wizardLineItemDraft.category}
                                              onValueChange={(v) => setWizardLineItemDraft({ ...wizardLineItemDraft, category: v })}
                                              style={{ height: 40 }}
                                            >
                                              <Picker.Item label="Category..." value="" />
                                              <Picker.Item label="Labor" value="Labor" />
                                              <Picker.Item label="Materials" value="Materials" />
                                              <Picker.Item label="Parts" value="Parts" />
                                              <Picker.Item label="Service Fee" value="Service Fee" />
                                              <Picker.Item label="Other" value="Other" />
                                            </Picker>
                                          </View>
                                        </View>
                                        <View style={[styles.modalFormField, { flex: 1 }]}>
                                          <Text style={styles.modalFormLabel}>Vendor</Text>
                                          <TextInput
                                            style={styles.modalFormInput}
                                            value={wizardLineItemDraft.vendor}
                                            onChangeText={(t) => setWizardLineItemDraft({ ...wizardLineItemDraft, vendor: t })}
                                            placeholder="Vendor name"
                                          />
                                        </View>
                                      </View>

                                      <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
                                        <View style={[styles.modalFormField, { flex: 1 }]}>
                                          <Text style={styles.modalFormLabel}>External amount <Text style={{ color: '#dc2626' }}>*</Text></Text>
                                          <TextInput
                                            style={[styles.modalFormInput, { fontWeight: '600' }]}
                                            value={wizardLineItemDraft.amount}
                                            onChangeText={(t) => setWizardLineItemDraft({ ...wizardLineItemDraft, amount: t })}
                                            placeholder="0.00"
                                            keyboardType="decimal-pad"
                                          />
                                        </View>
                                        <View style={[styles.modalFormField, { flex: 1 }]}>
                                          <Text style={styles.modalFormLabel}>Internal cost</Text>
                                          <TextInput
                                            style={styles.modalFormInput}
                                            value={wizardLineItemDraft.internal_cost}
                                            onChangeText={(t) => setWizardLineItemDraft({ ...wizardLineItemDraft, internal_cost: t })}
                                            placeholder="0.00 (optional)"
                                            keyboardType="decimal-pad"
                                          />
                                        </View>
                                      </View>

                                      <View style={[styles.wizardToggleRow, { marginBottom: 12 }]}>
                                        <View style={{ flex: 1 }}>
                                          <Text style={styles.wizardToggleLabel}>Visible to homeowner</Text>
                                          <Text style={styles.wizardToggleSub}>Show this line item on the homeowner's invoice</Text>
                                        </View>
                                        <Pressable
                                          onPress={() => setWizardLineItemDraft({ ...wizardLineItemDraft, homeowner_visible: !wizardLineItemDraft.homeowner_visible })}
                                          style={[styles.wizardToggleSwitch, wizardLineItemDraft.homeowner_visible && styles.wizardToggleSwitchOn]}
                                        >
                                          <View style={[styles.wizardToggleKnob, wizardLineItemDraft.homeowner_visible && styles.wizardToggleKnobOn]} />
                                        </Pressable>
                                      </View>

                                      <View style={{ flexDirection: 'row', gap: 8 }}>
                                        <Pressable
                                          onPress={() => { setShowWizardAddLineItemForm(false); setWizardLineItemDraft(getDefaultWizardLineItemDraft()); }}
                                          style={[styles.modalButton, styles.modalButtonCancel, { flex: 0, paddingHorizontal: 16 }]}
                                        >
                                          <Text style={styles.modalButtonCancelText}>Cancel</Text>
                                        </Pressable>
                                        <Pressable
                                          onPress={addDraftLineItem}
                                          disabled={!wizardLineItemDraft.amount.trim() || parseFloat(wizardLineItemDraft.amount) <= 0}
                                          style={[
                                            styles.modalButton,
                                            styles.modalButtonPrimary,
                                            (!wizardLineItemDraft.amount.trim() || parseFloat(wizardLineItemDraft.amount) <= 0) && styles.modalButtonPrimaryDisabled,
                                          ]}
                                        >
                                          <Text style={styles.modalButtonPrimaryText}>Add Cost</Text>
                                        </Pressable>
                                      </View>
                                    </View>
                                  ) : (
                                    <Pressable
                                      onPress={() => setShowWizardAddLineItemForm(true)}
                                      style={styles.wizardAddCostButton}
                                    >
                                      <Plus size={16} color="#2563eb" />
                                      <Text style={styles.wizardAddCostButtonText}>Add a cost component</Text>
                                    </Pressable>
                                  )}
                                </>
                              )}

                              {/* ── Step 2 (line item / edit): Details form ── */}
                              {!isNewParent && (
                                <>
                                  <View style={styles.wizardStepCard}>
                                    <Text style={styles.wizardStepCardTitle}>
                                      {isLineItemFlow ? 'Line item details' : 'Expense details'}
                                    </Text>
                                    <Text style={styles.wizardStepCardBody}>
                                      {isLineItemFlow
                                        ? 'Describe this cost component. Category and vendor help you track the breakdown internally.'
                                        : 'Give this expense a clear label. You can optionally track the category and vendor for reporting.'}
                                    </Text>
                                  </View>

                                  <View style={styles.modalFormField}>
                                    <Text style={styles.modalFormLabel}>Description</Text>
                                    <TextInput
                                      style={styles.modalFormTextArea}
                                      value={expenseFormData.description}
                                      onChangeText={(text) => setExpenseFormData({ ...expenseFormData, description: text })}
                                      placeholder={isLineItemFlow ? 'e.g. Contractor labor' : 'e.g. Gutter cleaning service'}
                                      multiline
                                      numberOfLines={3}
                                      autoFocus
                                    />
                                  </View>

                                  {isLineItemFlow && (
                                    <>
                                      <View style={{ flexDirection: 'row', gap: 10 }}>
                                        <View style={[styles.modalFormField, { flex: 1 }]}>
                                          <Text style={styles.modalFormLabel}>Category</Text>
                                          <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                                            <Picker
                                              selectedValue={expenseFormData.category}
                                              onValueChange={(value) => setExpenseFormData({ ...expenseFormData, category: value })}
                                              style={{ height: 40 }}
                                            >
                                              <Picker.Item label="Select..." value="" />
                                              <Picker.Item label="Labor" value="Labor" />
                                              <Picker.Item label="Materials" value="Materials" />
                                              <Picker.Item label="Parts" value="Parts" />
                                              <Picker.Item label="Service Fee" value="Service Fee" />
                                              <Picker.Item label="Other" value="Other" />
                                            </Picker>
                                          </View>
                                        </View>
                                        <View style={[styles.modalFormField, { flex: 1 }]}>
                                          <Text style={styles.modalFormLabel}>Vendor / Payee</Text>
                                          <TextInput
                                            style={styles.modalFormInput}
                                            value={expenseFormData.vendor}
                                            onChangeText={(text) => setExpenseFormData({ ...expenseFormData, vendor: text })}
                                            placeholder="Vendor name"
                                          />
                                        </View>
                                      </View>
                                    </>
                                  )}

                                  <View style={styles.modalFormField}>
                                    <Text style={styles.modalFormLabel}>Expense Date</Text>
                                    {Platform.OS === 'web' ? (
                                      <input
                                        type="date"
                                        value={expenseFormData.expense_date}
                                        onChange={(e) => setExpenseFormData({ ...expenseFormData, expense_date: e.target.value })}
                                        style={{
                                          width: '100%',
                                          padding: '10px 12px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: 6,
                                          fontSize: 14,
                                          color: '#111827',
                                          boxSizing: 'border-box',
                                        }}
                                      />
                                    ) : (
                                      <TextInput
                                        style={styles.modalFormInput}
                                        value={expenseFormData.expense_date}
                                        onChangeText={(text) => setExpenseFormData({ ...expenseFormData, expense_date: text })}
                                        placeholder="YYYY-MM-DD"
                                      />
                                    )}
                                  </View>

                                  {!expenseFormData.parent_expense_id && !isLineItemFlow && (
                                    <View style={styles.modalFormField}>
                                      <Text style={styles.modalFormLabel}>Link to Quote</Text>
                                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                                        <Picker
                                          selectedValue={expenseFormData.quote_id}
                                          onValueChange={(value) => setExpenseFormData({ ...expenseFormData, quote_id: value })}
                                          style={{ height: 40 }}
                                        >
                                          <Picker.Item label="No quote link" value="" />
                                          {quotes.map((quote) => (
                                            <Picker.Item
                                              key={quote.id}
                                              label={`${quote.service_provider?.company_name || 'Unknown Provider'} — ${formatCurrency(quote.amount || 0)}`}
                                              value={quote.id}
                                            />
                                          ))}
                                        </Picker>
                                      </View>
                                    </View>
                                  )}
                                </>
                              )}
                            </View>
                          )}

                          {/* ── Step 3: Billing / Costs ── */}
                          {expenseWizardStep === 3 && (
                            <View style={{ gap: 16 }}>

                              {/* New parent: billing total review */}
                              {isNewParent && (
                                <>
                                  <View style={styles.wizardStepCard}>
                                    <Text style={styles.wizardStepCardTitle}>Billing total</Text>
                                    <Text style={styles.wizardStepCardBody}>
                                      The total below is automatically computed from the cost components you entered. You can override it if you want to charge a different amount — for example, a rounded or negotiated price.
                                    </Text>
                                  </View>

                                  <View style={styles.wizardInfoBox}>
                                    <Text style={styles.wizardInfoBoxTitle}>Computed total from line items</Text>
                                    <Text style={styles.wizardInfoBoxValue}>{formatCurrency(wizardTotal)}</Text>
                                    {wizardInternalTotal > 0 && (
                                      <Text style={styles.wizardInfoBoxSub}>
                                        Internal cost: {formatCurrency(wizardInternalTotal)} · Margin: {formatCurrency(wizardTotal - wizardInternalTotal)}
                                      </Text>
                                    )}
                                  </View>

                                  <View style={styles.wizardToggleRow}>
                                    <View style={{ flex: 1 }}>
                                      <Text style={styles.wizardToggleLabel}>Override billing total</Text>
                                      <Text style={styles.wizardToggleSub}>Charge the homeowner a custom amount instead of the computed sum</Text>
                                    </View>
                                    <Pressable
                                      onPress={() => setWizardAmountOverride((v) => !v)}
                                      style={[styles.wizardToggleSwitch, wizardAmountOverride && styles.wizardToggleSwitchOn]}
                                    >
                                      <View style={[styles.wizardToggleKnob, wizardAmountOverride && styles.wizardToggleKnobOn]} />
                                    </Pressable>
                                  </View>

                                  {wizardAmountOverride && (
                                    <View style={styles.modalFormField}>
                                      <Text style={styles.modalFormLabel}>Custom billing total <Text style={{ color: '#dc2626' }}>*</Text></Text>
                                      <TextInput
                                        style={[styles.modalFormInput, { fontSize: 18, fontWeight: '600' }]}
                                        value={expenseFormData.amount}
                                        onChangeText={(text) => setExpenseFormData({ ...expenseFormData, amount: text })}
                                        placeholder="0.00"
                                        keyboardType="decimal-pad"
                                        autoFocus
                                      />
                                    </View>
                                  )}

                                  <View style={styles.modalFormField}>
                                    <Text style={styles.modalFormLabel}>Expense label (optional)</Text>
                                    <TextInput
                                      style={styles.modalFormInput}
                                      value={expenseFormData.description}
                                      onChangeText={(text) => setExpenseFormData({ ...expenseFormData, description: text })}
                                      placeholder="e.g. Gutter cleaning service — March 2026"
                                    />
                                    <Text style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                                      This label appears on the homeowner's invoice and payment email.
                                    </Text>
                                  </View>

                                  <View style={styles.modalFormField}>
                                    <Text style={styles.modalFormLabel}>Expense Date</Text>
                                    {Platform.OS === 'web' ? (
                                      <input
                                        type="date"
                                        value={expenseFormData.expense_date}
                                        onChange={(e) => setExpenseFormData({ ...expenseFormData, expense_date: e.target.value })}
                                        style={{
                                          width: '100%',
                                          padding: '10px 12px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: 6,
                                          fontSize: 14,
                                          color: '#111827',
                                          boxSizing: 'border-box',
                                        }}
                                      />
                                    ) : (
                                      <TextInput
                                        style={styles.modalFormInput}
                                        value={expenseFormData.expense_date}
                                        onChangeText={(text) => setExpenseFormData({ ...expenseFormData, expense_date: text })}
                                        placeholder="YYYY-MM-DD"
                                      />
                                    )}
                                  </View>

                                  <View style={{ gap: 10 }}>
                                    <View style={styles.wizardToggleRow}>
                                      <View style={{ flex: 1 }}>
                                        <Text style={styles.wizardToggleLabel}>Billable to homeowner</Text>
                                        <Text style={styles.wizardToggleSub}>The homeowner will be invoiced for this expense</Text>
                                      </View>
                                      <Pressable
                                        onPress={() => setExpenseFormData({ ...expenseFormData, billable_to_homeowner: !expenseFormData.billable_to_homeowner })}
                                        style={[styles.wizardToggleSwitch, expenseFormData.billable_to_homeowner && styles.wizardToggleSwitchOn]}
                                      >
                                        <View style={[styles.wizardToggleKnob, expenseFormData.billable_to_homeowner && styles.wizardToggleKnobOn]} />
                                      </Pressable>
                                    </View>

                                    <View style={styles.wizardToggleRow}>
                                      <View style={{ flex: 1 }}>
                                        <Text style={styles.wizardToggleLabel}>Needs contractor payment</Text>
                                        <Text style={styles.wizardToggleSub}>HoneyDone needs to pay a contractor or vendor</Text>
                                      </View>
                                      <Pressable
                                        onPress={() => setExpenseFormData({ ...expenseFormData, needs_contractor_payment: !expenseFormData.needs_contractor_payment })}
                                        style={[styles.wizardToggleSwitch, expenseFormData.needs_contractor_payment && styles.wizardToggleSwitchOn]}
                                      >
                                        <View style={[styles.wizardToggleKnob, expenseFormData.needs_contractor_payment && styles.wizardToggleKnobOn]} />
                                      </Pressable>
                                    </View>
                                  </View>
                                </>
                              )}

                              {/* Line item / edit: amounts */}
                              {!isNewParent && (
                                <>
                                  <View style={styles.wizardStepCard}>
                                    <Text style={styles.wizardStepCardTitle}>Set the amounts</Text>
                                    <Text style={styles.wizardStepCardBody}>
                                      External amount is what the homeowner is charged. Internal cost is what HoneyDone pays out — admin-only.
                                    </Text>
                                  </View>

                                  {isLineItemFlow && (
                                    <View style={styles.wizardInfoBox}>
                                      <Text style={styles.wizardInfoBoxTitle}>Parent total</Text>
                                      <Text style={styles.wizardInfoBoxValue}>
                                        {formatCurrency(
                                          lineItemParentExpense?.amount ??
                                          expenses.find((item) => item.id === editingExpense?.parent_expense_id)?.amount ??
                                          null
                                        )}
                                      </Text>
                                    </View>
                                  )}

                                  <View style={styles.modalFormField}>
                                    <Text style={styles.modalFormLabel}>
                                      External Amount — charged to homeowner <Text style={{ color: '#dc2626' }}>*</Text>
                                    </Text>
                                    <TextInput
                                      style={[styles.modalFormInput, { fontSize: 18, fontWeight: '600' }]}
                                      value={expenseFormData.amount}
                                      onChangeText={(text) => setExpenseFormData({ ...expenseFormData, amount: text })}
                                      placeholder="0.00"
                                      keyboardType="decimal-pad"
                                      autoFocus
                                    />
                                  </View>

                                  <View style={styles.modalFormField}>
                                    <Text style={styles.modalFormLabel}>Internal Cost — HoneyDone payout (optional)</Text>
                                    <TextInput
                                      style={styles.modalFormInput}
                                      value={expenseFormData.internal_cost}
                                      onChangeText={(text) => setExpenseFormData({ ...expenseFormData, internal_cost: text })}
                                      placeholder="0.00"
                                      keyboardType="decimal-pad"
                                    />
                                    {expenseFormData.amount && expenseFormData.internal_cost && (
                                      <View style={styles.wizardMarginPill}>
                                        <Text style={styles.wizardMarginPillText}>
                                          Margin: {formatCurrency(parseFloat(expenseFormData.amount || '0') - parseFloat(expenseFormData.internal_cost || '0'))}
                                        </Text>
                                      </View>
                                    )}
                                  </View>

                                  {!isLineItemFlow && (
                                    <View style={{ gap: 10 }}>
                                      <View style={styles.wizardToggleRow}>
                                        <View style={{ flex: 1 }}>
                                          <Text style={styles.wizardToggleLabel}>Billable to homeowner</Text>
                                          <Text style={styles.wizardToggleSub}>The homeowner will be invoiced for this expense</Text>
                                        </View>
                                        <Pressable
                                          onPress={() => setExpenseFormData({ ...expenseFormData, billable_to_homeowner: !expenseFormData.billable_to_homeowner })}
                                          style={[styles.wizardToggleSwitch, expenseFormData.billable_to_homeowner && styles.wizardToggleSwitchOn]}
                                        >
                                          <View style={[styles.wizardToggleKnob, expenseFormData.billable_to_homeowner && styles.wizardToggleKnobOn]} />
                                        </Pressable>
                                      </View>

                                      <View style={styles.wizardToggleRow}>
                                        <View style={{ flex: 1 }}>
                                          <Text style={styles.wizardToggleLabel}>Needs contractor payment</Text>
                                          <Text style={styles.wizardToggleSub}>HoneyDone needs to pay a contractor or vendor</Text>
                                        </View>
                                        <Pressable
                                          onPress={() => setExpenseFormData({ ...expenseFormData, needs_contractor_payment: !expenseFormData.needs_contractor_payment })}
                                          style={[styles.wizardToggleSwitch, expenseFormData.needs_contractor_payment && styles.wizardToggleSwitchOn]}
                                        >
                                          <View style={[styles.wizardToggleKnob, expenseFormData.needs_contractor_payment && styles.wizardToggleKnobOn]} />
                                        </Pressable>
                                      </View>
                                    </View>
                                  )}
                                </>
                              )}
                            </View>
                          )}

                          {/* ── Step 4: Visibility & Summary ── */}
                          {expenseWizardStep === 4 && (
                            <View style={{ gap: 16 }}>
                              <View style={styles.wizardStepCard}>
                                <Text style={styles.wizardStepCardTitle}>Review & visibility</Text>
                                <Text style={styles.wizardStepCardBody}>
                                  {isNewParent
                                    ? 'Review your expense. Visibility for each cost component is set below — tap a badge to toggle.'
                                    : 'Control what the homeowner sees when they receive their invoice or payment request.'}
                                </Text>
                              </View>

                              {/* New parent: summary + per-line-item visibility */}
                              {isNewParent && (
                                <>
                                  {/* Summary card */}
                                  <View style={{ gap: 8 }}>
                                    {expenseFormData.description ? (
                                      <View style={styles.wizardSummaryRow}>
                                        <Text style={styles.wizardSummaryLabel}>Label</Text>
                                        <Text style={styles.wizardSummaryValue}>{expenseFormData.description}</Text>
                                      </View>
                                    ) : null}
                                    <View style={styles.wizardSummaryRow}>
                                      <Text style={styles.wizardSummaryLabel}>Total charged to homeowner</Text>
                                      <Text style={[styles.wizardSummaryValue, { fontWeight: '700', color: '#111827' }]}>
                                        {formatCurrency(wizardAmountOverride && expenseFormData.amount.trim() ? parseFloat(expenseFormData.amount) : wizardTotal)}
                                      </Text>
                                    </View>
                                    {wizardInternalTotal > 0 && (
                                      <View style={styles.wizardSummaryRow}>
                                        <Text style={styles.wizardSummaryLabel}>Total internal cost</Text>
                                        <Text style={styles.wizardSummaryValue}>{formatCurrency(wizardInternalTotal)}</Text>
                                      </View>
                                    )}
                                    {wizardInternalTotal > 0 && (
                                      <View style={styles.wizardSummaryRow}>
                                        <Text style={styles.wizardSummaryLabel}>Margin</Text>
                                        <Text style={[styles.wizardSummaryValue, { color: '#16a34a' }]}>
                                          {formatCurrency(wizardTotal - wizardInternalTotal)}
                                        </Text>
                                      </View>
                                    )}
                                    <View style={styles.wizardSummaryRow}>
                                      <Text style={styles.wizardSummaryLabel}>Billable to homeowner</Text>
                                      <Text style={styles.wizardSummaryValue}>{expenseFormData.billable_to_homeowner ? 'Yes' : 'No'}</Text>
                                    </View>
                                    <View style={styles.wizardSummaryRow}>
                                      <Text style={styles.wizardSummaryLabel}>Needs contractor payment</Text>
                                      <Text style={styles.wizardSummaryValue}>{expenseFormData.needs_contractor_payment ? 'Yes' : 'No'}</Text>
                                    </View>
                                  </View>

                                  {/* Per-line-item visibility */}
                                  <View>
                                    <Text style={[styles.wizardStepCardTitle, { marginBottom: 8 }]}>Cost components visibility</Text>
                                    <View style={styles.wizardBuilderList}>
                                      {wizardLineItems.map((li, idx) => (
                                        <View key={li.tempId} style={styles.wizardBuilderRow}>
                                          <View style={{ flex: 1 }}>
                                            <Text style={styles.wizardBuilderRowLabel}>{li.description || '(no description)'}</Text>
                                            <Text style={styles.wizardBuilderRowMeta}>
                                              {[li.category, li.vendor].filter(Boolean).join(' · ')}
                                            </Text>
                                          </View>
                                          <View style={{ alignItems: 'flex-end', gap: 6 }}>
                                            <Text style={styles.wizardBuilderRowAmount}>{formatCurrency(parseFloat(li.amount) || 0)}</Text>
                                            <Pressable
                                              onPress={() => setWizardLineItems((prev) => prev.map((item, i) => i === idx ? { ...item, homeowner_visible: !item.homeowner_visible } : item))}
                                              style={[styles.wizardVisibilityBadge, li.homeowner_visible ? styles.wizardVisibilityBadgeVisible : styles.wizardVisibilityBadgeHidden]}
                                            >
                                              <Text style={[styles.wizardVisibilityBadgeText, li.homeowner_visible ? styles.wizardVisibilityBadgeTextVisible : styles.wizardVisibilityBadgeTextHidden]}>
                                                {li.homeowner_visible ? 'Visible' : 'Hidden'}
                                              </Text>
                                            </Pressable>
                                          </View>
                                        </View>
                                      ))}
                                    </View>
                                  </View>
                                </>
                              )}

                              {/* Line item / edit: visibility toggle */}
                              {!isNewParent && isLineItemFlow && (
                                <View style={{ gap: 12 }}>
                                  <View style={styles.wizardToggleRow}>
                                    <View style={{ flex: 1 }}>
                                      <Text style={styles.wizardToggleLabel}>Visible to homeowner</Text>
                                      <Text style={styles.wizardToggleSub}>
                                        Show this line item in the homeowner's invoice and payment email. If hidden, the amount still rolls up into the parent total.
                                      </Text>
                                    </View>
                                    <Pressable
                                      onPress={() => setExpenseFormData({ ...expenseFormData, homeowner_visible: !expenseFormData.homeowner_visible })}
                                      style={[styles.wizardToggleSwitch, expenseFormData.homeowner_visible && styles.wizardToggleSwitchOn]}
                                    >
                                      <View style={[styles.wizardToggleKnob, expenseFormData.homeowner_visible && styles.wizardToggleKnobOn]} />
                                    </Pressable>
                                  </View>

                                  <View style={[styles.wizardInfoBox, { borderLeftWidth: 3, borderLeftColor: expenseFormData.homeowner_visible ? '#2563eb' : '#9ca3af' }]}>
                                    <Text style={styles.wizardInfoBoxTitle}>
                                      {expenseFormData.homeowner_visible ? 'Visible on invoice' : 'Internal only'}
                                    </Text>
                                    <Text style={styles.wizardInfoBoxSub}>
                                      {expenseFormData.homeowner_visible
                                        ? 'This line item will appear as a separate row on the Stripe invoice and in payment request emails.'
                                        : 'This line item is for internal tracking only. The homeowner will only see the parent expense total.'}
                                    </Text>
                                  </View>
                                </View>
                              )}

                              {/* Edit parent: no visibility control needed */}
                              {!isNewParent && !isLineItemFlow && (
                                <View style={styles.wizardInfoBox}>
                                  <Text style={styles.wizardInfoBoxTitle}>Top-level expense</Text>
                                  <Text style={styles.wizardInfoBoxSub}>
                                    Visibility is managed at the line item level. Use "Add Line Item" after saving to control which components the homeowner sees.
                                  </Text>
                                </View>
                              )}

                              {error && (
                                <View style={styles.modalErrorBox}>
                                  <Text style={styles.modalErrorText}>{error}</Text>
                                </View>
                              )}
                            </View>
                          )}
                        </View>
                      </ScrollView>

                      {/* Wizard navigation footer */}
                      <View style={[styles.modalActions, { marginTop: 20, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#f3f4f6' }]}>
                        {expenseWizardStep > 1 ? (
                          <Pressable
                            onPress={() => setExpenseWizardStep((s) => s - 1)}
                            style={[styles.modalButton, styles.modalButtonCancel]}
                          >
                            <Text style={styles.modalButtonCancelText}>Back</Text>
                          </Pressable>
                        ) : (
                          <Pressable
                            onPress={closeWizard}
                            style={[styles.modalButton, styles.modalButtonCancel]}
                          >
                            <Text style={styles.modalButtonCancelText}>Cancel</Text>
                          </Pressable>
                        )}

                        {expenseWizardStep < 4 ? (
                          <Pressable
                            onPress={() => setExpenseWizardStep((s) => s + 1)}
                            disabled={expenseWizardStep === 2 && !step2Valid}
                            style={[
                              styles.modalButton,
                              styles.modalButtonPrimary,
                              expenseWizardStep === 2 && !step2Valid && styles.modalButtonPrimaryDisabled,
                            ]}
                          >
                            <Text style={styles.modalButtonPrimaryText}>Continue</Text>
                          </Pressable>
                        ) : (
                          <Pressable
                            onPress={(e: any) => handleSaveExpense(e)}
                            disabled={savingExpense || (!isNewParent && !step3Valid)}
                            style={[
                              styles.modalButton,
                              styles.modalButtonPrimary,
                              (savingExpense || (!isNewParent && !step3Valid)) && styles.modalButtonPrimaryDisabled,
                            ]}
                          >
                            {savingExpense ? (
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                <ActivityIndicator size="small" color="#ffffff" />
                                <Text style={styles.modalButtonPrimaryText}>Saving...</Text>
                              </View>
                            ) : (
                              <Text style={styles.modalButtonPrimaryText}>
                                {editingExpense ? 'Save Changes' : lineItemParentExpense ? 'Add Line Item' : 'Save Expense'}
                              </Text>
                            )}
                          </Pressable>
                        )}
                      </View>
                    </>
                  );
                })()}
        </View>
      </ExpenseEditorModal>

      {/* Request Homeowner Payment Confirmation Modal */}
      {showRequestHomeownerPaymentConfirmModal && expenseToRequestHomeownerPayment && (
        <Modal
          visible={showRequestHomeownerPaymentConfirmModal}
          transparent
          animationType="fade"
          onRequestClose={handleCloseRequestHomeownerPaymentConfirmModal}
        >
          <TouchableWithoutFeedback onPress={handleCloseRequestHomeownerPaymentConfirmModal}>
            <View style={styles.modalOverlay}>
              <TouchableWithoutFeedback>
                <View style={styles.modalContent}>
                  <View style={styles.modalHeader}>
                    <Text style={styles.modalTitle}>Request Homeowner Payment</Text>
                    <Pressable
                      onPress={handleCloseRequestHomeownerPaymentConfirmModal}
                      style={styles.modalCloseButton}
                    >
                      <X size={20} color="#9ca3af" />
                    </Pressable>
                  </View>

                  <View style={styles.modalBody}>
                    <View style={styles.modalInfo}>
                      <Text style={styles.modalInfoTitle}>
                        {formatCurrency(expenseToRequestHomeownerPayment.amount)}
                      </Text>
                      <Text style={[styles.modalInfoText, { marginTop: 6 }]}>
                        Send payment request to {request?.home?.owner?.full_name || 'the homeowner'}?
                      </Text>
                      {expenseToRequestHomeownerPayment.description ? (
                        <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                          {expenseToRequestHomeownerPayment.description}
                        </Text>
                      ) : null}
                    </View>

                    <View style={styles.modalActions}>
                      <Pressable
                        onPress={handleCloseRequestHomeownerPaymentConfirmModal}
                        style={[styles.modalButton, styles.modalButtonCancel]}
                      >
                        <Text style={styles.modalButtonCancelText}>Cancel</Text>
                      </Pressable>
                      <Pressable
                        onPress={handleConfirmRequestHomeownerPayment}
                        disabled={requestingPaymentExpenseId === expenseToRequestHomeownerPayment.id}
                        style={[
                          styles.modalButton,
                          styles.modalButtonPrimary,
                          requestingPaymentExpenseId === expenseToRequestHomeownerPayment.id && styles.modalButtonPrimaryDisabled
                        ]}
                      >
                        {requestingPaymentExpenseId === expenseToRequestHomeownerPayment.id ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <ActivityIndicator size="small" color="#ffffff" />
                            <Text style={styles.modalButtonPrimaryText}>Sending...</Text>
                          </View>
                        ) : (
                          <Text style={styles.modalButtonPrimaryText}>Send Payment Request</Text>
                        )}
                      </Pressable>
                    </View>
                  </View>
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}

      {/* Homeowner Payment Modal */}
      {showHomeownerPaymentModal && expenseToMarkHomeownerPaid && (
        <Modal
          visible={showHomeownerPaymentModal}
          transparent
          animationType="fade"
          onRequestClose={() => {
            setShowHomeownerPaymentModal(false);
            setExpenseToMarkHomeownerPaid(null);
            setHomeownerPaymentFormData({
              payment_date: '',
              payment_method: '',
              payment_notes: ''
            });
          }}
        >
          <TouchableWithoutFeedback onPress={() => {
            setShowHomeownerPaymentModal(false);
            setExpenseToMarkHomeownerPaid(null);
            setHomeownerPaymentFormData({
              payment_date: '',
              payment_method: '',
              payment_notes: ''
            });
          }}>
            <View style={styles.modalOverlay}>
              <TouchableWithoutFeedback>
                <View style={styles.modalContent}>
                  <View style={styles.modalHeader}>
                    <Text style={styles.modalTitle}>Mark Homeowner as Paid</Text>
                    <Pressable
                      onPress={() => {
                        setShowHomeownerPaymentModal(false);
                        setExpenseToMarkHomeownerPaid(null);
                        setHomeownerPaymentFormData({
                          payment_date: '',
                          payment_method: '',
                          payment_notes: ''
                        });
                      }}
                      style={styles.modalCloseButton}
                    >
                      <X size={20} color="#9ca3af" />
                    </Pressable>
                  </View>

                  <View style={[styles.modalInfo, { backgroundColor: '#f9fafb', marginBottom: 16 }]}>
                    <Text style={styles.modalInfoTitle}>
                      {formatCurrency(expenseToMarkHomeownerPaid.amount)}
                    </Text>
                    {expenseToMarkHomeownerPaid.description && (
                      <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                        {expenseToMarkHomeownerPaid.description}
                      </Text>
                    )}
                  </View>

                  <View style={styles.modalBody}>
                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Payment Date (Optional)</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={homeownerPaymentFormData.payment_date}
                          onChange={(e) => setHomeownerPaymentFormData({ ...homeownerPaymentFormData, payment_date: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            fontSize: 14,
                            color: '#111827',
                            boxSizing: 'border-box',
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.modalFormInput}
                          value={homeownerPaymentFormData.payment_date}
                          onChangeText={(text) => setHomeownerPaymentFormData({ ...homeownerPaymentFormData, payment_date: text })}
                          placeholder="YYYY-MM-DD"
                        />
                      )}
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Payment Method (Optional)</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={homeownerPaymentFormData.payment_method}
                          onValueChange={(value) => setHomeownerPaymentFormData({ ...homeownerPaymentFormData, payment_method: value })}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="Select payment method..." value="" />
                          <Picker.Item label="Credit Card" value="credit_card" />
                          <Picker.Item label="Check" value="check" />
                          <Picker.Item label="Cash" value="cash" />
                          <Picker.Item label="Bank Transfer" value="bank_transfer" />
                          <Picker.Item label="Other" value="other" />
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Payment Notes (Optional)</Text>
                      <TextInput
                        style={styles.modalFormTextArea}
                        value={homeownerPaymentFormData.payment_notes}
                        onChangeText={(text) => setHomeownerPaymentFormData({ ...homeownerPaymentFormData, payment_notes: text })}
                        placeholder="Additional payment notes..."
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    {error && (
                      <View style={styles.modalErrorBox}>
                        <Text style={styles.modalErrorText}>{error}</Text>
                      </View>
                    )}
                  </View>

                  <View style={styles.modalActions}>
                    <Pressable
                      onPress={() => {
                        setShowHomeownerPaymentModal(false);
                        setExpenseToMarkHomeownerPaid(null);
                        setHomeownerPaymentFormData({
                          payment_date: '',
                          payment_method: '',
                          payment_notes: ''
                        });
                      }}
                      style={[styles.modalButton, styles.modalButtonCancel]}
                    >
                      <Text style={styles.modalButtonCancelText}>Cancel</Text>
                    </Pressable>
                    <Pressable
                      onPress={(e: any) => handleMarkHomeownerPaid(e)}
                      disabled={markingHomeownerPaid}
                      style={[styles.modalButton, styles.modalButtonPrimary, markingHomeownerPaid && styles.modalButtonPrimaryDisabled]}
                    >
                      {markingHomeownerPaid ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <ActivityIndicator size="small" color="#ffffff" />
                          <Text style={styles.modalButtonPrimaryText}>Saving...</Text>
                        </View>
                      ) : (
                        <Text style={styles.modalButtonPrimaryText}>Mark Homeowner Paid</Text>
                      )}
                    </Pressable>
                  </View>
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}

      {/* Contractor Payment Modal */}
      {showContractorPaymentModal && expenseToMarkContractorPaid && (
        <Modal
          visible={showContractorPaymentModal}
          transparent
          animationType="fade"
          onRequestClose={() => {
            setShowContractorPaymentModal(false);
            setExpenseToMarkContractorPaid(null);
            setContractorPaymentFormData({
              payment_date: '',
              payment_method: '',
              payment_notes: ''
            });
          }}
        >
          <TouchableWithoutFeedback onPress={() => {
            setShowContractorPaymentModal(false);
            setExpenseToMarkContractorPaid(null);
            setContractorPaymentFormData({
              payment_date: '',
              payment_method: '',
              payment_notes: ''
            });
          }}>
            <View style={styles.modalOverlay}>
              <TouchableWithoutFeedback>
                <View style={styles.modalContent}>
                  <View style={styles.modalHeader}>
                    <Text style={styles.modalTitle}>Mark Contractor as Paid</Text>
                    <Pressable
                      onPress={() => {
                        setShowContractorPaymentModal(false);
                        setExpenseToMarkContractorPaid(null);
                        setContractorPaymentFormData({
                          payment_date: '',
                          payment_method: '',
                          payment_notes: ''
                        });
                      }}
                      style={styles.modalCloseButton}
                    >
                      <X size={20} color="#9ca3af" />
                    </Pressable>
                  </View>

                  <View style={[styles.modalInfo, { backgroundColor: '#f9fafb', marginBottom: 16 }]}>
                    <Text style={styles.modalInfoTitle}>
                      {formatCurrency(expenseToMarkContractorPaid.amount)}
                    </Text>
                    {expenseToMarkContractorPaid.description && (
                      <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                        {expenseToMarkContractorPaid.description}
                      </Text>
                    )}
                  </View>

                  <View style={styles.modalBody}>
                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Payment Date (Optional)</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={contractorPaymentFormData.payment_date}
                          onChange={(e) => setContractorPaymentFormData({ ...contractorPaymentFormData, payment_date: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            fontSize: 14,
                            color: '#111827',
                            boxSizing: 'border-box',
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.modalFormInput}
                          value={contractorPaymentFormData.payment_date}
                          onChangeText={(text) => setContractorPaymentFormData({ ...contractorPaymentFormData, payment_date: text })}
                          placeholder="YYYY-MM-DD"
                        />
                      )}
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Payment Method (Optional)</Text>
                      <View style={[styles.modalFormInput, { paddingHorizontal: 0, paddingVertical: 0 }]}>
                        <Picker
                          selectedValue={contractorPaymentFormData.payment_method}
                          onValueChange={(value) => setContractorPaymentFormData({ ...contractorPaymentFormData, payment_method: value })}
                          style={{ height: 40 }}
                        >
                          <Picker.Item label="Select payment method..." value="" />
                          <Picker.Item label="Check" value="check" />
                          <Picker.Item label="Bank Transfer" value="bank_transfer" />
                          <Picker.Item label="Credit Card" value="credit_card" />
                          <Picker.Item label="Cash" value="cash" />
                          <Picker.Item label="Other" value="other" />
                        </Picker>
                      </View>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Payment Notes (Optional)</Text>
                      <TextInput
                        style={styles.modalFormTextArea}
                        value={contractorPaymentFormData.payment_notes}
                        onChangeText={(text) => setContractorPaymentFormData({ ...contractorPaymentFormData, payment_notes: text })}
                        placeholder="Additional contractor payment notes..."
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    {error && (
                      <View style={styles.modalErrorBox}>
                        <Text style={styles.modalErrorText}>{error}</Text>
                      </View>
                    )}
                  </View>

                  <View style={styles.modalActions}>
                    <Pressable
                      onPress={() => {
                        setShowContractorPaymentModal(false);
                        setExpenseToMarkContractorPaid(null);
                        setContractorPaymentFormData({
                          payment_date: '',
                          payment_method: '',
                          payment_notes: ''
                        });
                      }}
                      style={[styles.modalButton, styles.modalButtonCancel]}
                    >
                      <Text style={styles.modalButtonCancelText}>Cancel</Text>
                    </Pressable>
                    <Pressable
                      onPress={(e: any) => handleMarkContractorPaid(e)}
                      disabled={markingContractorPaid}
                      style={[styles.modalButton, styles.modalButtonPrimary, markingContractorPaid && styles.modalButtonPrimaryDisabled]}
                    >
                      {markingContractorPaid ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <ActivityIndicator size="small" color="#ffffff" />
                          <Text style={styles.modalButtonPrimaryText}>Saving...</Text>
                        </View>
                      ) : (
                        <Text style={styles.modalButtonPrimaryText}>Mark Contractor Paid</Text>
                      )}
                    </Pressable>
                  </View>
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}

      {/* Create Invoice Modal */}
      {showInvoiceModal && (
        <Modal
          visible={showInvoiceModal}
          transparent
          animationType="fade"
          onRequestClose={() => {
            setShowInvoiceModal(false);
            setInvoiceError(null);
          }}
        >
          <TouchableWithoutFeedback onPress={() => {
            setShowInvoiceModal(false);
            setInvoiceError(null);
          }}>
            <View style={styles.modalOverlay}>
              <TouchableWithoutFeedback>
                <View style={styles.modalContent}>
                  <View style={styles.modalHeader}>
                    <Text style={styles.modalTitle}>Create Stripe Invoice</Text>
                    <Pressable
                      onPress={() => {
                        setShowInvoiceModal(false);
                        setInvoiceError(null);
                      }}
                      style={styles.modalCloseButton}
                    >
                      <X size={20} color="#9ca3af" />
                    </Pressable>
                  </View>

                  <View style={[styles.modalInfo, { backgroundColor: '#f9fafb', marginBottom: 16 }]}>
                    <Text style={styles.modalInfoTitle}>
                      Invoice for {selectedExpenseIds.size} expense(s)
                    </Text>
                    <Text style={styles.modalInfoText}>
                      Total: {formatCurrency(getParentExpenses().filter(e => selectedExpenseIds.has(e.id)).reduce((sum, e) => sum + e.amount, 0))}
                    </Text>
                    {request?.home?.owner && (
                      <Text style={[styles.modalInfoText, { marginTop: 4 }]}>
                        Homeowner: {request.home.owner.full_name}
                      </Text>
                    )}
                  </View>

                  <View style={styles.modalBody}>
                    <Text style={styles.modalSectionTitle}>Selected Expenses:</Text>
                    <View style={{ gap: 8, marginBottom: 16 }}>
                      {getParentExpenses().filter(e => selectedExpenseIds.has(e.id)).map(expense => (
                        <View key={expense.id} style={{ padding: 8, backgroundColor: '#f9fafb', borderRadius: 4 }}>
                          <Text style={{ fontSize: 14, fontWeight: '600' }}>
                            {formatCurrency(expense.amount)}
                          </Text>
                          {expense.description && (
                            <Text style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                              {expense.description}
                            </Text>
                          )}
                          {(expense.category || expense.vendor) && (
                            <Text style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                              {expense.category && `${expense.category}`}
                              {expense.category && expense.vendor && ' • '}
                              {expense.vendor && expense.vendor}
                            </Text>
                          )}
                        </View>
                      ))}
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Due Date (Optional)</Text>
                      <TextInput
                        style={styles.modalFormInput}
                        value={invoiceDueDate}
                        onChangeText={setInvoiceDueDate}
                        placeholder="YYYY-MM-DD"
                      />
                      <Text style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        Defaults to 30 days from now if not specified
                      </Text>
                    </View>

                    <View style={styles.modalFormField}>
                      <Text style={styles.modalFormLabel}>Invoice Notes (Optional)</Text>
                      <TextInput
                        style={styles.modalFormTextArea}
                        value={invoiceNotes}
                        onChangeText={setInvoiceNotes}
                        placeholder="Add any additional notes for the invoice..."
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    {invoiceError && (
                      <View style={styles.modalErrorBox}>
                        <Text style={styles.modalErrorText}>{invoiceError}</Text>
                      </View>
                    )}
                  </View>

                  <View style={styles.modalActions}>
                    <Pressable
                      onPress={() => {
                        setShowInvoiceModal(false);
                        setInvoiceError(null);
                      }}
                      style={[styles.modalButton, styles.modalButtonCancel]}
                    >
                      <Text style={styles.modalButtonCancelText}>Cancel</Text>
                    </Pressable>
                    <Pressable
                      onPress={handleCreateInvoice}
                      disabled={creatingInvoice}
                      style={[styles.modalButton, styles.modalButtonPrimary, creatingInvoice && styles.modalButtonPrimaryDisabled]}
                    >
                      {creatingInvoice ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <ActivityIndicator size="small" color="#ffffff" />
                          <Text style={styles.modalButtonPrimaryText}>Creating Invoice...</Text>
                        </View>
                      ) : (
                        <Text style={styles.modalButtonPrimaryText}>Create Invoice</Text>
                      )}
                    </Pressable>
                  </View>
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}

      {/* Image Preview Modal */}
      <Modal
        visible={!!previewImage}
        transparent
        animationType="fade"
        onRequestClose={() => setPreviewImage(null)}
      >
        <TouchableWithoutFeedback onPress={() => setPreviewImage(null)}>
          <View style={[styles.modalOverlay, { backgroundColor: 'rgba(0, 0, 0, 0.75)' }]}>
            <TouchableWithoutFeedback>
              <View style={{ position: 'relative', maxWidth: 896, maxHeight: '90%' }}>
                <Pressable
                  onPress={() => setPreviewImage(null)}
                  style={{
                    position: 'absolute',
                    top: -40,
                    right: 0,
                    padding: 8,
                    zIndex: 1,
                  }}
                >
                  <X size={24} color="#ffffff" />
                </Pressable>
                {previewImage && (
                  <Image
                    source={{ uri: previewImage }}
                    style={{
                      width: '100%',
                      maxWidth: 896,
                      height: '100%',
                      maxHeight: Dimensions.get('window').height * 0.9,
                      borderRadius: 8,
                    }}
                    resizeMode="contain"
                  />
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </WebScrollView>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  errorContainer: {
    padding: 24,
  },
  errorText: {
    fontSize: 14,
    color: '#dc2626',
  },
  container: {
    flex: 1,
    padding: 24,
  },
  header: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 24,
    gap: 12,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  backButtonText: {
    fontSize: 14,
    color: '#4b5563',
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#000000',
  },
  errorBanner: {
    backgroundColor: '#fee2e2',
    padding: 16,
    borderRadius: 8,
    marginBottom: 24,
  },
  errorBannerText: {
    fontSize: 14,
    color: '#dc2626',
  },
  contentRow: {
    flexDirection: 'row',
    gap: 24,
  },
  contentRowMobile: {
    flexDirection: 'column',
  },
  mainContent: {
    flex: 2,
    gap: 24,
  },
  mainContentMobile: {
    flex: 1,
    width: '100%',
  },
  sidebar: {
    flex: 1,
    gap: 24,
  },
  sidebarMobile: {
    flex: 1,
    width: '100%',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 24,
    ...shadows.sm,
  },
  cardHeader: {
    marginBottom: 24,
  },
  cardHeaderLeft: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  editRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  titleInput: {
    flex: 1,
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 12,
  },
  descriptionInput: {
    fontSize: 14,
    color: '#374151',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 12,
    minHeight: 120,
    marginBottom: 12,
  },
  editIconButton: {
    padding: 4,
    borderRadius: 4,
  },
  editActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  editButton: {
    padding: 8,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButton: {
    backgroundColor: '#16a34a',
  },
  cancelButton: {
    backgroundColor: '#f3f4f6',
  },
  smallButton: {
    padding: 6,
  },
  dateEditRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  dateDisplayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  dateInput: {
    fontSize: 12,
    color: '#374151',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    padding: 6,
    minWidth: 100,
  },
  cardOwner: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2563eb',
    marginBottom: 12,
  },
  cardMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  cardBody: {
    gap: 24,
  },
  section: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionHeaderMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12,
  },
  sectionActions: {
    flexDirection: 'row',
    gap: 8,
  },
  sectionActionsMobile: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  descriptionBox: {
    backgroundColor: '#f9fafb',
    padding: 16,
    borderRadius: 8,
  },
  descriptionText: {
    fontSize: 14,
    color: '#374151',
  },
  detailRow: {
    flexDirection: 'row',
    gap: 24,
  },
  detailColumn: {
    flex: 1,
  },
  detailValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pickerContainer: {
    borderWidth: 2,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  picker: {
    height: 44,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '500',
  },
  ownerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  ownerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  ownerName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#16a34a',
  },
  ownerUnassigned: {
    fontSize: 14,
    color: '#6b7280',
  },
  assignOwnerButton: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2563eb',
  },
  visitInfoBox: {
    backgroundColor: '#dbeafe',
    padding: 16,
    borderRadius: 8,
  },
  visitInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  visitInfoText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2563eb',
  },
  visitInfoTextCompleted: {
    fontSize: 14,
    fontWeight: '500',
    color: '#16a34a',
    marginTop: 8,
  },
  addNoteForm: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  noteInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
    minHeight: 60,
    textAlignVertical: 'top',
  },
  addNoteButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addNoteButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  notesList: {
    gap: 12,
  },
  noteCard: {
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 8,
  },
  noteContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  noteText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
  },
  noteFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  noteDate: {
    fontSize: 12,
    color: '#6b7280',
  },
  noteAuthor: {
    fontSize: 12,
    color: '#6b7280',
  },
  noNotesText: {
    fontSize: 14,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  associateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#2563eb',
    borderRadius: 6,
  },
  associateButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#000000',
    borderRadius: 6,
  },
  addButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  bidLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#059669',
    borderRadius: 6,
  },
  bidLinkButtonDisabled: {
    opacity: 0.7,
  },
  bidLinkButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  shareMessageText: {
    fontSize: 13,
    marginBottom: 4,
  },
  shareMessageSuccess: {
    color: '#059669',
  },
  shareMessageError: {
    color: '#dc2626',
  },
  noDataText: {
    fontSize: 14,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  quotesList: {
    gap: 16,
  },
  adminTasksList: {
    gap: 12,
  },
  adminTaskCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
    gap: 8,
  },
  adminTaskHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  adminTaskHeaderActions: {
    flexDirection: 'row',
    gap: 8,
  },
  adminTaskEditButton: {
    padding: 4,
  },
  adminTaskTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  adminTaskBadges: {
    flexDirection: 'row',
    gap: 6,
  },
  adminTaskDescription: {
    fontSize: 13,
    color: '#4b5563',
  },
  adminTaskMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  adminTaskCompleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    backgroundColor: '#16a34a',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginTop: 4,
  },
  adminTaskCompleteButtonDisabled: {
    opacity: 0.6,
  },
  adminTaskCompleteButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '500',
  },
  quoteCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
  },
  quoteCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  quoteCardContent: {
    flex: 1,
    marginRight: 12,
  },
  quoteTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  quoteProviderName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  quoteAmount: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  quoteNotes: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 8,
  },
  quoteMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  quoteMetaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  quoteActions: {
    flexDirection: 'row',
    gap: 8,
  },
  quoteActionButton: {
    padding: 6,
  },
  quoteCreated: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 8,
  },
  attachmentsContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  attachmentItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 6,
    padding: 8,
    marginBottom: 4,
  },
  attachmentContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 8,
  },
  attachmentInfo: {
    flex: 1,
  },
  attachmentName: {
    fontSize: 12,
    fontWeight: '500',
    color: '#111827',
  },
  attachmentSize: {
    fontSize: 12,
    color: '#6b7280',
  },
  attachmentActions: {
    flexDirection: 'row',
    gap: 4,
  },
  attachmentAction: {
    padding: 4,
  },
  expenseSummaryContainer: {
    backgroundColor: '#f9fafb',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 16,
  },
  expenseSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  expenseSummaryItem: {
    alignItems: 'center',
  },
  expenseSummaryLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
  },
  expenseSummaryTotal: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  expenseSummaryPaid: {
    fontSize: 18,
    fontWeight: '600',
    color: '#16a34a',
  },
  expenseSummaryUnpaid: {
    fontSize: 18,
    fontWeight: '600',
    color: '#dc2626',
  },
  expensesList: {
    gap: 16,
  },
  expenseCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
  },
  moneyFlowWidget: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
    marginBottom: 12,
  },
  moneyFlowTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#374151',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  moneyFlowRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 8,
  },
  moneyFlowSegment: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  moneyFlowNode: {
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  moneyFlowNodeComplete: {
    backgroundColor: '#dcfce7',
    borderColor: '#86efac',
  },
  moneyFlowNodePending: {
    backgroundColor: '#f3f4f6',
    borderColor: '#d1d5db',
  },
  moneyFlowNodeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  moneyFlowNodeTextComplete: {
    color: '#166534',
  },
  moneyFlowNodeTextPending: {
    color: '#6b7280',
  },
  moneyFlowArrowBlock: {
    alignItems: 'center',
    gap: 2,
    minWidth: 90,
  },
  moneyFlowArrow: {
    fontSize: 16,
    fontWeight: '700',
  },
  moneyFlowArrowComplete: {
    color: '#16a34a',
  },
  moneyFlowArrowPending: {
    color: '#9ca3af',
  },
  moneyFlowStepLabel: {
    fontSize: 10,
    textAlign: 'center',
    maxWidth: 110,
  },
  moneyFlowStepLabelComplete: {
    color: '#166534',
  },
  moneyFlowStepLabelPending: {
    color: '#6b7280',
  },
  expenseCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  expenseCardContent: {
    flex: 1,
    marginRight: 12,
  },
  expenseHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  expenseAmount: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  expenseDescription: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 8,
  },
  expenseMetaGrid: {
    gap: 8,
  },
  expenseMetaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  expenseMetaLabel: {
    fontWeight: '500',
  },
  expenseLineItemsContainer: {
    marginTop: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#f9fafb',
    gap: 8,
  },
  expenseLineItemsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  expenseLineItemRow: {
    flexDirection: 'row',
    gap: 10,
    padding: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 6,
    backgroundColor: '#ffffff',
  },
  expenseLineItemAmount: {
    fontSize: 12,
    fontWeight: '600',
    color: '#111827',
  },
  expenseLineItemDescription: {
    fontSize: 12,
    color: '#4b5563',
    marginTop: 2,
  },
  expenseLineItemMeta: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 4,
  },
  expenseLineItemActions: {
    gap: 6,
    alignItems: 'flex-end',
  },
  expenseLineItemActionButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 5,
    backgroundColor: '#f9fafb',
  },
  expenseLineItemActionText: {
    fontSize: 11,
    color: '#374151',
    fontWeight: '600',
  },
  expensePaymentInfo: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 4,
  },
  expenseHistoryList: {
    marginTop: 4,
    gap: 2,
  },
  expenseHistoryItemText: {
    fontSize: 11,
    color: '#6b7280',
  },
  expenseActionBar: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    flexWrap: 'wrap',
  },
  expenseActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  expenseActionButtonHomeowner: {
    borderColor: '#16a34a',
    backgroundColor: '#f0fdf4',
  },
  expenseActionButtonContractor: {
    borderColor: '#c2410c',
    backgroundColor: '#fff7ed',
  },
  expenseActionButtonDelete: {
    borderColor: '#fecaca',
    backgroundColor: '#fef2f2',
  },
  expenseActionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  expenseActionButtonTextHomeowner: {
    color: '#166534',
  },
  expenseActionButtonTextContractor: {
    color: '#9a3412',
  },
  expenseActionButtonTextDelete: {
    color: '#dc2626',
  },
  expenseCreated: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 8,
  },
  quickActionsContainer: {
    gap: 12,
  },
  quickActionButtonBlue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#dbeafe',
    borderRadius: 6,
  },
  quickActionButtonBlueText: {
    color: '#2563eb',
    fontSize: 14,
    fontWeight: '500',
  },
  quickActionButtonRed: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#fee2e2',
    borderRadius: 6,
  },
  quickActionButtonRedText: {
    color: '#dc2626',
    fontSize: 14,
    fontWeight: '500',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 448,
    ...shadows.lg,
  },
  modalScrollContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 448,
    maxHeight: '90%',
    zIndex: 1000,
    ...shadows.lg,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  modalTitleRed: {
    fontSize: 18,
    fontWeight: '600',
    color: '#dc2626',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalBody: {
    gap: 16,
    zIndex: 1,
  },
  modalInfo: {
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 8,
    gap: 4,
  },
  modalInfoTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  modalInfoText: {
    fontSize: 12,
    color: '#6b7280',
  },
  modalGridButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  modalGridButton: {
    flex: 1,
    minWidth: '45%',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalGridButtonDisabled: {
    backgroundColor: '#f3f4f6',
    opacity: 0.5,
  },
  modalGridButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  modalListButton: {
    padding: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  modalListButtonDisabled: {
    opacity: 0.5,
  },
  modalListButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonCancel: {
    backgroundColor: '#f3f4f6',
  },
  modalButtonCancelText: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '500',
  },
  modalButtonPrimary: {
    backgroundColor: '#000000',
  },
  modalButtonPrimaryDisabled: {
    opacity: 0.5,
  },
  modalButtonPrimaryText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  modalButtonDelete: {
    backgroundColor: '#dc2626',
  },
  modalButtonDeleteText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  modalFormField: {
    gap: 8,
    zIndex: 1,
  },
  /** Paints above sibling fields (e.g. quote attachments) so typeahead dropdown is clickable */
  modalFormFieldDropdownOpen: {
    zIndex: 10050,
  },
  modalSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  modalFormLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  typeaheadContainer: {
    position: 'relative',
    zIndex: 9999,
  },
  modalFormInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
  },
  modalFormTextArea: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
    minHeight: 80,
    textAlignVertical: 'top',
  },
  modalErrorBox: {
    backgroundColor: '#fee2e2',
    padding: 12,
    borderRadius: 6,
  },
  modalErrorText: {
    fontSize: 14,
    color: '#dc2626',
  },
  modalLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
  },
  modalLoadingText: {
    fontSize: 14,
    color: '#6b7280',
    marginLeft: 8,
  },
  actionButtonsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  expenseActionButtonsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 16,
  },
  expenseCheckbox: {
    paddingRight: 12,
    justifyContent: 'center',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: '#d1d5db',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  checkboxChecked: {
    backgroundColor: '#2563eb',
    borderColor: '#2563eb',
  },
  typeaheadDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    marginTop: 4,
    maxHeight: 200,
    zIndex: 10000,
    elevation: 20,
    ...shadows.lg,
  },
  typeaheadItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  typeaheadItemSelected: {
    backgroundColor: '#eff6ff',
  },
  typeaheadItemText: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  typeaheadItemSubtext: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  // Expense wizard styles
  wizardProgressBar: {
    flexDirection: 'row',
    gap: 4,
    marginBottom: 20,
  },
  wizardProgressSegment: {
    flex: 1,
    height: 4,
    borderRadius: 2,
  },
  wizardProgressSegmentActive: {
    backgroundColor: '#2563eb',
  },
  wizardProgressSegmentInactive: {
    backgroundColor: '#e5e7eb',
  },
  wizardIntroHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
  },
  wizardIntroTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
  },
  wizardStepCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    gap: 6,
  },
  wizardStepCardTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
    letterSpacing: 0.1,
  },
  wizardStepCardBody: {
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 19,
  },
  wizardInfoBox: {
    backgroundColor: '#eff6ff',
    borderRadius: 10,
    padding: 14,
    gap: 4,
  },
  wizardInfoBoxTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: '#2563eb',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  wizardInfoBoxValue: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
  },
  wizardInfoBoxSub: {
    fontSize: 12,
    color: '#4b5563',
    lineHeight: 18,
  },
  wizardMarginPill: {
    alignSelf: 'flex-start',
    backgroundColor: '#dcfce7',
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginTop: 4,
  },
  wizardMarginPillText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#166534',
  },
  wizardToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    backgroundColor: '#ffffff',
  },
  wizardToggleLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  wizardToggleSub: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
    lineHeight: 17,
  },
  wizardToggleSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#d1d5db',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  wizardToggleSwitchOn: {
    backgroundColor: '#2563eb',
  },
  wizardToggleKnob: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#ffffff',
  },
  wizardToggleKnobOn: {
    alignSelf: 'flex-end',
  },
  wizardSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    gap: 12,
  },
  wizardSummaryLabel: {
    fontSize: 12,
    color: '#6b7280',
    flex: 1,
  },
  wizardSummaryValue: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
    textAlign: 'right',
    flex: 1,
  },
  // Line item builder
  wizardBuilderList: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    overflow: 'hidden',
  },
  wizardBuilderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    backgroundColor: '#ffffff',
    gap: 12,
  },
  wizardBuilderRowLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  wizardBuilderRowMeta: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 2,
  },
  wizardBuilderRowAmount: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
  },
  wizardBuilderTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: '#eff6ff',
    borderTopWidth: 1,
    borderTopColor: '#bfdbfe',
  },
  wizardBuilderTotalLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1d4ed8',
  },
  wizardBuilderTotalAmount: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1d4ed8',
  },
  wizardAddCostButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1.5,
    borderColor: '#bfdbfe',
    borderStyle: 'dashed',
    borderRadius: 10,
    justifyContent: 'center',
    backgroundColor: '#f8faff',
  },
  wizardAddCostButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2563eb',
  },
  wizardAddLineItemForm: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    padding: 14,
    backgroundColor: '#f9fafb',
  },
  wizardVisibilityBadge: {
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderWidth: 1,
  },
  wizardVisibilityBadgeVisible: {
    backgroundColor: '#eff6ff',
    borderColor: '#bfdbfe',
  },
  wizardVisibilityBadgeHidden: {
    backgroundColor: '#f9fafb',
    borderColor: '#d1d5db',
  },
  wizardVisibilityBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  wizardVisibilityBadgeTextVisible: {
    color: '#1d4ed8',
  },
  wizardVisibilityBadgeTextHidden: {
    color: '#6b7280',
  },
  // Preview button
  expenseActionButtonPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  expenseActionButtonTextPreview: {
    color: '#4b5563',
  },
  // Preview modal
  previewTabRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    marginBottom: 16,
  },
  previewTab: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginBottom: -1,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  previewTabActive: {
    borderBottomColor: '#2563eb',
  },
  previewTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#9ca3af',
  },
  previewTabTextActive: {
    color: '#2563eb',
  },
  // Email preview
  previewEmailCard: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  previewEmailHeader: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    gap: 4,
  },
  previewEmailFrom: {
    fontSize: 11,
    color: '#6b7280',
  },
  previewEmailSubject: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  previewEmailBody: {
    padding: 16,
  },
  previewEmailText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  previewEmailLineItem: {
    fontSize: 13,
    color: '#374151',
    lineHeight: 22,
    paddingLeft: 8,
  },
  // Invoice preview
  previewInvoiceCard: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  previewInvoiceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    backgroundColor: '#f8faff',
  },
  previewInvoiceTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  previewInvoiceMeta: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    gap: 3,
  },
  previewInvoiceMetaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  previewInvoiceLineItems: {
    paddingHorizontal: 16,
  },
  previewInvoiceLineItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  previewInvoiceLineItemHeaderText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  previewInvoiceLineItemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    gap: 12,
  },
  previewInvoiceLineItemLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#111827',
  },
  previewInvoiceLineItemSub: {
    fontSize: 11,
    color: '#9ca3af',
    marginTop: 2,
  },
  previewInvoiceLineItemAmount: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  previewInvoiceTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: '#f8faff',
    borderTopWidth: 2,
    borderTopColor: '#2563eb',
  },
  previewInvoiceTotalLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
  },
  previewInvoiceTotalAmount: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  // Hidden callout (admin-only)
  previewHiddenCallout: {
    backgroundColor: '#fffbeb',
    borderWidth: 1,
    borderColor: '#fde68a',
    borderRadius: 10,
    padding: 14,
    gap: 8,
  },
  previewHiddenCalloutTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#92400e',
  },
  previewHiddenRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#fde68a',
  },
  previewHiddenLabel: {
    fontSize: 13,
    color: '#92400e',
    textDecorationLine: 'line-through',
  },
  previewHiddenAmount: {
    fontSize: 13,
    color: '#92400e',
    textDecorationLine: 'line-through',
  },
  previewHiddenNote: {
    fontSize: 11,
    color: '#a16207',
    lineHeight: 16,
  },
});

export default ServiceRequestDetail;
